import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { withAIQueryTracking, trackUserEvent } from "../utils/userEventTracker";
import {
  Center,
  Text,
  Flex,
  Box,
  Button,
  HStack,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  Input,
  Textarea,
  Heading,
  Spinner,
  Checkbox,
  Select,
  FormControl,
  FormLabel,
  SimpleGrid,
  Divider,
  Progress,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Tooltip,
} from "@chakra-ui/react";
import { isImpersonating } from "../utils/impersonation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getDefaultDateString,
  bucketName_files,
  getSendFormsUrl,
  colid2,
  prompts,
  BASE_URLS,
  tableName,
  getTypeFieldIndex,
  bucketName,
  file_reference_column,
  supabaseUrl,
  getBackendURL,
  basename,
  LLM_USER_TOKEN_LIMIT,
  UPGRADE_DEFAULTS,
} from "../components/env";
import JSZip from "jszip";
import { getPatientPageVisitPathSegment } from "../utils/visitPathUtils";
import { hasValidPassword } from "../utils/passwordProtection";
import PasswordModal from "../components/PasswordModal";
import {
  getLiveCreditUsage,
  getRecordedCreditUsage,
  getCodesCreditUsage,
  getTotalCreditUsage,
  initializeCreditTracking,
  addRecordedCredits,
} from "../utils/creditTracker";
import { cortiLogger } from "../utils/cortiLogger";
import { getAutoSaveSetting, setAutoSaveSetting, copyItems } from "../utils/utils";
import {
  getSupabase,
  supabaseSchema,
} from "../components/supabaseClient";
import { mirrorWriteToRds } from "../utils/rdsCutover";
import { shadowCompareScheduleRow } from "../utils/rdsShadowRead";
import {
  fetchDocument,
  generateMedADocsPath,
  generateMedADocsFolderPath,
} from "../documentComponents/documentUtilities";
import { uploadAudioFile } from "./AudioRecorder/utils/databaseUtils";
import Uploader from "../components/upload/Uploader";
import AudioUploader from "../components/upload/AudioUploader";
import {
  transcribeUploadedAudio as transcribeUploadedAudioInBrowser,
  VOSK_SERVICE_ORIGIN,
} from "../services/vosk/transcribeUploadedAudio";
import SchedulesTable from "../components/SchedulesTable";
import PatientPortal from "./PatientPortal";
import LeejoeSchedulesView from "../components/LeejoeSchedulesView";
import ScheduleRowActionsHost from "../components/schedules/ScheduleRowActions";
import ProvidersDropdown from "../components/ProvidersDropdown";
import { useSelectedProviders } from "../hooks/useSelectedProviders";
import AudioManagerModal, {
  clearBackupCache,
} from "../components/AudioManagerModal";
import { filterAudiosForVisitDate } from "../utils/audioMetadata";
import PatientList from "../components/PatientList";
import HelpDeskButton from "../components/HelpDeskButton";
import DocumentCreationModal from "../documentComponents/DocumentCreationModal";
import AddPatientModal from "../components/AddPatientModal";
import EditPatientModal from "../components/EditPatientModal";
import "./css/Schedules.css";
import { handleFormSending, handleWelcomeSending } from "../utils/formUtils";
import DuplicatePatientModal from "../components/DuplicatePatientModal";
import { jsPDF } from "jspdf";
import { checkCreditLimitAndUpdateUI } from "../utils/creditLimitChecker";
import {
  normalizeRole,
  isInGroup,
  getLabel,
  isPatientRole,
  isReadOnlyRole,
  hasDoctorRolePermissions,
  hasClinicalAdminPermissions,
} from "../config/roles";
import { fetchEffectiveUserRow } from "../utils/effectiveUserRole";
import { getAggregateAudioStream } from "../utils/audioUtils";
import VitaMedDriveModal from "../components/VitaMedDriveModal";
import { dbgLog } from "../utils/appDebugFlags";
import { resolveTenantRowAccess } from "../utils/shareAccess";
import { getSchedulesVersionParts } from "../utils/buildInfo";
import { recordingLabel } from "../utils/recordingLabel";

const sLog = (...args) => dbgLog("schedules", ...args);

const aggregateAiFeedbackDirect = async (supabase, currentUserId, schema, sinceISO) => {
  const trySchemas = new Set();
  if (schema) trySchemas.add(schema);
  trySchemas.add("public");

  const aggregatedByUser = new Map();
  const totals = { total_tokens: 0, total_queries: 0, total_input_tokens: 0, total_output_tokens: 0, total_cost_usd: 0 };
  const modelsUsed = new Set();
  const schemasHit = [];

  for (const s of trySchemas) {
    try {
      const sb = supabase.schema(s);
      const { data, error } = await sb
        .from("ai_feedback")
        .select(
          "user_id, total_tokens, input_tokens, output_tokens, cost_usd, model_used, created_at",
        )
        .gte("created_at", sinceISO)
        .limit(5000);
      if (error) {
        console.warn(`[ai_feedback direct] schema=${s} error:`, error);
        continue;
      }
      if (!data || !data.length) {
        console.log(`[ai_feedback direct] schema=${s} returned 0 rows`);
        continue;
      }
      schemasHit.push(s);
      console.log(`[ai_feedback direct] schema=${s} returned ${data.length} rows`);

      for (const row of data) {
        const tokens = Number(row.total_tokens) || ((Number(row.input_tokens) || 0) + (Number(row.output_tokens) || 0));
        const inTok = Number(row.input_tokens) || 0;
        const outTok = Number(row.output_tokens) || 0;
        const cost = Number(row.cost_usd) || 0;
        const uid = row.user_id ? String(row.user_id) : null;
        if (!uid) continue;

        totals.total_tokens += tokens;
        totals.total_queries += 1;
        totals.total_input_tokens += inTok;
        totals.total_output_tokens += outTok;
        totals.total_cost_usd += cost;
        if (row.model_used) modelsUsed.add(row.model_used);

        const slot = aggregatedByUser.get(uid) || { user_id: uid, total_tokens: 0, total_queries: 0, total_input_tokens: 0, total_output_tokens: 0, total_cost_usd: 0 };
        slot.total_tokens += tokens;
        slot.total_queries += 1;
        slot.total_input_tokens += inTok;
        slot.total_output_tokens += outTok;
        slot.total_cost_usd += cost;
        aggregatedByUser.set(uid, slot);
      }
    } catch (e) {
      console.warn(`[ai_feedback direct] schema=${s} threw:`, e);
    }
  }

  const top_users = [...aggregatedByUser.values()]
    .sort((a, b) => b.total_tokens - a.total_tokens)
    .slice(0, 50)
    .map((u, i) => ({ ...u, rank: i + 1, percent_of_total: totals.total_tokens > 0 ? (u.total_tokens / totals.total_tokens) * 100 : 0 }));

  let caller_usage = null;
  if (currentUserId) {
    const found = aggregatedByUser.get(String(currentUserId));
    if (found) {
      caller_usage = {
        total_queries: found.total_queries,
        total_input_tokens: found.total_input_tokens,
        total_output_tokens: found.total_output_tokens,
        total_tokens: found.total_tokens,
        total_cost_usd: found.total_cost_usd,
      };
    }
  }
  if (!caller_usage && totals.total_queries > 0) {
    caller_usage = { total_queries: 0, total_input_tokens: 0, total_output_tokens: 0, total_tokens: 0, total_cost_usd: 0 };
  }

  if (totals.total_queries === 0) {
    console.warn("[ai_feedback direct] no ai_feedback rows found in any schema");
    return null;
  }

  return {
    summary: { ...totals, models_used: [...modelsUsed], unique_users: aggregatedByUser.size },
    top_users,
    caller_usage: caller_usage || { total_queries: 0, total_input_tokens: 0, total_output_tokens: 0, total_tokens: 0, total_cost_usd: 0 },
    schemasHit,
  };
};

const Schedules = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  // Cross-schema providers — homeSchema flags foreign-schema rows in the
  // Leejoe simple view (chip) and is otherwise a no-op for single-schema
  // users (homeSchema === the sole schema, so no row is ever "foreign").
  const { homeSchema } = useSelectedProviders();
  const [selectedRows, setSelectedRows] = useState({});
  const [schedules, setSchedules] = useState([]);
  const [isSimplifiedView, setIsSimplifiedView] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
  const [existingDocument, setExistingDocument] = useState(null);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isDownloadingPatientData, setIsDownloadingPatientData] = useState(false);
  const [schedulesPageUserId, setSchedulesPageUserId] = useState(null);
  const [tenantShareAllOwnerIds, setTenantShareAllOwnerIds] = useState([]);
  const [tenantShareAllOwnerRoles, setTenantShareAllOwnerRoles] = useState(
    {},
  );
  const [selectedScheduleTenantAccess, setSelectedScheduleTenantAccess] =
    useState({
      canEditScheduleFields: true,
      canDeleteRow: true,
    });
  const [isEditPatientModalOpen, setIsEditPatientModalOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [isAudioManagerModalOpen, setIsAudioManagerModalOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [currentUserName, setCurrentUserName] = useState("");
  const [isReferralLinkOpen, setIsReferralLinkOpen] = useState(false);
  const [isMarkNoShowOpen, setIsMarkNoShowOpen] = useState(false);
  const [noShowReason, setNoShowReason] = useState("");
  const [isMarkingNoShow, setIsMarkingNoShow] = useState(false);
  const schedulesTableActionsRef = useRef(null);
  // The full table patched a row in place (a Confirm-status change, or its
  // rollback): mirror it here so the simple view shows the same status.
  const handleScheduleRowPatched = useCallback((rowId, fields) => {
    setSchedules((prev) =>
      Array.isArray(prev)
        ? prev.map((s) => (s.id === rowId ? { ...s, ...fields } : s))
        : prev,
    );
  }, []);
  // The simple view's Confirm chip goes through the full table's own handler
  // (audit stamp, per-schema write, rollback), which then patches both views.
  const handleRowConfirmationStatus = useCallback(
    (rowId, status) =>
      schedulesTableActionsRef.current?.setConfirmationStatus?.(rowId, status),
    [],
  );
  const [isTranscribeModalOpen, setIsTranscribeModalOpen] = useState(false);
  const [facts, setFacts] = useState(null);
  const [selectedAudioFile, setSelectedAudioFile] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [soapNotes, setSoapNotes] = useState(null);
  const [selectedAudioSource, setSelectedAudioSource] = useState(null);
  const [selectedAudioUrl, setSelectedAudioUrl] = useState(null);
  const [selectedAudioDuration, setSelectedAudioDuration] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [lastElapsedTime, setLastElapsedTime] = useState(null);
  const [estimatedProgress, setEstimatedProgress] = useState(0);
  const [currentMilestone, setCurrentMilestone] = useState("");
  const [milestoneProgress, setMilestoneProgress] = useState(0);
  const timerRef = useRef(null);
  const transcribeButtonRef = useRef(null);

  // Add state for live transcription modal
  const [isLiveTranscribeModalOpen, setIsLiveTranscribeModalOpen] =
    useState(false);
  const [isLiveRecording, setIsLiveRecording] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState("");
  const [liveFacts, setLiveFacts] = useState([]);
  const liveMediaRecorderRef = useRef(null);
  const liveSocketRef = useRef(null);

  // Add state for phone audio modal
  const [isPhoneAudioModalOpen, setIsPhoneAudioModalOpen] = useState(false);

  // Auto-save setting state
  const [autoSaveSetting, setAutoSaveSettingState] = useState(false);

  // Vita-Med Drive modal state
  const [isVitaMedDriveOpen, setIsVitaMedDriveOpen] = useState(false);

  // Auto-reformat setting state
  const [autoReformatEnabled, setAutoReformatEnabled] = useState(false); // Disabled for now

  // Recording state
  const [condition, setCondition] = useState(false);
  const [session, setSession] = useState(false);

  // Phone audio recording state
  const [phoneMediaRecorder, setPhoneMediaRecorder] = useState(null);
  const [phoneRecordingState, setPhoneRecordingState] = useState("idle"); // 'idle', 'recording', 'paused', 'stopped'
  const [phoneAudioChunks, setPhoneAudioChunks] = useState([]);
  const [phoneAudioURL, setPhoneAudioURL] = useState("");
  const [phoneRecordingStartTime, setPhoneRecordingStartTime] = useState(null);
  // Mirror of `phoneRecordingStartTime` for use inside MediaRecorder.onstop.
  // The handler is assigned when the recorder is created, so it closes over
  // the state value from that render — which is `null` on the first recording
  // and the PREVIOUS session's start time on later ones. A ref is always
  // current. (Same reason `capturedPatientRef` exists below.)
  const phoneRecordingStartRef = useRef(null);
  const phoneMediaRecorderRef = useRef(null);
  const phoneStreamRef = useRef(null);
  const phoneChunksRef = useRef([]);
  const capturedPatientRef = useRef(null);
  const capturedAutoSaveSettingRef = useRef(null);
  const phoneAudioDurationRef = useRef(null);
  const phoneModalClosedRef = useRef(false);
  const phoneAggregateCleanupRef = useRef(null);
  const [phoneIncludeSystemAudio, setPhoneIncludeSystemAudio] = useState(false);

  // Upload audio modal state
  const [isUploadAudioModalOpen, setIsUploadAudioModalOpen] = useState(false);
  const [uploadAudioFileInput, setUploadAudioFileInput] = useState(null);
  const [uploadAudioUrl, setUploadAudioUrl] = useState("");
  const [uploadAudioDuration, setUploadAudioDuration] = useState(null);
  const [isComputingUploadMeta, setIsComputingUploadMeta] = useState(false);
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);
  const uploadAudioFileRef = useRef(null);

  // Track if audio has been uploaded in transcribe modal
  const [hasUploadedAudio, setHasUploadedAudio] = useState(false);

  // Transcription model selection state
  const [selectedTranscriptionModel, setSelectedTranscriptionModel] =
    useState("corti"); // 'corti' or 'vosk'
  const [isModelSelectionOpen, setIsModelSelectionOpen] = useState(false);
  const [pendingTranscribeAction, setPendingTranscribeAction] = useState(null);

  // Auto-select VOSK for freemium users when model selection modal opens
  useEffect(() => {
    if (isModelSelectionOpen && isInGroup(userRole, 'freemium')) {
      setSelectedTranscriptionModel("vosk");
    }
  }, [isModelSelectionOpen]);

  // Transcription credit usage state
  const [transcriptionCreditUsage, setTranscriptionCreditUsage] =
    useState(null);
  const [isViewNotesOpen, setIsViewNotesOpen] = useState(false);

  // Password modal state
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  // Credit usage tracking state
  const [creditUsage, setCreditUsage] = useState({
    recordedTranscription: 0,
    liveTranscription: 0,
    codesTranscription: 0,
    total: 0,
  });

  // Add state for credit limit
  const [creditLimit, setCreditLimit] = useState(15.0); // Default credit limit

  // Credit usage panel is collapsed by default (admin/doctor only sees it).
  // Click the pill in the hero to expand it as a floating dropdown — the
  // expanded panel is absolutely-positioned so it never pushes layout.
  const [isCreditUsageOpen, setIsCreditUsageOpen] = useState(false);

  const [llmTokenUsage, setLlmTokenUsage] = useState({
    total_tokens: 0,
    total_queries: 0,
    percent_of_limit: 0,
    users: [],
    isLoading: false,
    source: null,
    backendError: null,
  });

  const fetchTokenUsage = useCallback(async () => {
    if (!schedulesPageUserId) return;
    setLlmTokenUsage(prev => ({ ...prev, isLoading: true }));
    try {
      const supabase = await getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        setLlmTokenUsage(prev => ({ ...prev, isLoading: false }));
        return;
      }

      const candidates = [];
      const addCandidate = (u) => {
        if (!u) return;
        const clean = String(u).replace(/\/+$/, "");
        if (!candidates.includes(clean)) candidates.push(clean);
      };
      addCandidate(process.env.REACT_APP_BACKEND_URL);
      addCandidate(getBackendURL());
      if (typeof window !== "undefined") addCandidate(window.location.origin);

      const startD = new Date(Date.now() - 30 * 86400000)
        .toISOString()
        .split("T")[0];
      const endD = new Date().toISOString().split("T")[0];

      let apiData = null;
      let backendError = null;
      for (const base of candidates) {
        try {
          const res = await fetch(
            `${base}/api/llm-usage-stats/?start=${startD}&end=${endD}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            },
          );
          if (res.ok) {
            apiData = await res.json();
            break;
          }
          backendError = `${base} → HTTP ${res.status}`;
        } catch (e) {
          backendError = `${base} → ${e?.message || "network error"}`;
        }
      }

      let currentUserUsage = { total_tokens: 0, total_queries: 0 };
      let usersList = [];
      let source = null;
      if (apiData) {
        usersList = apiData.top_users || [];
        const currentUser = usersList.find(
          (u) => String(u.user_id) === String(schedulesPageUserId),
        );
        if (currentUser) {
          currentUserUsage = {
            total_tokens: currentUser.total_tokens || 0,
            total_queries: currentUser.total_queries || 0,
          };
        } else if (apiData.caller_usage) {
          currentUserUsage = {
            total_tokens: apiData.caller_usage.total_tokens || 0,
            total_queries: apiData.caller_usage.total_queries || 0,
          };
        }
        source = "backend";
      }

      if (
        !apiData ||
        (currentUserUsage.total_tokens === 0 &&
          currentUserUsage.total_queries === 0)
      ) {
        try {
          const sinceISO = new Date(
            Date.now() - 30 * 86400000,
          ).toISOString();
          const direct = await aggregateAiFeedbackDirect(
            supabase,
            schedulesPageUserId,
            supabaseSchema,
            sinceISO,
          );
          if (direct) {
            const directUsersList = direct.top_users || [];
            const meInDirect = directUsersList.find(
              (u) => String(u.user_id) === String(schedulesPageUserId),
            );
            const directCaller =
              direct.caller_usage && direct.caller_usage.total_tokens > 0
                ? direct.caller_usage
                : meInDirect;
            if (directCaller && directCaller.total_tokens > 0) {
              currentUserUsage = {
                total_tokens: directCaller.total_tokens || 0,
                total_queries: directCaller.total_queries || 0,
              };
            }
            if (directUsersList.length && usersList.length === 0) {
              usersList = directUsersList.map((u) => ({
                ...u,
                username: u.username || u.name || "User",
              }));
            }
            source = source === "backend" ? "backend+direct" : "direct";
            console.log(
              "[fetchTokenUsage] direct read complete — schemas:",
              direct.schemasHit,
              "rows seen:",
              direct.summary?.total_queries,
              "tokens:",
              direct.summary?.total_tokens,
            );
          }
        } catch (e) {
          console.warn(
            "[fetchTokenUsage] direct ai_feedback fallback threw:",
            e,
          );
        }
      }

      if (!source) source = "none";

      setLlmTokenUsage({
        total_tokens: currentUserUsage.total_tokens,
        total_queries: currentUserUsage.total_queries,
        percent_of_limit:
          (currentUserUsage.total_tokens / LLM_USER_TOKEN_LIMIT) * 100,
        users: usersList,
        isLoading: false,
        source,
        backendError: apiData ? null : backendError,
      });
    } catch (error) {
      console.error("Error fetching LLM token usage:", error);
      setLlmTokenUsage(prev => ({ ...prev, isLoading: false }));
    }
  }, [schedulesPageUserId, supabaseSchema]);

  // Fetch user LLM token usage when the token panel is opened
  useEffect(() => {
    if (isCreditUsageOpen) {
      fetchTokenUsage();
    }
  }, [isCreditUsageOpen, fetchTokenUsage]);

  // Sync usage in real-time when credit/transcription updates occur
  useEffect(() => {
    const handleRefreshUsage = () => {
      sLog("🔄 Refreshing LLM token usage due to transcript/AI update event");
      fetchTokenUsage();
    };
    window.addEventListener("transcriptSaved", handleRefreshUsage);
    window.addEventListener("llmUsageUpdated", handleRefreshUsage);
    return () => {
      window.removeEventListener("transcriptSaved", handleRefreshUsage);
      window.removeEventListener("llmUsageUpdated", handleRefreshUsage);
    };
  }, [fetchTokenUsage]);

  // "Upgrade to Pro" button — per-schema on/off + checkout link, falling back
  // to UPGRADE_DEFAULTS (global env) when this schema has no override set.
  // Toggled from Admin settings (schema_settings.upgrade_enabled/upgrade_link).
  const [upgradeEnabled, setUpgradeEnabled] = useState(UPGRADE_DEFAULTS.enabled);
  const [upgradeLink, setUpgradeLink] = useState(UPGRADE_DEFAULTS.link);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchUpgradeSetting = async () => {
      try {
        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from("schema_settings")
          .select("upgrade_enabled, upgrade_link")
          .eq("schema_name", supabaseSchema)
          .single();

        if (cancelled) return;
        if (error) {
          if (error.code !== "PGRST116") {
            console.error("Error fetching upgrade setting:", error);
          }
          setUpgradeEnabled(UPGRADE_DEFAULTS.enabled);
          setUpgradeLink(UPGRADE_DEFAULTS.link);
          return;
        }

        setUpgradeEnabled(data?.upgrade_enabled ?? UPGRADE_DEFAULTS.enabled);
        setUpgradeLink(data?.upgrade_link || UPGRADE_DEFAULTS.link);
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching upgrade setting:", error);
        }
      }
    };
    fetchUpgradeSetting();
    return () => {
      cancelled = true;
    };
  }, [supabaseSchema]);

  // Secondary-actions dropdown: Add Document, View Document, Audio Manager,
  // Phone Audio, Vita-Med Drive, My Referral Link — Tools control in the page
  // hero (beside Credits when the credit pill is shown).
  const [isMoreActionsOpen, setIsMoreActionsOpen] = useState(false);

  // Track files being processed (uploaded, classified, or having text extracted)
  const [processingFiles, setProcessingFiles] = useState({});

  // Handler for processing state changes from Uploader
  const handleProcessingChange = (filePath, processingInfo) => {
    setProcessingFiles((prev) => {
      if (processingInfo === null) {
        // Remove the file from processing state
        const newState = { ...prev };
        delete newState[filePath];
        return newState;
      }
      // Add or update the file's processing state
      return { ...prev, [filePath]: processingInfo };
    });
  };

  // Log credit usage state changes
  useEffect(() => {
    // sLog("💳 Schedules: Credit usage state changed to:", creditUsage);
  }, [creditUsage]);

  // Initialize credit tracking on component mount
  useEffect(() => {
    const initCredits = async () => {
      // sLog("💳 Schedules: Initializing credit tracking");
      await initializeCreditTracking();

      // Set initial credit usage from the tracker
      const recordedCredits = getRecordedCreditUsage();
      const liveCredits = getLiveCreditUsage();
      const codesCredits = getCodesCreditUsage(); // Load codes credits from database
      setCreditUsage({
        recordedTranscription: recordedCredits,
        liveTranscription: liveCredits,
        codesTranscription: codesCredits,
        total: recordedCredits + liveCredits + codesCredits,
      });

      // Fetch user's credit limit using the same method as checkCreditLimitAndUpdateUI
      try {
        const creditStatus = await cortiLogger.getUserCreditStatus();
        if (creditStatus && creditStatus.creditLimit) {
          setCreditLimit(creditStatus.creditLimit);
          // sLog(
          //   "💳 Schedules: Credit limit loaded:",
          //   creditStatus.creditLimit,
          // );
        }
      } catch (error) {
        console.error("💳 Schedules: Error loading credit limit:", error);
      }
    };

    initCredits();
  }, []);

  // Load auto-reformat setting on component mount
  useEffect(() => {
    // Auto-reformat disabled for now - skip loading from database
    return;

    /* Original code commented out - re-enable when needed
    const loadAutoReformatSetting = async () => {
      try {
        const supabase = await getSupabase();
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session?.user?.id) {
          sLog('📐 No user session found, using default auto-reformat setting');
          return;
        }

        const { data, error } = await supabase
          .from('users')
          .select('settings')
          .eq('id', session.user.id)
          .single();

        if (error) {
          console.error('📐 Error loading auto-reformat setting:', error);
          return;
        }

        const autoReformatSetting = data?.settings?.auto_reformat_enabled ?? true;
        setAutoReformatEnabled(autoReformatSetting);
        sLog('📐 Auto-reformat setting loaded:', autoReformatSetting);
      } catch (error) {
        console.error('📐 Error loading auto-reformat setting:', error);
      }
    };
    
    loadAutoReformatSetting();
    */
  }, []);

  // Log SOAP notes state changes
  useEffect(() => {
    // sLog("🔄 SOAP notes state changed:", soapNotes);
    if (soapNotes) {
      // sLog(
      //   "📋 SOAP notes transcript_only exists:",
      //   !!soapNotes.transcript_only,
      // );
      // sLog(
      //   "📋 SOAP notes transcript_only length:",
      //   soapNotes.transcript_only?.length,
      // );
    }
  }, [soapNotes]);

  // Function to calculate credit usage from schedules data (DEPRECATED - now using user-specific tracking)
  const calculateCreditUsage = (schedulesData) => {
    // sLog(
    //   "💳 calculateCreditUsage called with schedulesData:",
    //   schedulesData,
    // );
    // This function is now deprecated since we're using user-specific credit tracking
    // The credit usage is now loaded from the database and managed by the credit tracker
    // sLog(
    //   "💳 calculateCreditUsage: Using user-specific credit tracking instead of schedule-based calculation",
    // );
  };

  const queryParams = new URLSearchParams(location.search);
  const initialDate = queryParams.get("date") || getDefaultDateString();
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const year = selectedDate ? selectedDate.split("-")[0] : "UnknownYear";

  const selectedVisitAudios = useMemo(() => {
    if (!selectedPatient) return [];
    return filterAudiosForVisitDate(
      selectedPatient.more_audios || [],
      selectedPatient.date_ || selectedDate,
      supabaseSchema,
    );
  }, [selectedPatient, selectedDate]);

  const refreshSelectedPatientFromDb = useCallback(async () => {
    const patientId = selectedPatient?.id;
    if (!patientId) return;

    try {
      const supabase = await getSupabase();
      const { data: row, error } = await supabase
        .from(tableName)
        .select("*")
        .eq("id", patientId)
        .maybeSingle();

      if (error) throw error;
      if (!row) return;

      // Test-integration plan Phase 1c: fire-and-forget, off by default
      // (REACT_APP_RDS_SHADOW_READ_SCHEDULES). Never awaited -- cannot
      // add latency or affect anything below this line.
      shadowCompareScheduleRow(supabaseSchema, tableName, patientId, row);

      const visitDate = row.date_ || selectedPatient?.date_ || selectedDate;
      const patientData = {
        ...row,
        id: row.id,
        date_: row.date_,
        time_: row.time_,
        more_audios: filterAudiosForVisitDate(
          row.more_audios || [],
          visitDate,
          supabaseSchema,
        ),
        ...prompts.reduce((acc, [key]) => {
          acc[key] = row[key] || "";
          return acc;
        }, {}),
      };

      setSelectedPatient(patientData);
      clearBackupCache(patientId);

      setSchedules((prev) =>
        (prev || []).map((s) =>
          s.id === patientId
            ? {
                ...s,
                ...patientData,
                more_audios: patientData.more_audios,
                transcripts: row.transcripts,
              }
            : s,
        ),
      );

      window.dispatchEvent(
        new CustomEvent("patientContextUpdate", {
          detail: {
            patientId,
            patientData: [patientData],
          },
        }),
      );
      window.dispatchEvent(
        new CustomEvent("patientDataRefresh", {
          detail: {
            patientId,
            timestamp: new Date().toISOString(),
            type: "audio_manager_restore",
          },
        }),
      );
    } catch (err) {
      console.error("[Schedules] refreshSelectedPatientFromDb:", err);
      toast({
        title: "Refresh failed",
        description:
          err.message || "Could not reload patient audio after restore.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [selectedPatient?.id, selectedPatient?.date_, selectedDate, toast]);

  const handleSelectedRowChange = (data) => {
    if (!data) {
      setSelectedRows({});
      setSelectedPatient(null);
      return;
    }

    // Handle the new format where data contains both selection and scheduleData
    if (data.selection) {
      setSelectedRows(data.selection);
    }

    if (data.scheduleData) {
      // Ensure we have all the necessary data from the patient record
      const patientData = {
        ...data.scheduleData,
        // Ensure these fields are present
        id: data.scheduleData.id,
        date_: data.scheduleData.date_,
        time_: data.scheduleData.time_,
        // Add any other required fields from prompts
        ...prompts.reduce((acc, [key]) => {
          acc[key] = data.scheduleData[key] || "";
          return acc;
        }, {}),
      };

      setSelectedPatient(patientData);

      // Notify RecordingBubble and other components of the patient context change
      window.dispatchEvent(
        new CustomEvent("patientContextUpdate", {
          detail: {
            patientId: patientData.id,
            patientData: [patientData],
          },
        }),
      );
    }
  };

  const safeExportName = (value) =>
    String(value || "Unknown")
      .trim()
      .replace(/[<>:"/\\|?*\x00-\x1F]/g, "_")
      .replace(/\s+/g, "_")
      .slice(0, 120);

  const convertDeltaToMarkdown = (delta) => {
    if (!delta || !delta.ops) return "";
    return delta.ops
      .map((op) => (typeof op.insert === "string" ? op.insert : ""))
      .join("")
      .trim();
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getPatientMedADocsPath = (patient) => {
    const patientName =
      patient?.[prompts[0][0]] || patient?.patient_name || "Unknown";
    const visitDate = patient?.date_;
    if (!visitDate) return null;
    const ehrIdIndex = prompts.findIndex((p) => p[0] === "EHR_ID");
    const ehrIdField = ehrIdIndex >= 0 ? prompts[ehrIdIndex][0] : null;
    const patientIdForFile =
      ehrIdField && patient?.[ehrIdField] ? patient[ehrIdField] : patient?.id;
    return generateMedADocsPath(
      visitDate.split("-")[0],
      visitDate,
      `${patientName}-${patientIdForFile}.json`,
    );
  };

  const parseSubmissionIdentity = (submission) => {
    if (!submission) return {};
    if (typeof submission === "object") {
      const url = submission.url || "";
      const match =
        typeof url === "string"
          ? url.match(/\/([^/]+)\/submission\/([^/?#]+)/)
          : null;
      return {
        form_id: submission.form_id || match?.[1],
        submission_id: submission.submission_id || submission.id || match?.[2],
        url,
        raw: submission,
      };
    }
    const match = String(submission).match(/\/([^/]+)\/submission\/([^/?#]+)/);
    return {
      form_id: match?.[1],
      submission_id: match?.[2],
      url: String(submission),
      raw: submission,
    };
  };

  const handleDownloadPatientData = async () => {
    if (!selectedPatient) {
      toast({
        title: "No patient selected",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsDownloadingPatientData(true);
    try {
      const supabase = await getSupabase();
      const patientName =
        selectedPatient?.[prompts[0][0]] ||
        selectedPatient?.patient_name ||
        "Unknown";
      const visitDate = selectedPatient?.date_ || selectedDate || "unknown-date";
      const rootName = `${safeExportName(patientName)}_${safeExportName(visitDate)}`;
      const zip = new JSZip();
      const root = zip.folder(rootName);

      root.file("patient-data.json", JSON.stringify(selectedPatient, null, 2));

      const documentPath = getPatientMedADocsPath(selectedPatient);
      if (documentPath) {
        const doc = await fetchDocument(bucketName_files, documentPath);
        root.file("notes.md", convertDeltaToMarkdown(doc?.delta) || "");
        root.file(
          "notes-source.json",
          JSON.stringify({ path: documentPath, document: doc }, null, 2),
        );
      } else {
        root.file("notes.md", "");
      }

      const reportFolderPath = selectedPatient?.[file_reference_column];
      if (reportFolderPath) {
        const reportsFolder = root.folder("reports");
        const { data: reportFiles, error: listError } = await supabase.storage
          .from(bucketName_files)
          .list(reportFolderPath);
        if (!listError && Array.isArray(reportFiles)) {
          for (const file of reportFiles.filter((item) => item.name.includes("."))) {
            const filePath = `${reportFolderPath}/${file.name}`;
            const { data: fileBlob, error: downloadError } =
              await supabase.storage.from(bucketName_files).download(filePath);
            if (!downloadError && fileBlob) {
              reportsFolder.file(file.name, fileBlob);
            }
          }
        }
      }

      const audios = Array.isArray(selectedPatient.more_audios)
        ? selectedPatient.more_audios
        : [];
      const audioFolder = root.folder("audio");
      audioFolder.file("audio-metadata.json", JSON.stringify(audios, null, 2));
      for (const [index, audioItem] of audios.entries()) {
        if (!audioItem?.url) continue;
        try {
          const response = await fetch(audioItem.url);
          if (!response.ok) continue;
          const audioBlob = await response.blob();
          audioFolder.file(
            safeExportName(
              audioItem.name || audioItem.displayName || `audio_${index + 1}`,
            ),
            audioBlob,
          );
        } catch (error) {
          console.warn("Could not download audio for export:", audioItem.url, error);
        }
      }

      const transcripts = selectedPatient.transcripts
        ? Array.isArray(selectedPatient.transcripts)
          ? selectedPatient.transcripts
          : [selectedPatient.transcripts]
        : [];
      const transcriptsFolder = root.folder("transcripts");
      transcriptsFolder.file("transcripts.json", JSON.stringify(transcripts, null, 2));
      transcripts.forEach((item, index) => {
        const label = safeExportName(
          item?.audio_file_name || item?.id || `transcript_${index + 1}`,
        );
        const transcriptText = Array.isArray(item?.transcript)
          ? item.transcript.join("\n")
          : String(item?.transcript || "");
        transcriptsFolder.file(`${label}.md`, transcriptText);
      });

      const submissions = Array.isArray(selectedPatient.submissions_array)
        ? selectedPatient.submissions_array
        : [];
      const formsFolder = root.folder("forms");
      formsFolder.file("submission-links.json", JSON.stringify(submissions, null, 2));
      for (const [index, submission] of submissions.entries()) {
        const identity = parseSubmissionIdentity(submission);
        const label = safeExportName(
          identity.submission_id || identity.form_id || `form_${index + 1}`,
        );
        if (identity.form_id && identity.submission_id) {
          const { data: formData } = await supabase
            .from("StoringFormSubmission")
            .select("*")
            .eq("form_id", identity.form_id)
            .eq("submission_id", identity.submission_id)
            .maybeSingle();
          formsFolder.file(
            `${label}.json`,
            JSON.stringify(formData || identity.raw, null, 2),
          );
          if (formData?.full_text) {
            formsFolder.file(`${label}.md`, formData.full_text);
          }
        } else {
          formsFolder.file(`${label}.json`, JSON.stringify(identity.raw, null, 2));
        }
      }

      const blob = await zip.generateAsync({ type: "blob" });
      downloadBlob(blob, `${rootName}.zip`);
      toast({
        title: "Patient data downloaded",
        description: `Created ${rootName}.zip`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error downloading patient data:", error);
      toast({
        title: "Download failed",
        description: error?.message || "Could not create patient data zip.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDownloadingPatientData(false);
    }
  };

  // Check user role on component mount
  useEffect(() => {
    const checkUserRole = async () => {
      console.log("[Schedules] checkUserRole: starting — waiting for Supabase client...");
      const supabase = await getSupabase();
      console.log("[Schedules] checkUserRole: Supabase client ready, fetching session...");
      try {
        // Get the current session — with a timeout so a slow Supabase doesn't hang forever
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error(
            "auth.getSession() timed out after 12s.\n" +
            "This call goes directly to the Supabase auth API (not the backend).\n" +
            "Root cause: the Supabase instance itself is under high load — CPU or Disk I/O is elevated.\n" +
            "Check the Monitor page: if CPU >70% or Disk I/O is high, this is why auth responses are slow.\n" +
            "The activity_history JSONB read/write cycle and versioning queries are likely the main contributors."
          )), 12000)
        );
        const {
          data: { session },
          error: sessionError,
        } = await Promise.race([sessionPromise, timeoutPromise]);
        if (sessionError || !session) {
          console.warn("[Schedules] checkUserRole: no session found →", sessionError?.message || "no session");
          toast({
            title: "Authentication Error",
            description: "Please log in to access this page.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          navigate("/auth");
          return;
        }

        // Set the user id IMMEDIATELY so the LLM-usage panel (and any other
        // user-id-gated fetches) doesn't get stuck on zero/empty values when
        // the slower role query below errors or times out. Role is only used
        // for access-control decisions; the rest of the page works with just
        // session.user.id.
        setSchedulesPageUserId(session.user.id ?? null);

        // Fetch user role from the "users" table
        const userEmail = session.user.email;
        console.log("[Schedules] checkUserRole: session found for", userEmail, "— querying users.role...");
        // Read from public.users: the patient_* verification columns only exist
        // there, not in tenant-schema copies, so querying the active schema would
        // 42703 for any user whose active schema is a tenant schema.
        const rolePromise = fetchEffectiveUserRow({ email: userEmail });
        const roleTimeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error(
            "SELECT role FROM users timed out after 12s.\n" +
            "This is a direct Supabase PostgREST query. It's slow because the Supabase DB is under high load.\n" +
            "Likely contributors: activity_history JSONB being read/written on every page load by multiple users, " +
            "versioning SELECT * on document_backups, and/or admin page notes-c table scans.\n" +
            "Check the Monitor page CPU and Disk I/O graphs."
          )), 12000)
        );
        const { data, error } = await Promise.race([rolePromise, roleTimeout]);

        if (error) {
          console.error("[Schedules] checkUserRole: users.role query failed →", error.message);
          throw error;
        }
        console.log("[Schedules] checkUserRole: role =", data.role);

        // Store user role and name
        setUserRole(data.role);
        setCurrentUserName(data.name || session.user.user_metadata?.full_name || userEmail.split("@")[0]);
        setSchedulesPageUserId(session.user.id ?? null);

        const baseRole = normalizeRole(data.role);
        if (
          hasDoctorRolePermissions(data.role) ||
          baseRole === "medstudent" ||
          isInGroup(data.role, "tenantSchema") ||
          isPatientRole(data.role)
        ) {
          console.log("[Schedules] checkUserRole: access granted, rendering page");
          setIsAuthenticated(true);
        } else if (baseRole === "admin") {
          console.log("[Schedules] checkUserRole: admin role → redirecting to /admin-page");
          navigate("/admin-page");
        } else {
          toast({
            title: "Access Denied",
            description: "You do not have permission to access this page.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          navigate("/forbidden"); // Redirect to a forbidden page
        }
      } catch (error) {
        console.error("[Schedules] checkUserRole: caught error →", error.message, error);
        toast({
          title: "Error",
          description: "Failed to verify user role.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        navigate("/auth");
      }
    };

    checkUserRole();
  }, [navigate, toast]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!schedulesPageUserId || !isInGroup(userRole, 'tenantSchema')) {
        if (!cancelled) {
          setTenantShareAllOwnerIds([]);
          setTenantShareAllOwnerRoles({});
        }
        return;
      }
      try {
        const res = await fetch(
          `${getBackendURL()}/api/shared-all-owner-ids/?user_id=${encodeURIComponent(schedulesPageUserId)}`,
        );
        const json = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && Array.isArray(json.owner_ids))
          setTenantShareAllOwnerIds(json.owner_ids);
        if (res.ok && json.owner_roles && typeof json.owner_roles === "object")
          setTenantShareAllOwnerRoles(json.owner_roles);
      } catch (_) {
        if (!cancelled) {
          setTenantShareAllOwnerIds([]);
          setTenantShareAllOwnerRoles({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userRole, schedulesPageUserId]);

  useEffect(() => {
    if (
      !selectedPatient ||
      !schedulesPageUserId ||
      !isInGroup(userRole, 'tenantSchema')
    ) {
      setSelectedScheduleTenantAccess({
        canEditScheduleFields: true,
        canDeleteRow: true,
      });
      return;
    }
    const acc = resolveTenantRowAccess({
      viewerId: schedulesPageUserId,
      rowUserId: selectedPatient.user_id,
      sharedUsers: selectedPatient.shared_users,
      shareAllOwnerIds: tenantShareAllOwnerIds,
      shareAllOwnerRoles: tenantShareAllOwnerRoles,
    });
    setSelectedScheduleTenantAccess({
      canEditScheduleFields: acc.canEditScheduleFields,
      canDeleteRow: acc.canDeleteRow,
    });
  }, [
    selectedPatient,
    schedulesPageUserId,
    userRole,
    tenantShareAllOwnerIds,
    tenantShareAllOwnerRoles,
  ]);

  useEffect(() => {
    const newDate = queryParams.get("date") || getDefaultDateString();
    setSelectedDate(newDate);
  }, [location.search]);

  // Calculate credit usage when schedules data changes (DEPRECATED)
  useEffect(() => {
    // This effect is now deprecated since we're using user-specific credit tracking
    // sLog(
    //   "💳 Schedules: Credit calculation from schedules data is deprecated",
    // );
  }, [schedules]);

  // Listen for schedules data refresh event to recalculate credits (DEPRECATED)
  useEffect(() => {
    // This effect is now deprecated since we're using user-specific credit tracking
    // sLog(
    //   "💳 Schedules: Schedules data refresh credit calculation is deprecated",
    // );
  }, [schedules]);

  // Listen for live and recorded credit usage updates
  useEffect(() => {
    // sLog("💳 Schedules: Setting up credit usage event listeners");

    const handleLiveCreditsUpdated = (event) => {
      // sLog(
      //   "💳 Schedules: Received live credits updated event:",
      //   event.detail,
      // );
      const { totalLiveCredits } = event.detail;
      // sLog(
      //   "💳 Schedules: totalLiveCredits from event:",
      //   totalLiveCredits,
      // );

      setCreditUsage((prevCreditUsage) => {
        // sLog(
        //   "💳 Schedules: Previous credit usage state:",
        //   prevCreditUsage,
        // );
        const newTotal =
          prevCreditUsage.recordedTranscription +
          totalLiveCredits +
          prevCreditUsage.codesTranscription;

        // sLog("💳 Schedules: Updated credit usage with live credits:", {
        //   recorded: prevCreditUsage.recordedTranscription,
        //   live: totalLiveCredits,
        //   codes: prevCreditUsage.codesTranscription,
        //   total: newTotal,
        // });

        const newState = {
          recordedTranscription: prevCreditUsage.recordedTranscription,
          liveTranscription: totalLiveCredits,
          codesTranscription: prevCreditUsage.codesTranscription,
          total: newTotal,
        };

        // sLog("💳 Schedules: New credit usage state:", newState);
        return newState;
      });
    };

    const handleRecordedCreditsUpdated = (event) => {
      // sLog(
      //   "💳 Schedules: Received recorded credits updated event:",
      //   event.detail,
      // );
      const { totalRecordedCredits } = event.detail;
      // sLog(
      //   "💳 Schedules: totalRecordedCredits from event:",
      //   totalRecordedCredits,
      // );

      setCreditUsage((prevCreditUsage) => {
        // sLog(
        //   "💳 Schedules: Previous credit usage state:",
        //   prevCreditUsage,
        // );
        const newTotal =
          totalRecordedCredits +
          prevCreditUsage.liveTranscription +
          prevCreditUsage.codesTranscription;

        // sLog(
        //   "💳 Schedules: Updated credit usage with recorded credits:",
        //   {
        //     recorded: totalRecordedCredits,
        //     live: prevCreditUsage.liveTranscription,
        //     codes: prevCreditUsage.codesTranscription,
        //     total: newTotal,
        //   },
        // );

        const newState = {
          recordedTranscription: totalRecordedCredits,
          liveTranscription: prevCreditUsage.liveTranscription,
          codesTranscription: prevCreditUsage.codesTranscription,
          total: newTotal,
        };

        // sLog("💳 Schedules: New credit usage state:", newState);
        return newState;
      });
    };

    const handleCodesCreditsUpdated = (event) => {
      // sLog(
      //   "💳 Schedules: Received codes credits updated event:",
      //   event.detail,
      // );
      const { totalCodesCredits } = event.detail;
      // sLog(
      //   "💳 Schedules: totalCodesCredits from event:",
      //   totalCodesCredits,
      // );

      setCreditUsage((prevCreditUsage) => {
        // sLog(
        //   "💳 Schedules: Previous credit usage state:",
        //   prevCreditUsage,
        // );
        const newTotal =
          prevCreditUsage.recordedTranscription +
          prevCreditUsage.liveTranscription +
          totalCodesCredits;

        // sLog("💳 Schedules: Updated credit usage with codes credits:", {
        //   recorded: prevCreditUsage.recordedTranscription,
        //   live: prevCreditUsage.liveTranscription,
        //   codes: totalCodesCredits,
        //   total: newTotal,
        // });

        const newState = {
          recordedTranscription: prevCreditUsage.recordedTranscription,
          liveTranscription: prevCreditUsage.liveTranscription,
          codesTranscription: totalCodesCredits,
          total: newTotal,
        };

        // sLog("💳 Schedules: New credit usage state:", newState);
        return newState;
      });
    };

    // sLog("💳 Schedules: Adding event listeners for credit updates");
    window.addEventListener("liveCreditsUpdated", handleLiveCreditsUpdated);
    window.addEventListener(
      "recordedCreditsUpdated",
      handleRecordedCreditsUpdated,
    );
    window.addEventListener("codesCreditsUpdated", handleCodesCreditsUpdated);

    return () => {
      // sLog("💳 Schedules: Removing event listeners for credit updates");
      window.removeEventListener(
        "liveCreditsUpdated",
        handleLiveCreditsUpdated,
      );
      window.removeEventListener(
        "recordedCreditsUpdated",
        handleRecordedCreditsUpdated,
      );
      window.removeEventListener(
        "codesCreditsUpdated",
        handleCodesCreditsUpdated,
      );
    };
  }, []);

  useEffect(() => {
    if (!selectedDate) return;

    const checkExistingDocument = async () => {
      const supabase = await getSupabase();

      // Use the already-parsed active schema from supabaseClient (single source of truth)
      const userSchema = supabaseSchema || "public";

      // Use helper function to generate folder path with schema
      const folderPath = generateMedADocsFolderPath(
        year,
        selectedDate,
        userSchema,
      );
      const { data, error } = await supabase.storage
        .from(bucketName_files)
        .list(folderPath);

      if (error) {
        console.error("Error checking for existing document:", error.message);
        return;
      }

      const fileMatch = data.find((file) => file.name === "SOAP_note-1.json");
      setExistingDocument(fileMatch ? fileMatch.name : null);
    };

    checkExistingDocument();
  }, [selectedDate]);

  const viewDocument = async () => {
    if (existingDocument) {
      const [name, id] = existingDocument.replace(".json", "").split("-");

      // Generate file path with schema support
      const filePath = await generateMedADocsPath(
        year,
        selectedDate,
        existingDocument,
      );

      // Get the time from the filename (format: time_name-id.json)
      const timeMatch = existingDocument.match(/^(\d{2}:\d{2}:\d{2})_/);
      const time = timeMatch ? timeMatch[1] : "";

      navigate(`/patient/${id}/edit-document`, {
        state: {
          name,
          id,
          date_: selectedDate,
          filePath: filePath,
          time_: time,
        },
      });
    }
  };

  const handlePatientClick = (patient) => {
    sLog("🚀 Patient clicked:", { uuid: patient.id, patient });
    // Get patient name from the correct field (prompts[0][0])
    const patientName = patient[prompts[0][0]] || patient.name || "Unknown";
    const visitSeg = getPatientPageVisitPathSegment(patient);
    const schemaQ = patient._schema ? `?schema=${encodeURIComponent(patient._schema)}` : '';
    navigate(`/visit/${visitSeg}${schemaQ}`, {
      state: {
        rowData: patient,
        date_: patient.date_,
        name: patientName,
        _schema: patient._schema,
      },
    });
  };

  const handleAddPatient = async (newPatient) => {
    setPatients((prevPatients) => [...prevPatients, newPatient]);
    // Send welcome first (waits for all parts), then forms
    await handleWelcomeSending(newPatient, toast);
    await handleFormSending(newPatient, toast);

    // Simple page refresh to show the new patient
    // window.location.reload(); 
  };

  const handleUpdatePatient = (updatedPatient) => {
    setPatients((prev) =>
      prev.map((p) => (p.id === updatedPatient.id ? updatedPatient : p)),
    );

    // Set localStorage flag to notify PatientPage to refresh
    localStorage.setItem("patientUpdated", updatedPatient.id);

    // Refresh the schedules to show updated data
    sLog("🔄 Refreshing schedules after patient update");
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const handleDuplicatePatient = () => {
    if (!selectedPatient) {
      toast({
        title: "No Patient Selected",
        description: "Please select a patient to duplicate.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    sLog("Selected patient before opening duplicate modal:", selectedPatient);
    setIsDuplicateModalOpen(true);
  };

  const handleDuplicateSave = async (newPatientData) => {
    const supabase = await getSupabase();
    try {
      sLog("Attempting to insert patient data:", newPatientData);

      // Map form data keys to database column names using prompts array
      const patientDataToInsert = {};

      // Map the form data to the prompts array structure
      const fieldMapping = {
        FirstName: prompts[0][0], // First prompt field (name/patient_name)
        Email:
          prompts.find((p) => p[1].toLowerCase().includes("email"))?.[0] ||
          "email",
        "Phone Number":
          prompts.find((p) => p[1].toLowerCase().includes("phone"))?.[0] ||
          "tel_num",
        "Class Id": "EHR_ID",
        "Output Format":
          prompts.find((p) => p[1].toLowerCase().includes("format"))?.[0] ||
          "format",
        Type:
          prompts.find((p) => p[1].toLowerCase().includes("type"))?.[0] ||
          "type",
        "Scheduled Date": "date_",
        "Scheduled Time": "time_",
        Forms: "forms_ind",
      };

      // Build the patient data using the mapping
      Object.keys(newPatientData).forEach((key) => {
        const mappedField = fieldMapping[key];
        if (mappedField) {
          patientDataToInsert[mappedField] = newPatientData[key];
        }
      });

      // Validate required fields based on the prompts array
      const requiredFields = prompts.filter((p) => p[2] === "Required");
      for (const field of requiredFields) {
        if (!patientDataToInsert[field[0]]) {
          throw new Error(`${field[1]} is a required field.`);
        }
      }
      if (!patientDataToInsert.date_) {
        throw new Error("Scheduled Date is a required field.");
      }
      if (!patientDataToInsert.time_) {
        throw new Error("Scheduled Time is a required field.");
      }

      // Insert the new patient record
      const { data, error } = await supabase
        .from("patients") // Assuming table name is "patients"
        .insert([patientDataToInsert])
        .select()
        .single();

      if (error) {
        console.error("Supabase error details:", error);
        throw error;
      }
      // Phase D (utils/rdsCutover.js `write_patients`): Supabase is the
      // source of truth and just accepted the row; when the flag is on,
      // RDS gets the same row now instead of at the next sync. Best effort,
      // reported, never awaited -- the user's write is already done.
      mirrorWriteToRds("write_patients", supabaseSchema || "public", data);

      setSchedules([...schedules, data]); // Assuming 'schedules' state holds the table data
      toast({
        title: "Success",
        description: "Patient duplicated successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setIsDuplicateModalOpen(false);
    } catch (error) {
      console.error("Error duplicating patient:", error);
      toast({
        title: "Error",
        description: `Failed to duplicate patient: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleTranscribeAudio = async () => {
    if (!selectedPatient) {
      toast({
        title: "No Patient Selected",
        description: "Please select a patient to transcribe audio.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Open the modal immediately for a snappy click-to-open response.
    // The credit-limit check (HTTP roundtrip to /api/llm-usage-stats/)
    // and the password-protection check (Supabase auth + users table
    // query) used to run sequentially before this point, adding several
    // seconds of perceived latency. Now we run them in parallel in the
    // background; if either blocks the action, we close the modal and
    // route to the right follow-up (toast or password prompt).
    setIsTranscribeModalOpen(true);

    void (async () => {
      try {
        const [creditLimitExceeded, passwordOk] = await Promise.all([
          checkCreditLimitAndUpdateUI(toast),
          hasValidPassword(),
        ]);
        if (creditLimitExceeded) {
          setIsTranscribeModalOpen(false);
          return;
        }
        if (!passwordOk) {
          sLog("🔒 Password required to transcribe audio");
          setIsTranscribeModalOpen(false);
          setPendingAction(() => () => setIsTranscribeModalOpen(true));
          setIsPasswordModalOpen(true);
        }
      } catch (err) {
        // Don't close the modal on a transient preflight error — let the
        // user proceed; any real failure will surface at upload time.
        console.error("Transcribe preflight error:", err);
      }
    })();
  };
  // Function to clean and validate SOAP notes
  const cleanSoapNotes = (soapNotes) => {
    if (!soapNotes) return null;

    sLog("🧹 Cleaning SOAP notes:", soapNotes);

    const cleaned = {};

    // Clean transcript_only
    if (soapNotes.transcript_only) {
      try {
        // Check if it's JSON schema (contains "properties" and "type")
        if (
          typeof soapNotes.transcript_only === "string" &&
          soapNotes.transcript_only.includes('"properties"') &&
          soapNotes.transcript_only.includes('"type"')
        ) {
          sLog("⚠️ Detected JSON schema in transcript_only, skipping");
          cleaned.transcript_only = null;
        } else {
          cleaned.transcript_only = soapNotes.transcript_only;
          sLog("✅ transcript_only cleaned successfully");
        }
      } catch (error) {
        console.error("Error cleaning transcript_only:", error);
        cleaned.transcript_only = null;
      }
    }

    // Clean facts_only
    if (soapNotes.facts_only) {
      try {
        // Check if it's JSON schema (contains "properties" and "type")
        if (
          typeof soapNotes.facts_only === "string" &&
          soapNotes.facts_only.includes('"properties"') &&
          soapNotes.facts_only.includes('"type"')
        ) {
          sLog("⚠️ Detected JSON schema in facts_only, skipping");
          cleaned.facts_only = null;
        } else {
          cleaned.facts_only = soapNotes.facts_only;
          sLog("✅ facts_only cleaned successfully");
        }
      } catch (error) {
        console.error("Error cleaning facts_only:", error);
        cleaned.facts_only = null;
      }
    }

    sLog("🧹 Cleaned SOAP notes result:", cleaned);
    return cleaned;
  };

  const generatePDF = () => {
    const doc = new jsPDF();

    // Add title
    doc.setFontSize(20);
    doc.text("Medical Report Transcription", 20, 20);

    // Add patient info
    doc.setFontSize(14);
    doc.text(
      `Patient: ${selectedPatient?.[prompts[0][0]] || "Unknown"}`,
      20,
      35,
    );
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 45);
    doc.text(`Duration: ${lastElapsedTime || 0} seconds`, 20, 55);

    // Add transcript
    doc.setFontSize(16);
    doc.text("Transcript:", 20, 75);
    doc.setFontSize(10);
    let yPos = 85;
    transcript?.forEach((line, index) => {
      if (yPos > 280) {
        doc.addPage();
        yPos = 20;
      }
      // Split long lines to fit page width
      const lines = doc.splitTextToSize(line, 170);
      lines.forEach((splitLine) => {
        if (yPos > 280) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(splitLine, 20, yPos);
        yPos += 6;
      });
    });

    // Add facts
    doc.addPage();
    doc.setFontSize(16);
    doc.text("Extracted Facts:", 20, 20);
    doc.setFontSize(10);
    yPos = 30;
    facts?.forEach((fact, index) => {
      if (yPos > 280) {
        doc.addPage();
        yPos = 20;
      }
      const factText = `${index + 1}. ${fact.text} (Category: ${fact.group})`;
      const lines = doc.splitTextToSize(factText, 170);
      lines.forEach((line) => {
        if (yPos > 280) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, 20, yPos);
        yPos += 6;
      });
      yPos += 3; // Add space between facts
    });

    // Add SOAP Notes
    if (soapNotes && (soapNotes.transcript_only || soapNotes.facts_only)) {
      doc.addPage();
      doc.setFontSize(16);
      doc.text("SOAP Notes:", 20, 20);
      doc.setFontSize(10);
      yPos = 30;

      if (soapNotes.transcript_only) {
        doc.setFontSize(12);
        doc.text("Transcript-Based SOAP Notes:", 20, yPos);
        yPos += 10;
        doc.setFontSize(10);
        const soapLines = doc.splitTextToSize(soapNotes.transcript_only, 170);
        soapLines.forEach((line) => {
          if (yPos > 280) {
            doc.addPage();
            yPos = 20;
          }
          doc.text(line, 20, yPos);
          yPos += 6;
        });
        yPos += 10;
      }

      if (soapNotes.facts_only) {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        doc.setFontSize(12);
        doc.text("Facts-Based SOAP Notes:", 20, yPos);
        yPos += 10;
        doc.setFontSize(10);
        const soapLines = doc.splitTextToSize(soapNotes.facts_only, 170);
        soapLines.forEach((line) => {
          if (yPos > 280) {
            doc.addPage();
            yPos = 20;
          }
          doc.text(line, 20, yPos);
          yPos += 6;
        });
      }
    }

    doc.save("medical-report.pdf");
  };

  // Estimate transcription time based on audio duration with dynamic scaling
  const estimateTranscriptionTime = (audioDuration) => {
    if (!audioDuration) return 60; // Default 60 seconds if no duration

    // Dynamic scaling based on audio duration
    if (audioDuration <= 60) {
      // Short audio (≤1 min): faster progress (1/4 of duration)
      return Math.max(15, audioDuration / 4);
    } else if (audioDuration <= 180) {
      // Medium audio (1-3 min): moderate progress (1/6 of duration)
      return Math.max(20, audioDuration / 6);
    } else if (audioDuration <= 240) {
      // Long audio (3-4 min): expect ~50 seconds for processing
      return 50;
    } else {
      // Very long audio (>4 min): scale proportionally but cap at reasonable time
      // For 4+ min, add extra time: base 50s + (duration - 240) / 10
      return Math.min(90, 50 + (audioDuration - 240) / 10);
    }
  };

  const updateProgress = (elapsedTime, estimatedTotalTime, audioDuration) => {
    // Adjust progress speed based on audio duration
    let progressMultiplier = 1;

    if (audioDuration <= 60) {
      // Short audio: slightly faster progress
      progressMultiplier = 1.2;
    } else if (audioDuration <= 180) {
      // Medium audio: normal progress
      progressMultiplier = 1.0;
    } else if (audioDuration <= 240) {
      // Long audio (3-4 min): faster progress to reach ~95-96% at 50 seconds
      // At 50s with 50s estimate: (50 * 0.96) / 50 = 96%
      progressMultiplier = 0.96;
    } else {
      // Very long audio (>4 min): moderately faster progress
      progressMultiplier = 0.9;
    }

    const adjustedElapsedTime = elapsedTime * progressMultiplier;
    const progress = Math.min(
      98,
      Math.round((adjustedElapsedTime / estimatedTotalTime) * 100),
    );
    setEstimatedProgress(progress);

    // Update milestone based on progress
    if (progress < 10) {
      setCurrentMilestone("Authenticating");
    } else if (progress < 20) {
      setCurrentMilestone("Creating Session");
    } else if (progress < 35) {
      setCurrentMilestone("Uploading Audio");
    } else if (progress < 50) {
      setCurrentMilestone("Processing Audio");
    } else if (progress < 65) {
      setCurrentMilestone("Creating Transcript");
    } else if (progress < 80) {
      setCurrentMilestone("Extracting Facts");
    } else if (progress < 95) {
      setCurrentMilestone("Generating SOAP Notes");
    } else {
      setCurrentMilestone("Finalizing");
    }
  };

  const startTimer = () => {
    setElapsedTime(0);
    setEstimatedProgress(0);
    setCurrentMilestone("");
    setMilestoneProgress(0);

    // Estimate total transcription time based on audio duration
    const estimatedTotalTime = estimateTranscriptionTime(selectedAudioDuration);

    timerRef.current = setInterval(() => {
      setElapsedTime((prev) => {
        const newTime = prev + 1;
        updateProgress(newTime, estimatedTotalTime, selectedAudioDuration);
        return newTime;
      });
    }, 1000);
  };

  const stopTimer = () => {
    clearInterval(timerRef.current);
    // Don't automatically set to 100% - let it stay where it is
  };

  const handleLiveTranscribe = async () => {
    // Check credit limit before proceeding
    const creditLimitExceeded = await checkCreditLimitAndUpdateUI(toast);
    if (creditLimitExceeded) {
      return;
    }

    setIsLiveTranscribeModalOpen(true);
    setLiveTranscript("");
    setLiveFacts([]);
  };

  // Helper: compute audio duration from a File or URL via an audio element.
  // For WebM files the browser reports duration=Infinity in metadata headers.
  // The workaround is to seek past the end — the browser clamps to the real
  // end frame and fires 'seeked' with the true currentTime.
  const computeAudioDuration = (source) =>
    new Promise((resolve) => {
      const el = document.createElement("audio");
      el.preload = "auto";
      let objectUrl = null;
      const cleanup = () => {
        try {
          el.src = "";
        } catch (_) {}
        if (objectUrl) {
          try {
            URL.revokeObjectURL(objectUrl);
          } catch (_) {}
        }
      };
      const timer = setTimeout(() => {
        cleanup();
        resolve(null);
      }, 30000);

      el.addEventListener("loadedmetadata", () => {
        const d = el.duration;
        if (isFinite(d) && d > 0) {
          clearTimeout(timer);
          cleanup();
          resolve(d);
        } else {
          // WebM: seek past the end to force the browser to scan to EOF
          el.currentTime = 1e10;
        }
      });

      el.addEventListener("seeked", () => {
        clearTimeout(timer);
        const d = el.currentTime;
        cleanup();
        resolve(d > 0 ? d : null);
      });

      el.addEventListener("error", () => {
        clearTimeout(timer);
        cleanup();
        resolve(null);
      });

      if (source instanceof File) {
        objectUrl = URL.createObjectURL(source);
        el.src = objectUrl;
      } else {
        el.src = source;
      }
    });

  // Duration (seconds) of a finished phone recording.
  //
  // Prefers the decoded audio, which is authoritative — the wall-clock timer
  // also counts any paused time and any lag between "start" and the first
  // captured frame. Falls back to the timer when the browser can't report a
  // usable duration (Chrome's MediaRecorder WebM often has no duration header,
  // so `computeAudioDuration` has to seek to EOF and can still come back null).
  //
  // The start time comes from a REF: this runs inside MediaRecorder.onstop,
  // where the state variable would be stale.
  const resolvePhoneRecordingDuration = async (source) => {
    let measured = null;
    try {
      measured = await computeAudioDuration(source);
    } catch (_) {
      measured = null;
    }
    if (measured && isFinite(measured) && measured > 0) return measured;
    const startedAt = phoneRecordingStartRef.current;
    return startedAt ? (Date.now() - startedAt) / 1000 : 0;
  };

  const handleUploadAudioFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadAudioFileInput(file);
    setUploadAudioUrl("");
    setUploadAudioDuration(null);
    setIsComputingUploadMeta(true);
    const dur = await computeAudioDuration(file);
    setUploadAudioDuration(dur);
    setIsComputingUploadMeta(false);
  };

  const handleUploadAudioUrlChange = async (url) => {
    setUploadAudioUrl(url);
    setUploadAudioFileInput(null);
    setUploadAudioDuration(null);
    if (!url.trim()) return;
    setIsComputingUploadMeta(true);
    const dur = await computeAudioDuration(url.trim());
    setUploadAudioDuration(dur);
    setIsComputingUploadMeta(false);
  };

  const handleUploadAudioSubmit = async () => {
    if (!selectedPatient) return;
    if (!uploadAudioFileInput && !uploadAudioUrl.trim()) return;
    setIsUploadingAudio(true);
    try {
      let finalAudioUrl;
      let contentType = "audio/webm";
      let fileSize = null;
      let recordingDate = null;
      let audioName = null;

      if (uploadAudioFileInput) {
        // File upload — store in the audio bucket with _Uploaded suffix
        contentType = uploadAudioFileInput.type || "audio/webm";
        fileSize = uploadAudioFileInput.size;
        recordingDate = uploadAudioFileInput.lastModified
          ? new Date(uploadAudioFileInput.lastModified).toISOString()
          : new Date().toISOString();

        // Convert to webm-compatible blob (keep original type for non-webm)
        const blob = new Blob([await uploadAudioFileInput.arrayBuffer()], {
          type: contentType,
        });

        const result = await uploadAudioFile(
          blob,
          uploadAudioFileInput.name,
          bucketName,
          supabaseUrl,
          {
            patientId: selectedPatient.id,
            patientName: selectedPatient[prompts[0][0]],
          },
          selectedDate || null,
          "_Uploaded",
        );
        finalAudioUrl = result.url;
        audioName = uploadAudioFileInput.name;
      } else {
        // URL — save as-is without re-uploading
        finalAudioUrl = uploadAudioUrl.trim();
        recordingDate = new Date().toISOString();
        audioName =
          finalAudioUrl.split("/").pop()?.split("?")[0] || "Uploaded Audio";
      }

      // Get current more_audios and append
      const supabase = await getSupabase();
      const { data: currentData, error: fetchError } = await supabase
        .from(tableName)
        .select("more_audios")
        .eq("id", selectedPatient.id)
        .single();
      if (fetchError && fetchError.code !== "PGRST116") throw fetchError;

      const currentMoreAudios = currentData?.more_audios || [];
      const audioCount = currentMoreAudios.length + 1;
      const now = new Date().toISOString();

      const newAudio = {
        name: `Uploaded Audio ${audioCount}`,
        displayName: `Uploaded Audio ${audioCount}`,
        description: audioName,
        url: finalAudioUrl,
        duration: uploadAudioDuration || 0,
        uploaded_at: now,
        recording_date: recordingDate || now,
        source: "uploaded",
        type: "audio",
        patient_id: selectedPatient.id,
        file_size: fileSize,
        content_type: contentType,
      };

      const nextMoreAudios = [...currentMoreAudios, newAudio];
      const { error: updateError } = await supabase
        .from(tableName)
        .update({ more_audios: nextMoreAudios })
        .eq("id", selectedPatient.id);
      if (updateError) throw updateError;

      // Keep the in-memory patient in step with the row we just wrote, so the
      // transcribe modal's audio list shows this upload without a refresh.
      setSelectedPatient((prev) =>
        prev && prev.id === selectedPatient.id
          ? { ...prev, more_audios: nextMoreAudios }
          : prev,
      );

      toast({
        title: "Audio Uploaded",
        description: "Audio saved to patient record.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Reset and close
      setIsUploadAudioModalOpen(false);
      setUploadAudioFileInput(null);
      setUploadAudioUrl("");
      setUploadAudioDuration(null);
      if (uploadAudioFileRef.current) uploadAudioFileRef.current.value = "";
    } catch (err) {
      console.error("❌ Upload audio failed:", err);
      toast({
        title: "Upload Failed",
        description: err.message || "Failed to upload audio. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploadingAudio(false);
    }
  };

  const handlePhoneAudio = async () => {
    if (!selectedPatient) {
      toast({
        title: "No Patient Selected",
        description: "Please select a patient to use phone audio.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Check if password protection is enabled
    if (!(await hasValidPassword())) {
      sLog("🔒 Password required to use phone audio");
      setPendingAction(() => () => setIsPhoneAudioModalOpen(true));
      setIsPasswordModalOpen(true);
      return;
    }

    // Reset the modal closed flag when opening the modal
    phoneModalClosedRef.current = false;
    setIsPhoneAudioModalOpen(true);
  };

  const handleAutoSaveChange = async (newValue) => {
    sLog("📞 handleAutoSaveChange called with value:", newValue);
    sLog("📞 Current autoSaveSetting state:", autoSaveSetting);
    sLog("📞 Selected patient:", selectedPatient?.id);

    // Check credit limit before enabling auto-save
    if (newValue) {
      const creditLimitExceeded = await checkCreditLimitAndUpdateUI(
        toast,
        null,
        null,
        setAutoSaveSettingState,
      );
      if (creditLimitExceeded) {
        sLog("📞 Credit limit exceeded, not updating auto-save setting");
        return;
      }
    }

    // Always update the auto-save setting state
    setAutoSaveSettingState(newValue);
    sLog("📞 Auto-save setting updated to:", newValue);

    // If there's a selected patient, also save the setting for that patient
    if (selectedPatient && selectedPatient.id) {
      setAutoSaveSetting(selectedPatient.id, newValue);
      sLog("📞 Auto-save setting saved for patient:", selectedPatient.id);
    } else {
      sLog("📞 No patient selected, auto-save setting only updated locally");
    }
  };

  // Handle auto-reformat setting change
  const handleAutoReformatChange = async (newValue) => {
    try {
      sLog("📐 handleAutoReformatChange called with value:", newValue);
      setAutoReformatEnabled(newValue);

      const supabase = await getSupabase();
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user?.id) {
        sLog("📐 No user session found");
        toast({
          title: "Session Error",
          description: "Please log in to save settings",
          status: "warning",
          duration: 3000,
        });
        return;
      }

      // Fetch current user settings
      const { data: userData, error: fetchError } = await supabase
        .from("users")
        .select("settings")
        .eq("id", session.user.id)
        .single();

      if (fetchError) {
        console.error("📐 Error fetching user settings:", fetchError);
        toast({
          title: "Error",
          description: "Failed to fetch user settings",
          status: "error",
          duration: 3000,
        });
        return;
      }

      // Update the auto_reformat_enabled setting
      const updatedSettings = {
        ...(userData?.settings || {}),
        auto_reformat_enabled: newValue,
      };

      const { error: updateError } = await supabase
        .from("users")
        .update({ settings: updatedSettings })
        .eq("id", session.user.id);

      if (updateError) {
        console.error("📐 Error updating auto-reformat setting:", updateError);
        toast({
          title: "Error",
          description: "Failed to update auto-reformat setting",
          status: "error",
          duration: 3000,
        });
        return;
      }

      sLog("📐 Auto-reformat setting updated to:", newValue);
      toast({
        title: newValue ? "Auto-Reformat Enabled" : "Auto-Reformat Disabled",
        description: newValue
          ? "Documents will be automatically reformatted on first entry"
          : "Documents will not be automatically reformatted",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("📐 Error handling auto-reformat change:", error);
      toast({
        title: "Error",
        description: "Failed to update auto-reformat setting",
        status: "error",
        duration: 3000,
      });
    }
  };

  // Function to save phone audio file to more_audios field (without transcription)
  const savePhoneAudioToMoreAudios = async (audioFile, patient = null) => {
    sLog("📞 savePhoneAudioToMoreAudios called");
    sLog("📞 audioFile:", audioFile?.name);
    sLog("📞 patient parameter:", patient?.id);
    sLog("📞 patient parameter name:", patient?.[prompts[0][0]]);
    sLog("📞 selectedPatient state:", selectedPatient?.id);
    sLog("📞 selectedPatient state name:", selectedPatient?.[prompts[0][0]]);

    // Use the passed patient parameter or fall back to selectedPatient state
    const targetPatient = patient || selectedPatient;

    if (!targetPatient || !audioFile) {
      sLog("📞 Save audio: Missing patient or audio file");
      sLog("📞 Save audio: targetPatient:", !!targetPatient);
      sLog("📞 Save audio: audioFile:", !!audioFile);
      if (!targetPatient) {
        toast({
          title: "Save Failed",
          description: "No patient selected. Please select a patient first.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      }
      return;
    }

    try {
      sLog("📞 Save audio: Uploading audio file to storage");

      const supabase = await getSupabase();

      // Get current more_audios array first
      const { data: currentData, error: fetchMoreAudiosError } = await supabase
        .from(tableName)
        .select("more_audios")
        .eq("id", targetPatient.id)
        .single();

      if (fetchMoreAudiosError && fetchMoreAudiosError.code !== "PGRST116") {
        console.error(
          "📞 Save audio: Error fetching more_audios:",
          fetchMoreAudiosError,
        );
        throw fetchMoreAudiosError;
      }

      // Initialize more_audios as array if it doesn't exist
      const currentMoreAudios = currentData?.more_audios || [];

      // Generate filename for storage
      const sanitizedName =
        targetPatient[prompts[0][0]]?.replace(/[^a-zA-Z0-9]/g, "_") ||
        "patient";
      const timestamp = new Date().getTime();
      const fileExtension = audioFile.name.split(".").pop() || "webm";
      const filename = `phone_audio_${sanitizedName}_${timestamp}.${fileExtension}`;

      // Generate a user-friendly name for the audio entry
      const audioCount = currentMoreAudios.length + 1;
      const audioName = `Phone Audio ${audioCount}`;

      // Store the generated name for use in transcription
      const audioNameWithCount = { name: audioName, count: audioCount };

      sLog("📞 Save audio: Generated filename:", filename);

      // Upload audio file to Supabase storage
      const { data: storageData, error: storageError } = await supabase.storage
        .from(bucketName)
        .upload(filename, audioFile, {
          cacheControl: "3600",
          upsert: false,
          contentType: audioFile.type,
        });

      if (storageError) {
        console.error(
          "📞 Save audio: Error uploading to storage:",
          storageError,
        );
        throw storageError;
      }

      // Generate permanent URL
      const fullPath = storageData.path;
      // Encode each path segment so spaces / unicode / etc. become safe URL
      // components and don't break Supabase public URLs.
      const encodedFullPath = fullPath
        .split("/")
        .map((seg) => encodeURIComponent(seg))
        .join("/");
      const finalAudioUrl = `${supabaseUrl}/storage/v1/object/public/${bucketName}/${encodedFullPath}`;

      sLog("📞 Save audio: Audio uploaded successfully, URL:", finalAudioUrl);

      // Create new audio entry with permanent URL
      const newAudio = {
        name: audioName, // Use the generated user-friendly name
        description: "Phone audio recording",
        url: finalAudioUrl, // Use permanent storage URL
        duration: selectedAudioDuration || 0,
        source: "phone_audio",
        created_at: new Date().toISOString(),
      };

      sLog("📞 Save audio: New audio entry:", newAudio);
      const updatedMoreAudios = [...currentMoreAudios, newAudio];

      // Update the more_audios field
      const { error } = await supabase
        .from(tableName)
        .update({ more_audios: updatedMoreAudios })
        .eq("id", targetPatient.id);

      if (error) {
        console.error("📞 Save audio: Error saving audio file:", error);
        throw error;
      }

      sLog("📞 Save audio: Audio file saved successfully to more_audios");
      trackUserEvent('audio_record', {
        metadata: {
          source: 'phone_audio',
          size_bytes: audioFile?.size ?? null,
          patient_id: targetPatient?.id || null,
        },
      });
      toast({
        title: "Audio Saved",
        description: "Phone audio recording saved to patient record.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Return the generated audio name for use in transcription
      return audioNameWithCount;
    } catch (error) {
      console.error("📞 Save audio: Error saving audio file:", error);
      toast({
        title: "Save Failed",
        description: "Failed to save audio file. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  /** Same as manual “AI summary”: POST /api/generate-ai-recap/ + append is_ai_recap row */
  const triggerAiRecapForTranscript = async (
    patientId,
    transcriptId,
    lines,
  ) => {
    try {
      const text = Array.isArray(lines)
        ? lines
            .map((l) =>
              typeof l === "string" ? l : l?.transcript || l?.text || "",
            )
            .join(" ")
            .trim()
        : String(lines || "").trim();
      if (!text || !patientId || !transcriptId) return;
      const supabase = await getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const base = getBackendURL();
      const res = await withAIQueryTracking('recap', () => fetch(`${base}/api/generate-ai-recap/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Schema": supabaseSchema || "public",
        },
        body: JSON.stringify({
          transcript: text,
          patientId: String(patientId),
          sessionId: `transcript_${transcriptId}`,
          schema: supabaseSchema || "public",
          userJwtToken: session?.access_token || null,
        }),
      }));
      const json = await res.json();
      if (!res.ok || !json.recap) return;
      const { data: row } = await supabase
        .from(tableName)
        .select("transcripts")
        .eq("id", patientId)
        .single();
      const list = Array.isArray(row?.transcripts) ? row.transcripts : [];
      await supabase
        .from(tableName)
        .update({
          transcripts: [
            ...list,
            {
              id: crypto.randomUUID ? crypto.randomUUID() : String(Date.now()),
              is_ai_recap: true,
              linked_transcript_id: transcriptId,
              patient_id: String(patientId),
              summary: json.recap.summary || "",
              key_takeaways: json.recap.key_takeaways || [],
              action_items: json.recap.action_items || [],
              created_at: new Date().toISOString(),
            },
          ],
        })
        .eq("id", patientId);
    } catch (e) {
      console.warn("AI recap after transcribe:", e);
    }
  };

  // Function to automatically save transcription results to patient record
  const autoSaveTranscriptionResults = async (
    transcriptData,
    factsData,
    soapNotesData,
    creditUsage,
    audioStorageUrl = null,
    audioFile = null,
    audioDuration = null,
    skipCredits = false,
  ) => {
    sLog("📞 autoSaveTranscriptionResults called");
    sLog("📞 transcriptData:", transcriptData);
    sLog("📞 factsData:", factsData);
    sLog("📞 soapNotesData:", soapNotesData);
    sLog("📞 creditUsage:", creditUsage);

    // Use captured patient from ref for auto-save
    const patientToUse = capturedPatientRef.current || selectedPatient;
    sLog("📞 patientToUse (from ref):", patientToUse?.id);
    sLog("📞 patientToUse name:", patientToUse?.[prompts[0][0]]);
    sLog("📞 audioFile parameter:", audioFile?.name);
    sLog("📞 audioDuration parameter:", audioDuration);
    sLog("📞 selectedAudioFile (state):", selectedAudioFile?.name);
    sLog("📞 selectedAudioDuration (state):", selectedAudioDuration);

    if (!patientToUse || !transcriptData || !factsData || !soapNotesData) {
      sLog("📞 Auto-save: Missing data for saving transcription");
      sLog("📞 Auto-save: patientToUse:", !!patientToUse);
      sLog("📞 Auto-save: transcriptData:", !!transcriptData);
      sLog("📞 Auto-save: factsData:", !!factsData);
      sLog("📞 Auto-save: soapNotesData:", !!soapNotesData);

      if (!patientToUse) {
        sLog("📞 Auto-save: No patient captured, cannot save transcription");
        toast({
          title: "Auto-save Failed",
          description: "No patient selected. Please select a patient first.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      }
      return undefined;
    }

    try {
      sLog(
        "%c💳 CORTI CREDITS - AUTO-SAVING TRANSCRIPTION RESULTS",
        "color: gold; font-weight: bold; font-size: 14px;",
      );
      sLog(
        "📊 Auto-saving transcription for patient:",
        patientToUse?.[prompts[0][0]],
      );
      sLog("📊 Patient ID:", patientToUse?.id);

      const supabase = await getSupabase();

      // First, get existing transcriptions
      const { data: existingData, error: fetchError } = await supabase
        .from(tableName)
        .select("transcripts")
        .eq("id", patientToUse.id)
        .single();

      if (fetchError && fetchError.code !== "PGRST116") {
        // PGRST116 is "not found"
        throw fetchError;
      }

      // Create new transcription data
      const resolvedAudioFileName =
        audioFile?.name ||
        selectedAudioFile?.name ||
        (selectedAudioSource === "url" && selectedAudioUrl
          ? selectedPatient?.more_audios?.find(
              (a) => a.url === selectedAudioUrl,
            )?.name
          : "") ||
        "";
      const newTranscription = {
        id: Date.now().toString(), // Generate unique ID
        patient_id: patientToUse.id?.toString() || "",
        name: (patientToUse[prompts[0][0]] || "").toString(),
        transcript: Array.isArray(transcriptData)
          ? transcriptData
          : [transcriptData],
        facts: Array.isArray(factsData) ? factsData : [factsData],
        soap_notes: soapNotesData || {},
        audio_duration: audioDuration || selectedAudioDuration || 0,
        audio_source: selectedAudioSource || "",
        audio_file_name: resolvedAudioFileName,
        audio_url:
          selectedAudioSource === "url" && selectedAudioUrl
            ? selectedAudioUrl
            : "",
        estimated_credit_usage: creditUsage || null,
        created_at: new Date().toISOString(),
      };

      // Prepare the updated transcripts array
      let updatedTranscripts = [];
      if (existingData && existingData.transcripts) {
        // If existing data is an array, use it; otherwise, convert single object to array
        if (Array.isArray(existingData.transcripts)) {
          updatedTranscripts = [...existingData.transcripts, newTranscription];
        } else {
          // Convert single transcription object to array and add new one
          updatedTranscripts = [existingData.transcripts, newTranscription];
        }
      } else {
        // No existing transcriptions, start with new one
        updatedTranscripts = [newTranscription];
      }

      // Log before update
      sLog("📞 Auto-save: Updating patient record with id:", patientToUse.id);
      sLog("📞 Auto-save: Updated transcripts array:", updatedTranscripts);

      // Update transcripts only (audio file is already saved by savePhoneAudioToMoreAudios)
      const updateData = { transcripts: updatedTranscripts };

      const { data, error } = await supabase
        .from(tableName)
        .update(updateData)
        .eq("id", patientToUse.id);

      if (error) {
        console.error(
          "📞 Auto-save: Error saving transcription and audio:",
          error,
        );
        throw error;
      }

      sLog(
        "%c💳 CORTI CREDITS - TRANSCRIPTION RESULTS AUTO-SAVED",
        "color: gold; font-weight: bold; font-size: 14px;",
      );
      sLog("📊 Transcription results automatically saved to database");
      sLog("📊 Patient record updated with new transcription");

      // Add recorded credits to the user's total (skip when caller already credited)
      if (creditUsage && !skipCredits) {
        sLog("💳 Adding recorded credits to user total:", creditUsage);
        addRecordedCredits(creditUsage).catch((error) => {
          console.error("💳 Error adding recorded credits:", error);
        });
      }

      toast({
        title: "Transcription Saved!",
        description:
          "Transcription results have been automatically saved to the patient record!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      window.dispatchEvent(
        new CustomEvent("transcriptSaved", {
          detail: { patientId: patientToUse.id },
        }),
      );
      return newTranscription.id;
    } catch (error) {
      console.error(
        "📞 Auto-save: Error saving transcription to Supabase:",
        error,
      );
      sLog(
        "%c💳 CORTI CREDITS - AUTO-SAVE TRANSCRIPTION FAILED",
        "color: gold; font-weight: bold; font-size: 14px;",
      );
      sLog("📊 Failed to auto-save transcription results:", error.message);

      toast({
        title: "Auto-Save Failed",
        description:
          "Failed to automatically save transcription results to database.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return undefined;
    }
  };

  // Function to handle phone audio transcription with optional direct file parameter and duration
  const handlePhoneAudioTranscription = async (
    directAudioFile = null,
    directAudioDuration = null,
  ) => {
    sLog("📞 handlePhoneAudioTranscription called");
    sLog("📞 directAudioFile:", directAudioFile?.name);
    sLog("📞 selectedAudioSource:", selectedAudioSource);
    sLog("📞 isLoading:", isLoading);
    sLog("📞 selectedAudioFile:", selectedAudioFile?.name);

    // Use direct file if provided, otherwise use state
    const audioFileToUse = directAudioFile || selectedAudioFile;
    const audioSourceToUse = directAudioFile ? "file" : selectedAudioSource;
    const audioDurationToUse = directAudioDuration || selectedAudioDuration;

    sLog("📞 audioFileToUse:", audioFileToUse?.name);
    sLog("📞 audioSourceToUse:", audioSourceToUse);

    if (!audioSourceToUse || isLoading) {
      sLog("📞 Early return - missing audio source or already loading");
      return;
    }

    setIsLoading(true);
    startTimer();
    setEstimatedProgress(0);
    setCurrentMilestone("Starting transcription...");
    setMilestoneProgress(0);

    try {
      sLog("📞 Phone Audio: Starting transcription using REST API...");
      sLog(
        "%c💳 CORTI CREDITS - PHONE AUDIO TRANSCRIPTION STARTED",
        "color: gold; font-weight: bold; font-size: 14px;",
      );
      sLog("📞 Phone Audio: Audio source:", audioSourceToUse);
      sLog("📞 Phone Audio: Audio duration:", audioDurationToUse);
      sLog("📞 Phone Audio: Patient:", selectedPatient?.[prompts[0][0]]);

      const hostname = window.location.hostname;
      const baseUrl =
        hostname === "localhost"
          ? BASE_URLS.local.backend
          : BASE_URLS.render.backend;
      const formData = new FormData();

      // Add transcription model selection
      formData.append("transcription_model", selectedTranscriptionModel);
      console.log("📊 Using transcription model:", selectedTranscriptionModel);

      // Log audio file details for debugging
      sLog("📞 Phone Audio: Audio file details:", {
        name: audioFileToUse?.name,
        size: audioFileToUse?.size,
        type: audioFileToUse?.type,
        lastModified: audioFileToUse?.lastModified,
      });

      if (audioSourceToUse === "url") {
        sLog("📞 Phone Audio: Sending audio URL to backend:", selectedAudioUrl);
        const urlBlob = new Blob([selectedAudioUrl], { type: "text/plain" });
        formData.append("audio_file", urlBlob, "audio_url.txt");
      } else {
        // Send actual file
        sLog(
          "📞 Phone Audio: Sending audio file to backend:",
          audioFileToUse.name,
        );
        sLog("📞 Phone Audio: File details:", {
          name: audioFileToUse.name,
          size: audioFileToUse.size,
          type: audioFileToUse.type,
        });
        formData.append("audio_file", audioFileToUse);
      }

      sLog(
        "📞 Phone Audio: Sending request to:",
        `${baseUrl}/corti/transcribe-recorded/`,
      );

      const res = await fetch(`${baseUrl}/corti/transcribe-recorded/`, {
        method: "POST",
        body: formData,
        credentials: "include",
        headers: {
          "X-CSRFToken":
            document.cookie.split("csrftoken=")[1]?.split(";")[0] || "",
        },
      });

      // Phone Audio Modal - Handle response
      sLog("📞 Phone Audio: Response status:", res.status);

      if (!res.ok) {
        console.error("📞 Phone Audio: Upload failed with status:", res.status);
        let errorMessage = `Failed to upload recording: ${res.status} ${res.statusText}`;

        try {
          const errorText = await res.text();
          console.error("📞 Phone Audio: Error response:", errorText);
          // Try to parse as JSON for more detailed error
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.error || errorMessage;
          } catch (parseError) {
            // If not JSON, use the text as is
            errorMessage = errorText || errorMessage;
          }
        } catch (textError) {
          console.error(
            "📞 Phone Audio: Could not read error response:",
            textError,
          );
        }

        setFacts({ error: errorMessage });
        setTranscript(null);
        setSoapNotes(null);
        return;
      }

      const data = await res.json();
      sLog("📞 Phone Audio: Received data:", data);
      sLog("📝 SOAP notes in response:", data.soap_notes);
      sLog("💳 Credit usage in response:", data.credit_usage);
      sLog("📝 SOAP notes type:", typeof data.soap_notes);
      sLog("📝 SOAP notes transcript_only:", data.soap_notes?.transcript_only);
      sLog("📝 SOAP notes facts_only:", data.soap_notes?.facts_only);
      sLog(
        "📝 SOAP notes keys:",
        data.soap_notes ? Object.keys(data.soap_notes) : "null",
      );

      if (res.ok) {
        // Set progress to 100% when complete
        setEstimatedProgress(100);
        setCurrentMilestone("Completed");

        sLog(
          "%c💳 CORTI CREDITS - RECORDED TRANSCRIPTION COMPLETED",
          "color: gold; font-weight: bold; font-size: 14px;",
        );
        sLog("📊 Transcription successful");
        sLog(
          "📊 Transcript lines:",
          Array.isArray(data.transcript) ? data.transcript.length : 0,
        );
        sLog(
          "📊 Facts extracted:",
          Array.isArray(data.facts) ? data.facts.length : 0,
        );
        sLog("📊 SOAP notes generated:", !!data.soap_notes);
        sLog("📊 Credit usage:", data.credit_usage || "Not available");
        sLog(
          "📊 Total processing time:",
          `${Math.floor(elapsedTime / 60)}:${(elapsedTime % 60).toString().padStart(2, "0")}`,
        );

        // Show toast with model used for transcription
        const modelUsed =
          data.processing_method === "vosk_local" || data.processing_method === "whisper_local" ? "VOSK" : "Corti AI";
        toast({
          title: "Transcription Complete",
          description: `Audio transcribed using ${modelUsed}`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        // Store credit usage for display
        if (data.credit_usage) {
          setTranscriptionCreditUsage(data.credit_usage);

          // Log transcription to corti_logs table
          try {
            await cortiLogger.logTranscription({
              patientId: selectedPatient?.id,
              patientName: selectedPatient?.[prompts[0][0]],
              transcriptionType: "recorded",
              audioFileName:
                (selectedAudioSource === "url"
                  ? selectedPatient.more_audios?.find(
                      (a) => a.url === selectedAudioUrl,
                    )?.name
                  : selectedAudioFile?.name) || "",
              audioDurationSeconds: selectedAudioDuration || 0,
              audioSource: selectedAudioSource || "",
              audioUrl: selectedAudioSource === "url" ? selectedAudioUrl : null,
              creditUsage: data.credit_usage,
              transcriptLength: Array.isArray(data.transcript)
                ? data.transcript.join(" ").length
                : 0,
              factsExtracted: Array.isArray(data.facts) ? data.facts.length : 0,
              soapNotesGenerated: !!data.soap_notes,
              processingTimeSeconds: Math.floor(elapsedTime),
              status: "completed",
              metadata: {
                source: "schedules_transcribe_modal",
              },
            });
            sLog("📊 CortiLogger: Successfully logged transcription");
          } catch (error) {
            console.error(
              "📊 CortiLogger: Error logging transcription:",
              error,
            );
          }
        }

        setFacts(Array.isArray(data.facts) ? data.facts : []);
        setTranscript(Array.isArray(data.transcript) ? data.transcript : []);
        setSoapNotes(cleanSoapNotes(data.soap_notes));
        sLog("✅ Set SOAP notes:", data.soap_notes);
        sLog("✅ SOAP notes state set to:", data.soap_notes);

        // Auto-save transcription results if auto-save is enabled
        // Use the captured auto-save setting from when recording started
        const capturedAutoSaveSetting = directAudioFile
          ? true
          : autoSaveSetting; // If direct file, auto-save was enabled

        // Get the captured patient information from the refs for persistence
        const capturedPatient = capturedPatientRef.current || selectedPatient;

        if (capturedAutoSaveSetting) {
          sLog(
            "📞 Auto-save enabled, automatically saving transcription results...",
          );
          sLog(
            "📞 Auto-save setting value in transcription completion:",
            capturedAutoSaveSetting,
          );
          sLog("📞 Captured patient for auto-save:", capturedPatient?.id);
          sLog("📞 Captured patient name:", capturedPatient?.[prompts[0][0]]);

          // Check if we have a captured patient before attempting to save
          if (!capturedPatient) {
            sLog(
              "📞 Auto-save: No patient captured, cannot auto-save transcription",
            );
            toast({
              title: "Auto-save Failed",
              description:
                "No patient selected. Please select a patient first.",
              status: "warning",
              duration: 5000,
              isClosable: true,
            });
          } else {
            // Use setTimeout to ensure state updates are processed first
            setTimeout(async () => {
              sLog("📞 Calling autoSaveTranscriptionResults after timeout...");
              sLog("📞 Using audio file:", audioFileToUse?.name);
              sLog("📞 Using audio duration:", audioDurationToUse);
              const tid = await autoSaveTranscriptionResults(
                Array.isArray(data.transcript) ? data.transcript : [],
                Array.isArray(data.facts) ? data.facts : [],
                cleanSoapNotes(data.soap_notes),
                data.credit_usage,
                data.audio_storage_url, // Pass the permanent audio storage URL
                audioFileToUse, // Pass the audio file
                audioDurationToUse, // Pass the audio duration
              );
              if (tid)
                await triggerAiRecapForTranscript(
                  capturedPatient.id,
                  tid,
                  data.transcript,
                );
            }, 100);
          }
        } else {
          sLog(
            "📞 Auto-save disabled, transcription results will be saved manually",
          );
          sLog(
            "📞 Auto-save setting value in transcription completion:",
            capturedAutoSaveSetting,
          );
        }

        // Auto-save is already handled by autoSaveTranscriptionResults function
        // No need for duplicate auto-save logic here
      } else {
        sLog(
          "%c💳 CORTI CREDITS - RECORDED TRANSCRIPTION FAILED",
          "color: gold; font-weight: bold; font-size: 14px;",
        );
        sLog("📊 Transcription failed with error:", data.error);
        sLog("📊 Error type:", data.error_type);
        sLog(
          "📊 Total processing time:",
          `${Math.floor(elapsedTime / 60)}:${(elapsedTime % 60).toString().padStart(2, "0")}`,
        );

        // Check for specific error types
        if (data.error_type === "no_speech_detected") {
          setFacts({ error: data.error });
          setTranscript(null);
          setSoapNotes(null);
        } else {
          setFacts({
            error: data.error || "Transcription failed. Please try again.",
          });
          setTranscript(null);
          setSoapNotes(null);
        }
      }
    } catch (err) {
      console.error("Transcription error:", err);
      sLog(
        "%c💳 CORTI CREDITS - RECORDED TRANSCRIPTION ERROR",
        "color: gold; font-weight: bold; font-size: 14px;",
      );
      sLog("📊 Network/API error:", err.message);
      sLog(
        "📊 Total processing time:",
        `${Math.floor(elapsedTime / 60)}:${(elapsedTime % 60).toString().padStart(2, "0")}`,
      );

      setFacts({ error: "Transcription failed. Please try again." });
      setTranscript(null);
      setSoapNotes(null);
      // Reset progress on error
      setEstimatedProgress(0);
      setCurrentMilestone("");
    } finally {
      setIsLoading(false);
      stopTimer();
      setLastElapsedTime(elapsedTime);
    }
  };

  const startPhoneRecording = async () => {
    if (!phoneMediaRecorderRef.current) {
      sLog("📞 Phone recorder not ready yet, reinitializing...");

      // Reinitialize the recorder
      await initializePhoneRecorder(
        0,
        toast,
        setPhoneMediaRecorder,
        phoneMediaRecorderRef,
        phoneStreamRef,
        phoneChunksRef,
        setPhoneRecordingState,
        setPhoneAudioURL,
        setSelectedAudioFile,
        setSelectedAudioSource,
        autoSaveSetting,
        handlePhoneAudioTranscription,
      );

      // Wait a moment for initialization to complete
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Check if initialization was successful
      if (!phoneMediaRecorderRef.current) {
        sLog("📞 Phone recorder initialization failed");
        toast({
          title: "Recorder Not Ready",
          description:
            "Audio recorder initialization failed. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      sLog("📞 Phone recorder reinitialized successfully");
    }

    // Check if MediaRecorder is in a valid state
    if (phoneMediaRecorderRef.current.state !== "inactive") {
      console.error(
        "📞 MediaRecorder is not in inactive state:",
        phoneMediaRecorderRef.current.state,
      );
      toast({
        title: "Recorder Busy",
        description:
          "Audio recorder is currently busy. Please wait for it to finish or refresh the page.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Check if the stream is still active
    if (!phoneStreamRef.current || !phoneStreamRef.current.active) {
      console.error("📞 MediaRecorder stream is not active, reinitializing...");
      sLog("📞 Phone Audio: Stream ref exists:", !!phoneStreamRef.current);
      sLog("📞 Phone Audio: Stream active:", phoneStreamRef.current?.active);
      toast({
        title: "Reinitializing Recorder",
        description: "Audio stream was lost. Reinitializing...",
        status: "info",
        duration: 2000,
        isClosable: true,
      });

      // Reinitialize the recorder
      await initializePhoneRecorder(
        0,
        toast,
        setPhoneMediaRecorder,
        phoneMediaRecorderRef,
        phoneStreamRef,
        phoneChunksRef,
        setPhoneRecordingState,
        setPhoneAudioURL,
        setSelectedAudioFile,
        setSelectedAudioSource,
        autoSaveSetting,
        handlePhoneAudioTranscription,
      );

      // Wait a moment and try again
      setTimeout(() => {
        if (
          phoneMediaRecorderRef.current &&
          phoneMediaRecorderRef.current.state === "inactive"
        ) {
          startPhoneRecording();
        }
      }, 1000);
      return;
    }

    try {
      sLog("📞 Starting phone recording...");
      sLog("📞 MediaRecorder state:", phoneMediaRecorderRef.current.state);
      sLog(
        "📞 MediaRecorder mimeType:",
        phoneMediaRecorderRef.current.mimeType,
      );
      sLog("📞 Stream active:", phoneStreamRef.current?.active);
      sLog("📞 Phone Audio: Recorder ready for recording");

      // Capture current state values for use in the onstop callback
      const currentAutoSaveSetting = autoSaveSetting;
      const currentSelectedPatient = selectedPatient;

      sLog(
        "📞 Captured auto-save setting for recording:",
        currentAutoSaveSetting,
      );
      sLog(
        "📞 Captured selected patient for recording:",
        currentSelectedPatient?.id,
      );

      // Store the captured values in refs for persistent access
      capturedAutoSaveSettingRef.current = currentAutoSaveSetting;
      capturedPatientRef.current = currentSelectedPatient;

      sLog(
        "📞 Stored captured patient in ref:",
        capturedPatientRef.current?.id,
      );
      sLog(
        "📞 Stored captured auto-save setting in ref:",
        capturedAutoSaveSettingRef.current,
      );

      // If "Include system/meeting audio" is checked, use aggregate stream (mic + tab/screen audio)
      if (phoneIncludeSystemAudio) {
        try {
          const { stream: aggStream, cleanup: aggCleanup } =
            await getAggregateAudioStream({
              audioConstraints: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
                sampleRate: 16000,
              },
              includeSystemAudio: true,
            });
          phoneAggregateCleanupRef.current = aggCleanup;

          const supportedTypes = [
            "audio/webm; codecs=opus",
            "audio/webm",
            "audio/mp4",
            "audio/ogg; codecs=opus",
            "audio/ogg",
            "audio/wav",
          ];
          let bestMimeType = null;
          for (const type of supportedTypes) {
            if (MediaRecorder.isTypeSupported(type)) {
              bestMimeType = type;
              break;
            }
          }
          if (!bestMimeType) bestMimeType = "audio/webm; codecs=opus";

          let aggRecorder;
          try {
            aggRecorder = new MediaRecorder(aggStream, {
              mimeType: bestMimeType,
              audioBitsPerSecond: 16000,
            });
          } catch (e) {
            aggRecorder = new MediaRecorder(aggStream);
          }
          phoneStreamRef.current = aggStream;
          phoneMediaRecorderRef.current = aggRecorder;
          setPhoneMediaRecorder(aggRecorder);

          aggRecorder.ondataavailable = (e) => {
            if (e.data.size > 0) phoneChunksRef.current.push(e.data);
          };

          aggRecorder.onstop = async () => {
            if (phoneAggregateCleanupRef.current) {
              try {
                phoneAggregateCleanupRef.current();
              } catch (e) {
                console.warn("📞 Aggregate cleanup error", e);
              }
              phoneAggregateCleanupRef.current = null;
            }
            try {
              if (phoneChunksRef.current.length === 0) {
                setPhoneRecordingState("idle");
                toast({
                  title: "Recording Error",
                  description:
                    "No audio data was captured. Please try recording again.",
                  status: "error",
                  duration: 5000,
                  isClosable: true,
                });
                return;
              }
              const audioBlob = new Blob(phoneChunksRef.current, {
                type: bestMimeType,
              });
              const audioUrl = URL.createObjectURL(audioBlob);
              setPhoneAudioURL(audioUrl);
              setPhoneRecordingState("stopped");
              const timestamp = new Date()
                .toISOString()
                .replace(/[:.]/g, "-")
                .slice(0, -5);
              const audioFile = new File(
                [audioBlob],
                `phone-recording-${timestamp}.webm`,
                { type: bestMimeType },
              );
              const recordingDuration =
                await resolvePhoneRecordingDuration(audioUrl);
              setSelectedAudioSource("file");
              setSelectedAudioDuration(recordingDuration);
              phoneAudioDurationRef.current = recordingDuration;
              phoneStreamRef.current = null;
              phoneMediaRecorderRef.current = null;
              const currentAutoSaveSetting = capturedAutoSaveSettingRef.current;
              const currentSelectedPatient = capturedPatientRef.current;
              if (phoneChunksRef.current && !phoneModalClosedRef.current) {
                setTimeout(async () => {
                  const audioNameResult = await savePhoneAudioToMoreAudios(
                    audioFile,
                    currentSelectedPatient,
                  );
                  if (audioNameResult) {
                    const fileExtension =
                      audioFile.name.split(".").pop() || "webm";
                    const audioFileWithNiceName = new File(
                      [audioBlob],
                      `${audioNameResult.name}.${fileExtension}`,
                      { type: bestMimeType },
                    );
                    setSelectedAudioFile(audioFileWithNiceName);
                    if (currentAutoSaveSetting && currentSelectedPatient) {
                      setTimeout(() => {
                        handlePhoneAudioTranscription(
                          audioFileWithNiceName,
                          recordingDuration,
                        );
                      }, 200);
                    }
                  }
                }, 100);
              }
            } catch (err) {
              console.error("📞 Error in recorder.onstop:", err);
              setPhoneRecordingState("idle");
              toast({
                title: "Recording Error",
                description:
                  "Failed to process the recorded audio. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
              });
            }
          };

          aggRecorder.onerror = (event) => {
            console.error("📞 MediaRecorder error:", event.error);
            setPhoneRecordingState("idle");
            toast({
              title: "Recording Error",
              description: `MediaRecorder error: ${event.error.message}`,
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          };

          phoneChunksRef.current = [];
          setPhoneRecordingState("recording");
          setPhoneRecordingStartTime(Date.now());
          phoneRecordingStartRef.current = Date.now();
          await new Promise((resolve) => setTimeout(resolve, 50));
          aggRecorder.start(2000);
          sLog("📞 Recording started with system/meeting audio");
          return;
        } catch (aggErr) {
          const isTabAudioRequired =
            aggErr?.code === "TAB_AUDIO_REQUIRED" ||
            (aggErr?.message &&
              aggErr.message.includes("TAB_AUDIO_REQUIRED")) ||
            aggErr?.name === "InvalidStateError" ||
            (aggErr?.message && aggErr.message.includes("no audio track"));
          if (aggErr.name === "NotAllowedError") {
            toast({
              title: "Tab/screen share cancelled",
              description: "Recording with microphone only.",
              status: "info",
              duration: 4000,
              isClosable: true,
            });
          } else if (isTabAudioRequired) {
            toast({
              title: "No tab audio shared",
              description:
                'The share dialog requires "Also share tab audio" to be checked so we can capture meeting audio. Recording with microphone only. Next time: turn on "Also share tab audio" in the share dialog.',
              status: "warning",
              duration: 8000,
              isClosable: true,
            });
          } else {
            throw aggErr;
          }
        }
      }

      // Clear previous chunks
      phoneChunksRef.current = [];

      // Update state before starting to prevent double-clicks
      setPhoneRecordingState("recording");
      setPhoneRecordingStartTime(Date.now());
      phoneRecordingStartRef.current = Date.now();

      // Add a small delay to ensure state updates are processed
      await new Promise((resolve) => setTimeout(resolve, 50));

      // Try to start recording with error handling
      phoneMediaRecorderRef.current.start(2000); // 2 second chunks
      sLog("📞 Recording started successfully");

      // Store the captured values in the MediaRecorder object for use in onstop (backup)
      phoneMediaRecorderRef.current._capturedAutoSaveSetting =
        currentAutoSaveSetting;
      phoneMediaRecorderRef.current._capturedSelectedPatient =
        currentSelectedPatient;
    } catch (error) {
      console.error("📞 Error starting recording:", error);
      console.error("📞 Error details:", {
        name: error.name,
        message: error.message,
        state: phoneMediaRecorderRef.current?.state,
        streamActive: phoneMediaRecorderRef.current?.stream?.active,
      });

      // Handle specific MediaRecorder errors
      if (error.name === "NotSupportedError") {
        sLog("📞 NotSupportedError detected, attempting to reinitialize...");
        toast({
          title: "Reinitializing Recorder",
          description:
            "Audio format issue detected. Reinitializing recorder...",
          status: "info",
          duration: 3000,
          isClosable: true,
        });

        // Reset state and reinitialize
        setPhoneRecordingState("idle");
        await initializePhoneRecorder(
          0,
          toast,
          setPhoneMediaRecorder,
          phoneMediaRecorderRef,
          phoneStreamRef,
          phoneChunksRef,
          setPhoneRecordingState,
          setPhoneAudioURL,
          setSelectedAudioFile,
          setSelectedAudioSource,
          autoSaveSetting,
          handlePhoneAudioTranscription,
        );

        // Try again after reinitialization
        setTimeout(() => {
          if (
            phoneMediaRecorderRef.current &&
            phoneMediaRecorderRef.current.state === "inactive"
          ) {
            startPhoneRecording();
          }
        }, 1000);
      } else if (error.name === "InvalidStateError") {
        toast({
          title: "Recorder Not Ready",
          description:
            "The audio recorder is not in the correct state. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setPhoneRecordingState("idle");
      } else {
        toast({
          title: "Recording Error",
          description: `Failed to start recording: ${error.message}. Please try again.`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setPhoneRecordingState("idle");
      }
    }
  };

  const stopPhoneRecording = () => {
    if (
      phoneMediaRecorderRef.current &&
      phoneMediaRecorderRef.current.state === "recording"
    ) {
      sLog("📞 Stopping phone recording...");
      phoneMediaRecorderRef.current.stop();
    }
  };

  const pausePhoneRecording = () => {
    if (
      phoneMediaRecorderRef.current &&
      phoneMediaRecorderRef.current.state === "recording"
    ) {
      sLog("📞 Pausing phone recording...");
      phoneMediaRecorderRef.current.pause();
      setPhoneRecordingState("paused");
    }
  };

  const resumePhoneRecording = () => {
    if (
      phoneMediaRecorderRef.current &&
      phoneMediaRecorderRef.current.state === "paused"
    ) {
      sLog("📞 Resuming phone recording...");
      phoneMediaRecorderRef.current.resume();
      setPhoneRecordingState("recording");
    }
  };

  // Initialize auto-save setting when patient is selected
  useEffect(() => {
    if (selectedPatient && selectedPatient.id) {
      const savedAutoSaveSetting = getAutoSaveSetting(selectedPatient.id);
      if (savedAutoSaveSetting !== null) {
        setAutoSaveSettingState(savedAutoSaveSetting);
      }
    }
  }, [selectedPatient]);

  // Initialize phone audio media recorder function
  const initializePhoneRecorder = async (
    retryCount = 0,
    toast,
    setPhoneMediaRecorder,
    phoneMediaRecorderRef,
    phoneStreamRef,
    phoneChunksRef,
    setPhoneRecordingState,
    setPhoneAudioURL,
    setSelectedAudioFile,
    setSelectedAudioSource,
    autoSaveSetting,
    handlePhoneAudioTranscription,
  ) => {
    const maxRetries = 2;

    if (retryCount > maxRetries) {
      console.error("📞 Max retries reached for phone recorder initialization");
      toast({
        title: "Initialization Failed",
        description:
          "Unable to initialize audio recorder after multiple attempts. Please refresh the page and try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    try {
      sLog("📞 Initializing phone recorder...");

      // Request audio stream with specific constraints
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
        },
      });

      sLog("📞 Audio stream obtained successfully");

      // Find the best supported MIME type
      let bestMimeType = null;
      const supportedTypes = [
        "audio/webm; codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg; codecs=opus",
        "audio/ogg",
        "audio/wav",
      ];

      for (const type of supportedTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          bestMimeType = type;
          sLog("📞 Using MIME type:", bestMimeType);
          break;
        }
      }

      if (!bestMimeType) {
        console.warn("📞 No supported MIME type found, using fallback");
        bestMimeType = "audio/webm; codecs=opus";
      }

      // Create MediaRecorder with error handling
      let recorder;
      try {
        recorder = new MediaRecorder(stream, {
          mimeType: bestMimeType,
          audioBitsPerSecond: 16000,
        });
        sLog("📞 MediaRecorder created successfully");
      } catch (recorderError) {
        console.error("📞 Error creating MediaRecorder:", recorderError);
        // Try without specifying MIME type
        recorder = new MediaRecorder(stream);
        sLog("📞 MediaRecorder created with default settings");
      }

      // Store the stream reference for later cleanup
      phoneStreamRef.current = stream;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          sLog("📞 Phone Audio: Data available -", e.data.size, "bytes");
          phoneChunksRef.current.push(e.data);
        }
      };

      recorder.onstop = async () => {
        try {
          sLog("📞 Phone recorder stopped, creating audio file...");
          sLog("📞 Audio chunks count:", phoneChunksRef.current.length);
          sLog(
            "📞 Total audio data size:",
            phoneChunksRef.current.reduce(
              (total, chunk) => total + chunk.size,
              0,
            ),
          );

          if (phoneChunksRef.current.length === 0) {
            console.error("📞 No audio chunks available");
            setPhoneRecordingState("idle");
            toast({
              title: "Recording Error",
              description:
                "No audio data was captured. Please try recording again.",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
            return;
          }

          const audioBlob = new Blob(phoneChunksRef.current, {
            type: bestMimeType,
          });
          sLog("📞 Audio blob created:", {
            size: audioBlob.size,
            type: audioBlob.type,
          });

          if (audioBlob.size === 0) {
            console.error("📞 Audio blob is empty");
            setPhoneRecordingState("idle");
            toast({
              title: "Recording Error",
              description:
                "The recorded audio is empty. Please try recording again.",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
            return;
          }

          const audioUrl = URL.createObjectURL(audioBlob);
          setPhoneAudioURL(audioUrl);
          setPhoneRecordingState("stopped");

          // Create a file from the blob for transcription
          const timestamp = new Date()
            .toISOString()
            .replace(/[:.]/g, "-")
            .slice(0, -5);
          const audioFile = new File(
            [audioBlob],
            `phone-recording-${timestamp}.webm`,
            { type: bestMimeType },
          );

          // Measure the recorded audio (falls back to the wall-clock timer)
          const recordingDuration =
            await resolvePhoneRecordingDuration(audioUrl);

          sLog("📞 Audio file created:", {
            name: audioFile.name,
            size: audioFile.size,
            type: audioFile.type,
            duration: recordingDuration,
          });

          setSelectedAudioSource("file");
          setSelectedAudioDuration(recordingDuration);

          // Store the duration in a ref for persistence
          phoneAudioDurationRef.current = recordingDuration;

          // Stop all tracks and clean up stream
          sLog("📞 Phone Audio: Stopping stream tracks and cleaning up");
          stream.getTracks().forEach((track) => track.stop());
          phoneStreamRef.current = null;

          // Clear MediaRecorder reference to force reinitialization
          phoneMediaRecorderRef.current = null;
          sLog(
            "📞 Phone Audio: MediaRecorder reference cleared, will reinitialize on next recording",
          );

          sLog("📞 Phone recording completed successfully");

          // Auto-start transcription when Stop and Save is pressed
          // Use the captured values from when recording started (from refs for persistence)
          const currentAutoSaveSetting = capturedAutoSaveSettingRef.current;
          const currentSelectedPatient = capturedPatientRef.current;

          sLog("📞 Auto-save setting check - value:", currentAutoSaveSetting);
          sLog("📞 Selected patient check:", currentSelectedPatient?.id);
          sLog(
            "📞 Selected patient name:",
            currentSelectedPatient?.[prompts[0][0]],
          );

          // Always save the audio file to more_audios first (only if modal is still open)
          if (phoneChunksRef.current && !phoneModalClosedRef.current) {
            sLog("📞 Saving audio file to more_audios");
            setTimeout(async () => {
              const audioNameResult = await savePhoneAudioToMoreAudios(
                audioFile,
                currentSelectedPatient,
              );
              sLog("📞 Audio name result:", audioNameResult);

              // Store the audio name for use in transcription
              if (audioNameResult) {
                // Create a modified audio file with the nice name and preserve the extension
                const fileExtension = audioFile.name.split(".").pop() || "webm";
                const audioFileWithNiceName = new File(
                  [audioBlob],
                  `${audioNameResult.name}.${fileExtension}`,
                  { type: bestMimeType },
                );

                // Set the selected audio file to the nice name for UI display
                setSelectedAudioFile(audioFileWithNiceName);

                if (currentAutoSaveSetting) {
                  sLog(
                    "📞 Auto-save enabled, starting transcription automatically...",
                  );
                  sLog("📞 Auto-save setting value:", currentAutoSaveSetting);
                  sLog(
                    "📞 Created audio file with nice name:",
                    audioFileWithNiceName.name,
                  );
                  sLog("📞 Audio source will be set to: file");

                  // Check if we have a selected patient before starting transcription
                  if (!currentSelectedPatient) {
                    sLog(
                      "📞 Auto-save: No patient selected, cannot start automatic transcription",
                    );
                    toast({
                      title: "Auto-save Failed",
                      description:
                        "No patient selected. Please select a patient first.",
                      status: "warning",
                      duration: 5000,
                      isClosable: true,
                    });
                  } else {
                    // Use setTimeout to ensure state updates are processed first
                    setTimeout(() => {
                      sLog(
                        "📞 Calling handlePhoneAudioTranscription after timeout...",
                      );
                      sLog(
                        "📞 Passing audio file with nice name:",
                        audioFileWithNiceName.name,
                      );
                      sLog("📞 Passing audio duration:", recordingDuration);
                      handlePhoneAudioTranscription(
                        audioFileWithNiceName,
                        recordingDuration,
                      );
                    }, 200); // Increased delay to ensure audio file is saved first
                  }
                } else {
                  sLog("📞 Auto-save disabled, transcription will be manual");
                  sLog("📞 Auto-save setting value:", currentAutoSaveSetting);
                }
              }
            }, 100);
          }
        } catch (error) {
          console.error("📞 Error in recorder.onstop:", error);
          setPhoneRecordingState("idle");
          toast({
            title: "Recording Error",
            description:
              "Failed to process the recorded audio. Please try again.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      };

      recorder.onerror = (event) => {
        console.error("📞 MediaRecorder error:", event.error);
        setPhoneRecordingState("idle");
        toast({
          title: "Recording Error",
          description: `MediaRecorder error: ${event.error.message}`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      };

      setPhoneMediaRecorder(recorder);
      phoneMediaRecorderRef.current = recorder;
      sLog("📞 Phone recorder initialized successfully");
      sLog("📞 Phone Audio: MediaRecorder state:", recorder.state);
      sLog("📞 Phone Audio: Stream active:", stream.active);
      sLog("📞 Phone Audio: Stream tracks:", stream.getTracks().length);
    } catch (error) {
      console.error(
        "📞 Error initializing phone recorder (attempt",
        retryCount + 1,
        "):",
        error,
      );

      // Retry for certain errors
      if (
        retryCount < maxRetries &&
        (error.name === "NotSupportedError" || error.name === "AbortError")
      ) {
        sLog("📞 Retrying initialization in 1 second...");
        setTimeout(() => {
          initializePhoneRecorder(
            retryCount + 1,
            toast,
            setPhoneMediaRecorder,
            phoneMediaRecorderRef,
            phoneStreamRef,
            phoneChunksRef,
            setPhoneRecordingState,
            setPhoneAudioURL,
            setSelectedAudioFile,
            setSelectedAudioSource,
            autoSaveSetting,
            handlePhoneAudioTranscription,
          );
        }, 1000);
        return;
      }

      let errorMessage =
        "Unable to access microphone. Please check permissions.";
      if (error.name === "NotAllowedError") {
        errorMessage =
          "Microphone access denied. Please allow microphone access and try again.";
      } else if (error.name === "NotFoundError") {
        errorMessage =
          "No microphone found. Please connect a microphone and try again.";
      } else if (error.name === "NotSupportedError") {
        errorMessage =
          "Audio recording is not supported in this browser. Please try a different browser.";
      }

      toast({
        title: "Microphone Access Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Initialize phone audio media recorder
  useEffect(() => {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      initializePhoneRecorder(
        0,
        toast,
        setPhoneMediaRecorder,
        phoneMediaRecorderRef,
        phoneStreamRef,
        phoneChunksRef,
        setPhoneRecordingState,
        setPhoneAudioURL,
        setSelectedAudioFile,
        setSelectedAudioSource,
        autoSaveSetting,
        handlePhoneAudioTranscription,
      );
    } else {
      console.error("📞 MediaDevices API not supported");
      toast({
        title: "Browser Not Supported",
        description:
          "Audio recording is not supported in this browser. Please use a modern browser like Chrome, Firefox, or Safari.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }

    // Cleanup function to stop any active streams when component unmounts
    return () => {
      sLog("📞 Cleaning up phone recorder on component unmount");

      // Stop any active recording
      if (
        phoneMediaRecorderRef.current &&
        phoneMediaRecorderRef.current.state === "recording"
      ) {
        sLog("📞 Stopping active recording on unmount");
        phoneMediaRecorderRef.current.stop();
      }

      // Stop all stream tracks
      if (phoneStreamRef.current) {
        sLog("📞 Stopping all stream tracks on unmount");
        phoneStreamRef.current.getTracks().forEach((track) => track.stop());
        phoneStreamRef.current = null;
      }

      // Clear refs
      phoneMediaRecorderRef.current = null;
      phoneChunksRef.current = [];
    };
  }, []);

  const handleStartLive = async () => {
    // Check credit limit before starting live transcription
    const creditLimitExceeded = await checkCreditLimitAndUpdateUI(toast);
    if (creditLimitExceeded) {
      sLog(
        "📊 Live transcription: Credit limit exceeded, cannot start live transcription",
      );
      return;
    }

    setLiveTranscript("");
    setLiveFacts([]);
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mediaRecorder = new window.MediaRecorder(stream, {
      mimeType: "audio/webm",
    });
    liveMediaRecorderRef.current = mediaRecorder;
    // Update WebSocket port to 8765
    const socket = new window.WebSocket("ws://localhost:8765/live-transcribe");
    socket.binaryType = "arraybuffer";
    liveSocketRef.current = socket;

    socket.onopen = () => {
      setIsLiveRecording(true);
      mediaRecorder.start(500); // 500ms chunks
    };
    socket.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === "transcript") {
          const text = msg.data?.map((seg) => seg.transcript).join(" ") || "";
          setLiveTranscript((prev) => prev + " " + text);
        } else if (msg.type === "facts") {
          setLiveFacts(msg.fact || []);
        }
      } catch (e) {
        // Ignore non-JSON (e.g., binary) messages
      }
    };
    socket.onerror = (e) => {
      setIsLiveRecording(false);
      alert("WebSocket error: " + e.message);
    };
    socket.onclose = () => {
      setIsLiveRecording(false);
    };
    mediaRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0 && socket.readyState === 1) {
        e.data.arrayBuffer().then((buffer) => {
          socket.send(buffer);
        });
      }
    };
    mediaRecorder.onstop = () => {
      stream.getTracks().forEach((track) => track.stop());
      if (socket.readyState === 1) {
        socket.close();
      }
    };
  };

  const handleStopLive = () => {
    setIsLiveRecording(false);
    if (
      liveMediaRecorderRef.current &&
      liveMediaRecorderRef.current.state !== "inactive"
    ) {
      liveMediaRecorderRef.current.stop();
    }
  };

  // Render nothing until authentication and role check are complete
  if (!isAuthenticated) {
    return null; // You could also return a loading spinner here
  }

  const isPatientView = isPatientRole(userRole);
  const isReadOnlyView = isReadOnlyRole(userRole) || isImpersonating();
  const showCreditUsagePanel = !isPatientView;

  // Patients get the VitaMed-style mobile portal instead of the clinic UI.
  if (isPatientView) {
    return <PatientPortal />;
  }

  const schedulesBuildInfo = getSchedulesVersionParts();

  return (
    // id="clinic-root" activates the Tailwind utilities — tailwind.config.js
    // scopes its `important` to this id so the redesign doesn't bleed into
    // the rest of the (mostly Chakra) app.
    <div id="clinic-root" className="min-h-[calc(100vh-64px)] bg-slate-50 dark:bg-[#06070d] text-slate-900 dark:text-slate-100">
      {/* Soft radial wash — same atmospheric touch the clinic page uses to
          break up the slate background without competing with content. */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0"
        style={{
          backgroundImage:
            'radial-gradient(circle at 18% -10%, rgba(56,189,248,0.10), transparent 45%), radial-gradient(circle at 88% 0%, rgba(244,114,182,0.09), transparent 40%), radial-gradient(circle at 50% 110%, rgba(16,185,129,0.08), transparent 50%)',
        }}
      />
      <div className="relative w-full px-4 sm:px-6 lg:px-8 py-3">
        {/* ── Hero ───────────────────────────────────────────────────────── */}
        <header className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span
                aria-hidden="true"
                className="inline-block w-1 h-3.5 rounded bg-gradient-to-b from-blue-500 to-indigo-500"
              />
              <span className="text-[10px] font-bold tracking-[0.16em] text-slate-500 dark:text-slate-400">
                PATIENT SCHEDULE
              </span>
              {userRole && (
                <span className="ml-1 rounded-full bg-slate-200/70 dark:bg-slate-800/60 px-2 py-0.5 text-[9px] font-bold tracking-widest text-slate-700 dark:text-slate-300 uppercase">
                  {userRole}
                </span>
              )}
              {selectedDate && selectedDate === getDefaultDateString() && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-500/[0.15] px-2 py-0.5 text-[9px] font-bold tracking-widest text-emerald-700 dark:text-emerald-300 uppercase">
                  <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 live-dot" />
                  Today
                </span>
              )}
            </div>
            <div className="flex items-baseline gap-3 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight leading-tight text-slate-900 dark:text-slate-50">
                {selectedDate && selectedDate === getDefaultDateString() ? (
                  <>Today&rsquo;s Clinic</>
                ) : selectedDate ? (
                  <>
                    {new Date(`${selectedDate}T00:00:00`).toLocaleDateString(undefined, {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </>
                ) : (
                  <>Schedule</>
                )}
              </h1>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {selectedDate
                  ? new Date(`${selectedDate}T00:00:00`).toLocaleDateString(undefined, {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })
                  : 'Pick a date below.'}
              </span>
            </div>
          </div>
          <div className="flex flex-row items-center gap-2 shrink-0">
            {/* Simplified / full view toggle */}
            <button
              type="button"
              onClick={() => setIsSimplifiedView((v) => !v)}
              className={`inline-flex items-center gap-1.5 rounded-md border px-3 h-8 text-sm font-bold tracking-tight shadow-sm transition-colors ${
                isSimplifiedView
                  ? "border-indigo-400/70 dark:border-indigo-500/40 bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-200 hover:bg-indigo-200 dark:hover:bg-indigo-500/30"
                  : "border-slate-200/70 dark:border-slate-700/50 bg-white dark:bg-slate-800/40 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50"
              }`}
            >
              <span aria-hidden="true" className="text-base leading-none">
                {isSimplifiedView ? "⊞" : "☰"}
              </span>
              {isSimplifiedView ? "Full View" : "Simple View"}
            </button>
            <ProvidersDropdown userRole={userRole} />
            {showCreditUsagePanel && (
              /* LLM Usage Tracker — admin / doctor only. Collapsed pill
                 + click-to-expand floating dropdown so the panel never
                 pushes the hero layout down. */
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsCreditUsageOpen((v) => !v)}
                  aria-expanded={isCreditUsageOpen}
                  className="inline-flex items-center gap-1.5 rounded-md border border-blue-200/70 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/[0.10] px-3 h-8 text-sm font-bold tracking-tight text-blue-700 dark:text-blue-200 shadow-sm hover:bg-blue-100 dark:hover:bg-blue-500/[0.18] transition-colors"
                >
                  <span aria-hidden="true" className="text-base leading-none">
                    ⚡
                  </span>
                  LLM Usage
                  <svg
                    aria-hidden="true"
                    className={`w-3 h-3 shrink-0 transition-transform ${isCreditUsageOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isCreditUsageOpen && (
                  <div className="absolute right-0 top-full mt-2 z-20 w-80 rounded-xl border border-blue-200/60 dark:border-blue-500/20 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-500/[0.08] dark:to-indigo-500/[0.08] p-4 shadow-lg max-h-96 overflow-y-auto">
                    {llmTokenUsage.isLoading ? (
                      <div className="flex flex-col items-center justify-center py-6 text-blue-700 dark:text-blue-200">
                        <svg className="animate-spin h-5 w-5 mb-2 text-blue-600 dark:text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-xs font-semibold">Loading usage stats...</span>
                      </div>
                    ) : (
                      <>
                        <div className="text-xs font-bold text-blue-955 dark:text-blue-100 mb-2 uppercase tracking-wider">
                          Your LLM Usage
                        </div>
                        <dl className="space-y-2 mb-3">
                          <div className="flex justify-between text-xs">
                            <dt className="text-blue-700 dark:text-blue-200">Queries Made</dt>
                            <dd className="font-semibold text-blue-900 dark:text-blue-50 tabular-nums">
                              {llmTokenUsage.total_queries.toLocaleString()}
                            </dd>
                          </div>
                          <div className="flex justify-between text-xs">
                            <dt className="text-blue-700 dark:text-blue-200">Tokens Consumed</dt>
                            <dd className="font-semibold text-blue-900 dark:text-blue-50 tabular-nums">
                              {llmTokenUsage.total_tokens.toLocaleString()}
                            </dd>
                          </div>
                          <div className="flex justify-between text-xs">
                            <dt className="text-blue-700 dark:text-blue-200">Token Limit</dt>
                            <dd className="font-semibold text-blue-900 dark:text-blue-50 tabular-nums">
                              {LLM_USER_TOKEN_LIMIT.toLocaleString()}
                            </dd>
                          </div>
                          <div className="space-y-1">
                            <div className="flex justify-between text-xs font-bold text-blue-800 dark:text-blue-100">
                              <span>Usage Progress</span>
                              <span className="tabular-nums">
                                {Math.round(llmTokenUsage.percent_of_limit)}%
                              </span>
                            </div>
                            <div className="w-full bg-blue-200/50 dark:bg-blue-900/50 rounded-full h-2 overflow-hidden">
                              <div 
                                className={`h-full rounded-full transition-all duration-500 ${
                                  llmTokenUsage.percent_of_limit > 90 ? 'bg-red-500' :
                                  llmTokenUsage.percent_of_limit > 70 ? 'bg-orange-500' : 'bg-green-500'
                                }`}
                                style={{ width: `${Math.min(100, llmTokenUsage.percent_of_limit)}%` }}
                              />
                            </div>
                          </div>
                        </dl>

                        {llmTokenUsage.users && llmTokenUsage.users.length > 0 && (
                          <>
                            <div className="my-2 border-t border-blue-300/70 dark:border-blue-500/30" />
                            <div className="text-xs font-bold text-blue-955 dark:text-blue-100 mb-2 uppercase tracking-wider">
                              User Limits (Tenant)
                            </div>
                            <div className="space-y-3 max-h-48 overflow-y-auto pr-1">
                              {llmTokenUsage.users.map((user, idx) => {
                                const userPercent = (user.total_tokens / LLM_USER_TOKEN_LIMIT) * 100;
                                return (
                                  <div key={user.user_id || idx} className="space-y-1">
                                    <div className="flex justify-between text-[11px] text-blue-800 dark:text-blue-200">
                                      <span className="font-medium truncate max-w-[150px]" title={user.name || user.username || 'User'}>
                                        {user.name || user.username || 'User'}
                                      </span>
                                      <span className="tabular-nums font-semibold">
                                        {Math.round(userPercent)}% ({Math.round(user.total_tokens / 1000)}k)
                                      </span>
                                    </div>
                                    <div className="w-full bg-blue-200/30 dark:bg-blue-950/40 rounded-full h-1.5 overflow-hidden">
                                      <div 
                                        className={`h-full rounded-full transition-all duration-500 ${
                                          userPercent > 90 ? 'bg-red-500' :
                                          userPercent > 70 ? 'bg-orange-500' : 'bg-green-500'
                                        }`}
                                        style={{ width: `${Math.min(100, userPercent)}%` }}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
            {upgradeEnabled && (
              /* Upgrade to Pro — docked nav CTA, per-schema on/off + link
                 controlled from Admin settings (schema_settings.upgrade_*). */
              <button
                type="button"
                onClick={() => setIsUpgradeModalOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md border border-amber-300/70 dark:border-amber-500/30 bg-gradient-to-b from-amber-50 to-amber-100 dark:from-amber-500/[0.14] dark:to-amber-500/[0.08] px-3 h-8 text-sm font-bold tracking-tight text-amber-800 dark:text-amber-200 shadow-sm hover:from-amber-100 hover:to-amber-200 dark:hover:from-amber-500/[0.2] dark:hover:to-amber-500/[0.14] transition-colors whitespace-nowrap"
              >
                <svg aria-hidden="true" className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d="M12 19V5M5 12l7-7 7 7" />
                </svg>
                Upgrade
              </button>
            )}
            {isUpgradeModalOpen && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
                role="dialog"
                aria-modal="true"
                aria-labelledby="upgrade-modal-heading"
              >
                <div
                  aria-hidden="true"
                  className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                  onClick={() => setIsUpgradeModalOpen(false)}
                />
                <div className="relative w-full max-w-lg rounded-2xl border border-slate-200 dark:border-slate-700/60 bg-white dark:bg-[#0b1420] shadow-2xl overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setIsUpgradeModalOpen(false)}
                    aria-label="Close"
                    className="absolute right-3 top-3 inline-flex items-center justify-center w-7 h-7 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:text-slate-500 dark:hover:text-slate-300 dark:hover:bg-slate-800/60 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>

                  <div className="px-7 pt-7 pb-6">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 dark:bg-amber-500/[0.14] px-2.5 py-1 text-[10px] font-bold tracking-widest text-amber-800 dark:text-amber-300 uppercase mb-4">
                      <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 7.2H22l-6 4.6 2.3 7.2L12 16.4l-6.3 4.6 2.3-7.2-6-4.6h7.6z"/></svg>
                      Pro
                    </span>
                    <h2 id="upgrade-modal-heading" className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-50 leading-snug mb-2">
                      You&rsquo;ve outgrown the free plan &mdash; make the upgrade official.
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-5 max-w-md">
                      Everything you rely on during a visit, without the caps. Pro is built for clinicians who record every day.
                    </p>

                    <ul className="space-y-3 mb-6">
                      {[
                        { title: "Live tech support sessions", desc: "book time with a real person when something's stuck" },
                        { title: "Our best AI models", desc: "sharper transcription and note quality on every visit" },
                        { title: "Unlimited recording & transcription", desc: "no monthly minute cap, ever" },
                        { title: "Your own note templates", desc: "build the structure your practice actually uses" },
                      ].map((item) => (
                        <li key={item.title} className="flex items-start gap-3 text-sm">
                          <span className="mt-0.5 flex-none inline-flex items-center justify-center w-5 h-5 rounded-md bg-emerald-100 dark:bg-emerald-500/[0.14] text-emerald-600 dark:text-emerald-400">
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                          </span>
                          <span className="text-slate-600 dark:text-slate-300">
                            <span className="font-semibold text-slate-900 dark:text-slate-100">{item.title}</span>
                            {" "}&mdash; {item.desc}
                          </span>
                        </li>
                      ))}
                      <li className="flex items-start gap-3 text-sm italic text-slate-400 dark:text-slate-500">
                        <span className="mt-0.5 flex-none inline-block w-5" />
                        &hellip; and more added every month
                      </li>
                    </ul>

                    <div className="flex items-center gap-4 flex-wrap">
                      <a
                        href={upgradeLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setIsUpgradeModalOpen(false)}
                        className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 px-5 py-2.5 text-sm font-bold text-amber-950 shadow-sm transition-colors"
                      >
                        Upgrade to Pro
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
                      </a>
                      <button
                        type="button"
                        onClick={() => setIsUpgradeModalOpen(false)}
                        className="text-sm text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 underline underline-offset-4"
                      >
                        Maybe later
                      </button>
                    </div>
                    <p className="mt-3 text-xs text-slate-400 dark:text-slate-600">
                      Secure checkout via Stripe &middot; cancel anytime
                    </p>
                  </div>
                </div>
              </div>
            )}
            {/* Tools — secondary schedule actions (same hero band as Credits). */}
            <div className="relative shrink-0">
              <button
                type="button"
                onClick={() => setIsMoreActionsOpen((v) => !v)}
                aria-expanded={isMoreActionsOpen}
                aria-label="Tools"
                className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200/45 dark:border-neutral-700/45 bg-transparent dark:bg-white/[0.04] px-3 h-8 text-sm font-medium tracking-tight text-neutral-400/85 dark:text-neutral-500 hover:border-neutral-200/70 hover:bg-neutral-50/70 dark:hover:border-neutral-600/40 dark:hover:bg-white/[0.07] shadow-none transition-colors whitespace-nowrap"
              >
                <svg className="w-4 h-4 shrink-0 opacity-70 dark:opacity-65" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Tools
                <svg
                  aria-hidden="true"
                  className={`w-3 h-3 shrink-0 opacity-65 dark:opacity-55 transition-transform ${isMoreActionsOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isMoreActionsOpen && (
                <div className="absolute right-0 top-full mt-2 z-20 w-56 rounded-xl border border-slate-200 dark:border-slate-800/60 bg-white dark:bg-[#0a0a0f] shadow-lg p-1.5">
                  {!isPatientView && (
                    <button
                      type="button"
                      onClick={() => { setIsMoreActionsOpen(false); setIsModalOpen(true); }}
                      disabled={isReadOnlyView}
                      title={isReadOnlyView ? "Read-only while viewing as another user" : undefined}
                      className="w-full inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-semibold text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-500/[0.12] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                      Add Document
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => { setIsMoreActionsOpen(false); viewDocument(); }}
                    disabled={!selectedDate}
                    className="w-full inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-500/[0.12] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    View Document
                  </button>
                  {!isPatientView && (
                    <button
                      type="button"
                      onClick={() => { setIsMoreActionsOpen(false); setIsAudioManagerModalOpen(true); }}
                      disabled={
                        !selectedPatient ||
                        Object.keys(selectedRows).length === 0 ||
                        isReadOnlyView
                      }
                      title={isReadOnlyView ? "Read-only while viewing as another user" : "Manage patient audio files and backups"}
                      className="w-full inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-semibold text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-500/[0.12] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 19V6l12-2v13M9 19a3 3 0 11-6 0 3 3 0 016 0zM21 17a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                      Audio Manager ({selectedVisitAudios.length})
                    </button>
                  )}
                  {!isPatientView && !isInGroup(userRole, 'tenantSchema') && (
                    <button
                      type="button"
                      onClick={() => { setIsMoreActionsOpen(false); handlePhoneAudio(); }}
                      disabled={
                        !selectedPatient ||
                        Object.keys(selectedRows).length === 0 ||
                        isReadOnlyView
                      }
                      title={isReadOnlyView ? "Read-only while viewing as another user" : undefined}
                      className="w-full inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-semibold text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-500/[0.12] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <FontAwesomeIcon icon={faPhone} className="text-[11px]" />
                      Phone Audio
                    </button>
                  )}
                  {!isPatientView && (
                    <>
                      <button
                        type="button"
                        onClick={() => { setIsMoreActionsOpen(false); setIsVitaMedDriveOpen(true); }}
                        className="w-full inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-semibold text-cyan-700 dark:text-cyan-300 hover:bg-cyan-50 dark:hover:bg-cyan-500/[0.12] transition-colors"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" /></svg>
                        Vita-Med Drive
                      </button>
                      <button
                        type="button"
                        onClick={() => { setIsMoreActionsOpen(false); setIsReferralLinkOpen(true); }}
                        disabled={!currentUserName}
                        className="w-full inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-semibold text-pink-600 dark:text-pink-300 hover:bg-pink-50 dark:hover:bg-pink-500/[0.12] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                        My Referral Link
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            <HelpDeskButton size="sm" />
          </div>
        </header>

        {/* Calendar + recorder — inline row, no separate section bar */}
        <div className="mb-2 flex flex-wrap items-center justify-center gap-3 rounded-xl border border-slate-200/80 dark:border-slate-800/60 bg-white/70 dark:bg-slate-900/40 px-3 py-1.5 shadow-sm backdrop-blur-sm">
          <PatientList userRole={userRole} />
          {!isPatientView && (
            <Uploader
              selection={selectedPatient}
              table={schedules}
              onProcessingChange={handleProcessingChange}
            />
          )}
        </div>

        {/* Actions — primary patient / schedule shortcuts (Tools menu lives in the hero next to Credits). */}
        <div className="mb-2 flex flex-wrap items-center gap-1.5 pb-0.5">
          {!isPatientView && (
            <>
              <button
                type="button"
                onClick={() => setIsPatientModalOpen(true)}
                disabled={isReadOnlyView}
                title={isReadOnlyView ? "Read-only while viewing as another user" : undefined}
                className="group inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold shadow-sm ring-1 ring-teal-500/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap bg-teal-600 hover:bg-teal-500 hover:shadow-md hover:-translate-y-px text-white"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
                Add {userRole ? getLabel(userRole, 'entity') : "Patient"}
              </button>
              <button
                type="button"
                onClick={() => setIsEditPatientModalOpen(true)}
                disabled={
                  !selectedPatient ||
                  Object.keys(selectedRows).length === 0 ||
                  (isInGroup(userRole, 'tenantSchema') &&
                    !selectedScheduleTenantAccess.canEditScheduleFields) ||
                  isReadOnlyView
                }
                title={isReadOnlyView ? "Read-only while viewing as another user" : undefined}
                className="group inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold shadow-sm ring-1 ring-amber-500/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap bg-amber-500 hover:bg-amber-400 hover:shadow-md hover:-translate-y-px text-white"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                Edit {userRole ? getLabel(userRole, 'entity') : "Patient"}
              </button>
            </>
          )}
          {selectedPatient && (
            <button
              type="button"
              onClick={handleDownloadPatientData}
              disabled={isDownloadingPatientData}
              className="group inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold shadow-sm ring-1 ring-green-500/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap bg-green-600 hover:bg-green-500 hover:shadow-md hover:-translate-y-px text-white"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
              {isDownloadingPatientData ? "Downloading…" : "Download Patient Data"}
            </button>
          )}
          {/* Transcribe Audio: clinical + freemium upload path */}
          {!isPatientView && (!isInGroup(userRole, 'tenantSchema') || isInGroup(userRole, 'freemium')) && (
            <button
              type="button"
              onClick={handleTranscribeAudio}
              disabled={
                !selectedPatient ||
                Object.keys(selectedRows).length === 0 ||
                isImpersonating()
              }
              title={isImpersonating() ? "Read-only while viewing as another user" : undefined}
              className="group inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold shadow-sm ring-1 ring-fuchsia-500/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap bg-fuchsia-600 hover:bg-fuchsia-500 hover:shadow-md hover:-translate-y-px text-white"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>
              <span className="hidden sm:inline">Transcribe </span>
              Audio
            </button>
          )}
          {hasClinicalAdminPermissions(userRole) && (
            <button
              type="button"
              onClick={() => { setNoShowReason(""); setIsMarkNoShowOpen(true); }}
              disabled={
                Object.keys(selectedRows).length === 0 ||
                isReadOnlyView
              }
              title={isReadOnlyView ? "Read-only while viewing as another user" : "Mark selected patient(s) as no-show"}
              className="group inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold shadow-sm ring-1 ring-red-500/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap bg-red-600 hover:bg-red-500 hover:shadow-md hover:-translate-y-px text-white"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
              <span className="hidden sm:inline">Mark </span>
              No-Show
            </button>
          )}

        </div>

        {/* Vita-Med Drive Modal */}
        <VitaMedDriveModal
          isOpen={isVitaMedDriveOpen}
          onClose={() => setIsVitaMedDriveOpen(false)}
        />

        {/* Referral Link Modal */}
        <Modal isOpen={isReferralLinkOpen} onClose={() => setIsReferralLinkOpen(false)} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>My Referral Link</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text fontSize="sm" color="gray.600" mb={3}>
                Share this link. Anyone who signs up through it will have the referral field pre-filled as "Referred by: {currentUserName}" and won't be able to change it.
              </Text>
              <Box p={3} bg="gray.50" borderRadius="md" fontSize="sm" wordBreak="break-all" color="gray.700" borderWidth={1}>
                {`${window.location.origin}${basename}/auth?ref=${encodeURIComponent(currentUserName)}&view=sign_up`}
              </Box>
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="purple"
                onClick={() => {
                  const link = `${window.location.origin}${basename}/auth?ref=${encodeURIComponent(currentUserName)}&view=sign_up`;
                  navigator.clipboard.writeText(link).then(() => {
                    toast({ title: "Link copied to clipboard!", status: "success", duration: 2000, isClosable: true });
                  });
                }}
              >
                Copy Link
              </Button>
              <Button variant="ghost" ml={3} onClick={() => setIsReferralLinkOpen(false)}>Close</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Mark as No-Show Modal */}
        <Modal isOpen={isMarkNoShowOpen} onClose={() => setIsMarkNoShowOpen(false)} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Mark as No-Show</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text fontSize="sm" color="gray.600" mb={3}>
                This will move {Object.keys(selectedRows).length} selected
                patient{Object.keys(selectedRows).length === 1 ? "" : "s"} from
                today's schedule to the No-Shows table.
              </Text>
              <FormControl>
                <FormLabel fontSize="sm">Reason (optional)</FormLabel>
                <Textarea
                  value={noShowReason}
                  onChange={(e) => setNoShowReason(e.target.value)}
                  placeholder="e.g. Patient called - sick, no transportation, forgot"
                  rows={4}
                />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="red"
                isLoading={isMarkingNoShow}
                loadingText="Marking"
                onClick={async () => {
                  setIsMarkingNoShow(true);
                  const ids = Object.keys(selectedRows);
                  const idSet = new Set(ids);
                  const supabase = await getSupabase();
                  const { data: sessionData } = await supabase.auth.getSession();
                  const markedBy = sessionData?.session?.user?.email || "unknown";
                  const markedAt = new Date().toISOString();
                  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
                  const buildMetadata = (row) => {
                    const dt = row.date_ && row.time_
                      ? new Date(`${row.date_}T${row.time_}`)
                      : null;
                    return {
                      reason: noShowReason || null,
                      factors: {
                        weather: null,
                        day_of_week: dt ? days[dt.getDay()] : null,
                        time_of_day: row.time_ || null,
                        transportation_distance: null,
                        last_contact_date: null,
                      },
                      marked_at: markedAt,
                      marked_by: markedBy,
                    };
                  };
                  // copy to no_show + delete from notes-c (true move)
                  const result = await copyItems(ids, ids, buildMetadata);
                  setIsMarkingNoShow(false);
                  if (result.success) {
                    schedulesTableActionsRef.current?.removeSchedulesById?.(ids);
                    setSchedules((prev) => prev.filter((s) => !idSet.has(s.id)));
                    setSelectedRows({});
                    setIsMarkNoShowOpen(false);
                    toast({
                      title: `${ids.length} marked as no-show`,
                      status: "success",
                      duration: 2500,
                      isClosable: true,
                    });
                  } else {
                    toast({
                      title: "Failed to mark as no-show",
                      description: String(result.error?.message || result.error || "Unknown error"),
                      status: "error",
                      duration: 5000,
                      isClosable: true,
                    });
                  }
                }}
              >
                Confirm Mark as No-Show
              </Button>
              <Button variant="ghost" ml={3} onClick={() => setIsMarkNoShowOpen(false)}>
                Cancel
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Upload Audio Modal */}
        <Modal
          isOpen={isUploadAudioModalOpen}
          onClose={() => {
            setIsUploadAudioModalOpen(false);
            setUploadAudioFileInput(null);
            setUploadAudioUrl("");
            setUploadAudioDuration(null);
            if (uploadAudioFileRef.current)
              uploadAudioFileRef.current.value = "";
          }}
          size="md"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Upload Audio</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                {/* File upload */}
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="semibold">
                    Audio File
                  </FormLabel>
                  <input
                    ref={uploadAudioFileRef}
                    type="file"
                    accept="audio/*"
                    style={{ display: "none" }}
                    onChange={handleUploadAudioFileChange}
                  />
                  <HStack>
                    <Button
                      size="sm"
                      colorScheme="purple"
                      variant="outline"
                      onClick={() => uploadAudioFileRef.current?.click()}
                    >
                      Choose File
                    </Button>
                    <Text
                      fontSize="sm"
                      color={uploadAudioFileInput ? "gray.800" : "gray.400"}
                      isTruncated
                    >
                      {uploadAudioFileInput
                        ? uploadAudioFileInput.name
                        : "No file chosen"}
                    </Text>
                  </HStack>
                </FormControl>

                <Flex align="center" gap={2}>
                  <Divider />
                  <Text fontSize="xs" color="gray.500" flexShrink={0}>
                    OR
                  </Text>
                  <Divider />
                </Flex>

                {/* URL input */}
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="semibold">
                    Audio URL
                  </FormLabel>
                  <Input
                    placeholder="https://..."
                    size="sm"
                    value={uploadAudioUrl}
                    onChange={(e) => handleUploadAudioUrlChange(e.target.value)}
                    isDisabled={!!uploadAudioFileInput}
                  />
                </FormControl>

                {/* Metadata preview */}
                {(uploadAudioFileInput || uploadAudioUrl.trim()) && (
                  <Box p={3} bg="gray.50" borderRadius="md" fontSize="sm">
                    <Text fontWeight="semibold" mb={2} color="gray.700">
                      File Info
                    </Text>
                    {
                      <VStack align="start" spacing={1}>
                        <HStack spacing={2}>
                          <Text color="gray.600">
                            <strong>Duration:</strong>{" "}
                            {uploadAudioDuration != null
                              ? `${Math.floor(uploadAudioDuration / 60)}:${String(Math.floor(uploadAudioDuration % 60)).padStart(2, "0")}`
                              : isComputingUploadMeta
                                ? ""
                                : "Unknown"}
                          </Text>
                          {isComputingUploadMeta && (
                            <HStack spacing={1}>
                              <Spinner size="xs" />
                              <Text fontSize="xs" color="gray.400">
                                computing...
                              </Text>
                            </HStack>
                          )}
                        </HStack>
                        {uploadAudioFileInput && (
                          <>
                            <Text color="gray.600">
                              <strong>Size:</strong>{" "}
                              {(
                                uploadAudioFileInput.size /
                                (1024 * 1024)
                              ).toFixed(2)}{" "}
                              MB
                            </Text>
                            <Text color="gray.600">
                              <strong>Last Modified:</strong>{" "}
                              {new Date(
                                uploadAudioFileInput.lastModified,
                              ).toLocaleString()}
                            </Text>
                            <Text color="gray.600">
                              <strong>Type:</strong>{" "}
                              {uploadAudioFileInput.type || "unknown"}
                            </Text>
                          </>
                        )}
                        <Text color="gray.600">
                          <strong>Will be saved as:</strong> Uploaded Audio
                          (source tagged "uploaded")
                        </Text>
                      </VStack>
                    }
                  </Box>
                )}
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="ghost"
                mr={3}
                onClick={() => {
                  setIsUploadAudioModalOpen(false);
                  setUploadAudioFileInput(null);
                  setUploadAudioUrl("");
                  setUploadAudioDuration(null);
                  if (uploadAudioFileRef.current)
                    uploadAudioFileRef.current.value = "";
                }}
              >
                Cancel
              </Button>
              <Button
                colorScheme="purple"
                onClick={handleUploadAudioSubmit}
                isLoading={isUploadingAudio}
                loadingText="Uploading..."
                isDisabled={
                  isUploadingAudio ||
                  isComputingUploadMeta ||
                  (!uploadAudioFileInput && !uploadAudioUrl.trim())
                }
              >
                Upload
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Inline preferences + schedule meta — single row above the table */}
        <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
          <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              checked={autoReformatEnabled}
              onChange={(e) => handleAutoReformatChange(e.target.checked)}
              className="h-3 w-3 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
            />
            Auto-reformat docs on first entry
          </label>
          {Array.isArray(schedules) && schedules.length > 0 && (
            <span className="rounded-full bg-emerald-100 dark:bg-emerald-500/[0.12] px-2 py-0.5 text-[10px] font-bold tracking-widest text-emerald-700 dark:text-emerald-300 uppercase tabular-nums">
              {schedules.length} {schedules.length === 1 ? "visit" : "visits"}
            </span>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 dark:border-slate-800/60 bg-white dark:bg-[#0a0a0f] shadow-sm overflow-hidden mb-3">
          {/* SchedulesTable always mounted so it keeps populating `schedules` state */}
          <div style={{ display: isSimplifiedView ? "none" : "block" }}>
            <SchedulesTable
              showCheckboxes={!isPatientView}
              onSelectedRowChange={handleSelectedRowChange}
              externalSelectedRows={selectedRows}
              setSchedules={setSchedules}
              processingFiles={processingFiles}
              userRole={userRole}
              actionsRef={schedulesTableActionsRef}
              onRowPatched={handleScheduleRowPatched}
            />
          </div>
          {isSimplifiedView && (
            <ScheduleRowActionsHost
              enabled={!isPatientView}
              rows={schedules}
              userRole={userRole}
              homeSchema={homeSchema}
              selectedDate={selectedDate}
              currentUserId={schedulesPageUserId}
              currentUserName={currentUserName}
              onSetConfirmationStatus={handleRowConfirmationStatus}
            >
              {(renderRowActions) => (
                <LeejoeSchedulesView
                  schedules={schedules}
                  selectedDate={selectedDate}
                  userRole={userRole}
                  selectedRows={selectedRows}
                  onSelectedRowChange={
                    isPatientView ? null : handleSelectedRowChange
                  }
                  homeSchema={homeSchema}
                  renderRowActions={renderRowActions}
                />
              )}
            </ScheduleRowActionsHost>
          )}
        </div>

        {!isPatientView && (
          <DocumentCreationModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            selectedDate={selectedDate}
            navigate={navigate}
          />
        )}
        {!isPatientView && (
          <AddPatientModal
            isOpen={isPatientModalOpen}
            onClose={() => setIsPatientModalOpen(false)}
            onAddPatient={handleAddPatient}
            initialData={{ date_: selectedDate }}
            userRole={userRole}
          />
        )}
        {sLog(
          "📅 Schedules: Passing selectedDate to AddPatientModal:",
          selectedDate,
        )}
        {!isPatientView && (
          <EditPatientModal
            isOpen={isEditPatientModalOpen}
            onClose={() => setIsEditPatientModalOpen(false)}
            patientData={selectedPatient}
            onUpdatePatient={handleUpdatePatient}
            userRole={userRole}
            readOnlyScheduleFields={
              isInGroup(userRole, 'tenantSchema') &&
              !selectedScheduleTenantAccess.canEditScheduleFields
            }
          />
        )}
        <DuplicatePatientModal
          isOpen={isDuplicateModalOpen}
          onClose={() => setIsDuplicateModalOpen(false)}
          patientData={{
            [prompts[0][0]]: selectedPatient?.[prompts[0][0]],
            [prompts[1][0]]: selectedPatient?.[prompts[1][0]],
            [prompts[2][0]]: selectedPatient?.[prompts[2][0]],
            EHR_ID: selectedPatient?.EHR_ID,
            [prompts[getTypeFieldIndex()][0]]:
              selectedPatient?.[prompts[getTypeFieldIndex()][0]],
            date_: selectedPatient?.date_,
            time_: selectedPatient?.time_,
            forms_ind: selectedPatient?.forms_ind,
          }}
          onDuplicatePatient={handleDuplicateSave}
        />
        <Modal
          isOpen={isTranscribeModalOpen}
          onClose={() => {
            setIsTranscribeModalOpen(false);
            setSelectedAudioFile(null);
            setFacts(null);
            setTranscript(null);
            setSoapNotes(null);
            setTranscriptionCreditUsage(null);
            setSelectedAudioSource(null);
            setSelectedAudioUrl(null);
            setHasUploadedAudio(false);
            stopTimer();
          }}
        >
          <ModalOverlay />
          <ModalContent maxW="800px" maxH="90vh" position="relative">
            <ModalHeader>
              Transcribe Audio
              {/* Model Selection Indicator */}
              {hasUploadedAudio && (
                <Text fontSize="sm" fontWeight="normal" color="gray.600" mt={1}>
                  Model:{" "}
                  <Text
                    as="span"
                    fontWeight="bold"
                    color={
                      selectedTranscriptionModel === "corti"
                        ? "blue.600"
                        : "purple.600"
                    }
                  >
                    {selectedTranscriptionModel === "corti"
                      ? "Corti AI"
                      : "VOSK"}
                  </Text>
                </Text>
              )}
            </ModalHeader>
            <ModalCloseButton />

            {/* Timer Display - positioned in top right corner */}
            {elapsedTime > 0 && (
              <Box
                position="absolute"
                top="10px"
                right="60px"
                bg="orange.500"
                color="white"
                px={4}
                py={2}
                borderRadius="lg"
                boxShadow="xl"
                zIndex={10}
                minWidth="140px"
              >
                <Text fontSize="xl" fontWeight="bold" textAlign="center">
                  ⏱️ {Math.floor(elapsedTime / 60)}:
                  {(elapsedTime % 60).toString().padStart(2, "0")}
                </Text>
                <Text fontSize="sm" fontWeight="bold" textAlign="center">
                  📊 {estimatedProgress}%
                </Text>
                <Text fontSize="xs" textAlign="center" opacity={0.9}>
                  {isLoading
                    ? currentMilestone || "Transcribing..."
                    : currentMilestone === "Completed"
                      ? "Completed"
                      : "Ready"}
                </Text>
              </Box>
            )}

            <ModalBody maxH="70vh" overflowY="auto">
              <VStack spacing={4} align="stretch">
                 {/* Patient Audio Selection - enabled for all users; freemium uses VOSK automatically */}
                {selectedPatient?.more_audios &&
                  selectedPatient.more_audios.length > 0 && (
                    <Box>
                      <Heading size="md" mb={2}>
                        Transcribe with{" "}
                        {isInGroup(userRole, 'freemium') ? "VOSK" : "Patient Audio"}
                      </Heading>
                      <Text mb={3} fontSize="sm" color="gray.600">
                        {isInGroup(userRole, 'freemium')
                          ? "Select a saved patient audio file (uses VOSK):"
                          : "Select a saved patient audio file:"}
                      </Text>
                      <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                        {selectedPatient.more_audios.map((audio, index) => (
                          <Box
                            key={index}
                            p={3}
                            borderWidth={1}
                            borderRadius="md"
                            bg={
                              selectedAudioSource === "url" &&
                              selectedAudioUrl === audio.url
                                ? "blue.50"
                                : "gray.50"
                            }
                          >
                            <HStack justify="space-between" align="start">
                              <VStack align="start" spacing={1} flex={1}>
                                <Text fontWeight="bold" fontSize="sm">
                                  {recordingLabel(audio, index + 1)}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  {audio.duration
                                    ? `${Math.floor(audio.duration / 60)}:${Math.round(
                                        audio.duration % 60,
                                      )
                                        .toString()
                                        .padStart(2, "0")}`
                                    : "Duration unknown"}
                                </Text>
                                {audio.source && (
                                  <Text fontSize="xs" color="gray.400">
                                    Source: {audio.source}
                                  </Text>
                                )}
                              </VStack>
                              <Button
                                colorScheme="blue"
                                size="sm"
                                onClick={() => {
                                  setSelectedAudioSource("url");
                                  setSelectedAudioUrl(audio.url);
                                  setSelectedAudioFile(null);
                                  setSelectedAudioDuration(audio.duration);
                                  setHasUploadedAudio(true);
                                  // For all users, open model selection to confirm
                                  setPendingTranscribeAction({
                                    type: "patient_audio",
                                    audio,
                                  });
                                  setIsModelSelectionOpen(true);
                                }}
                                isDisabled={isLoading}
                              >
                                Transcribe
                              </Button>
                            </HStack>
                          </Box>
                        ))}
                      </SimpleGrid>
                    </Box>
                  )}

                {/* Divider */}
                {selectedPatient?.more_audios &&
                  selectedPatient.more_audios.length > 0 && <Divider />}

                {/* File Upload Option */}
                <Box>
                  <Heading size="md" mb={3}>
                    {selectedPatient?.more_audios &&
                    selectedPatient.more_audios.length > 0
                      ? "Or Upload New Audio File"
                      : "Upload Audio File"}
                  </Heading>
                  <Input
                    type="file"
                    accept="audio/*,.mpeg,.mpg,.m4a,.m4b,.aac,.mov,.mp4,.3gp,.amr,.ogg,.flac,.caf,.3gpp,.wma,.wav,.mp3,.webm"
                    isDisabled={
                      selectedAudioSource === "url" && !!selectedAudioUrl
                    }
                    onChange={(e) => {
                      const file = e.target.files[0];
                      setSelectedAudioSource("file");
                      setSelectedAudioFile(file);
                      setSelectedAudioUrl(null);

                      // Get audio duration from uploaded file
                      if (file) {
                        const audio = new Audio();
                        audio.src = URL.createObjectURL(file);
                        audio.addEventListener("loadedmetadata", () => {
                          setSelectedAudioDuration(audio.duration);
                        });
                      }
                    }}
                  />
                  {selectedAudioFile && (
                    <Text mt={2} color="green.600">
                      Selected: {selectedAudioFile.name}
                    </Text>
                  )}
                </Box>

                {/* URL paste — enabled for freemium; only “Transcribe with Corti” patient library stays greyed */}
                <Box>
                  <Heading size="md" mb={3}>
                    Or Enter Audio URL
                  </Heading>
                  <Input
                    placeholder="https://..."
                    size="sm"
                    value={
                      selectedAudioSource === "url" ? selectedAudioUrl : ""
                    }
                    onChange={(e) => {
                      const url = e.target.value;
                      setSelectedAudioSource("url");
                      setSelectedAudioUrl(url);
                      setSelectedAudioFile(null);

                      // Get audio duration from URL
                      if (url.trim()) {
                        computeAudioDuration(url.trim()).then((dur) => {
                          setSelectedAudioDuration(dur);
                        });
                      } else {
                        setSelectedAudioDuration(null);
                      }
                    }}
                    isDisabled={!!selectedAudioFile}
                  />
                  {selectedAudioSource === "url" && selectedAudioUrl && (
                    <Text mt={2} fontSize="sm" color="gray.600">
                      URL: {selectedAudioUrl}
                    </Text>
                  )}
                  {selectedAudioSource === "url" && selectedAudioUrl && (
                    <Box p={3} bg="blue.50" borderRadius="md" mt={2}>
                      <Text fontWeight="bold" color="blue.700">
                        {selectedPatient?.more_audios?.find(
                          (a) => a.url === selectedAudioUrl,
                        )
                          ? `Selected Patient Audio: ${selectedPatient.more_audios.find((a) => a.url === selectedAudioUrl)?.name}`
                          : "Selected Audio URL"}
                      </Text>
                      <Text fontSize="sm" color="blue.600">
                        {selectedAudioUrl}
                      </Text>
                    </Box>
                  )}
                </Box>

                {/* Progress bar when loading */}
                {isLoading && (
                  <Box>
                    <Progress
                      size="md"
                      isIndeterminate
                      colorScheme="orange"
                      mb={2}
                    />
                  </Box>
                )}

                {transcript && (
                  <Box>
                    {/* Credit Usage Display */}
                    {transcriptionCreditUsage && (
                      <Box
                        mb={4}
                        p={3}
                        bg="blue.50"
                        borderRadius="md"
                        borderWidth={1}
                        borderColor="blue.200"
                      >
                        <HStack justify="space-between" align="center">
                          <Text fontWeight="bold" color="blue.700">
                            💳 Credits Used:{" "}
                            {transcriptionCreditUsage.toFixed(4)}
                          </Text>
                          <Text fontSize="sm" color="blue.600">
                            Recorded Transcription
                          </Text>
                        </HStack>
                      </Box>
                    )}

                    {/* Action buttons at the top */}
                    <HStack spacing={3} mb={4} justify="flex-end">
                      <Button colorScheme="blue" onClick={generatePDF}>
                        Export to PDF
                      </Button>
                      <Button
                        colorScheme="teal"
                        onClick={() => setIsViewNotesOpen(true)}
                      >
                        View Notes
                      </Button>
                      {/* Only show Save Results button if auto-save is disabled for this patient */}
                      {!getAutoSaveSetting(selectedPatient?.id) && (
                        <Button
                          colorScheme="green"
                          onClick={async () => {
                            if (
                              !selectedPatient ||
                              !transcript ||
                              !facts ||
                              !soapNotes
                            ) {
                              toast({
                                title: "No Data to Save",
                                description:
                                  "Please complete a transcription first.",
                                status: "warning",
                                duration: 5000,
                                isClosable: true,
                              });
                              return;
                            }

                            try {
                              sLog(
                                "%c💳 CORTI CREDITS - SAVING TRANSCRIPTION RESULTS",
                                "color: gold; font-weight: bold; font-size: 14px;",
                              );
                              sLog(
                                "📊 Saving transcription for patient:",
                                selectedPatient?.[prompts[0][0]],
                              );
                              sLog("📊 Patient ID:", selectedPatient?.id);

                              const supabase = await getSupabase();

                              // First, get existing transcriptions
                              const { data: existingData, error: fetchError } =
                                await supabase
                                  .from(tableName)
                                  .select("transcripts")
                                  .eq("id", selectedPatient.id)
                                  .single();

                              if (
                                fetchError &&
                                fetchError.code !== "PGRST116"
                              ) {
                                // PGRST116 is "not found"
                                throw fetchError;
                              }

                              // Create new transcription data
                              const newTranscription = {
                                id: Date.now().toString(), // Generate unique ID
                                patient_id:
                                  selectedPatient.id?.toString() || "",
                                name: (
                                  selectedPatient[prompts[0][0]] || ""
                                ).toString(),
                                transcript: Array.isArray(transcript)
                                  ? transcript
                                  : [transcript],
                                facts: Array.isArray(facts) ? facts : [facts],
                                soap_notes: soapNotes || {},
                                audio_duration: selectedAudioDuration || 0,
                                audio_source: selectedAudioSource || "",
                                audio_file_name:
                                  (selectedAudioSource === "url"
                                    ? selectedPatient.more_audios?.find(
                                        (a) => a.url === selectedAudioUrl,
                                      )?.name
                                    : selectedAudioFile?.name) || "",
                                estimated_credit_usage:
                                  transcriptionCreditUsage || null, // Include credit usage from API response
                                created_at: new Date().toISOString(),
                              };

                              // Prepare the updated transcripts array
                              let updatedTranscripts = [];
                              if (existingData && existingData.transcripts) {
                                // If existing data is an array, use it; otherwise, convert single object to array
                                if (Array.isArray(existingData.transcripts)) {
                                  updatedTranscripts = [
                                    ...existingData.transcripts,
                                    newTranscription,
                                  ];
                                } else {
                                  // Convert single transcription object to array and add new one
                                  updatedTranscripts = [
                                    existingData.transcripts,
                                    newTranscription,
                                  ];
                                }
                              } else {
                                // No existing transcriptions, start with new one
                                updatedTranscripts = [newTranscription];
                              }

                              // Log before update
                              sLog(
                                "Updating patient record with id:",
                                selectedPatient.id,
                              );
                              sLog(
                                "Updated transcripts array:",
                                updatedTranscripts,
                              );

                              // Update the transcripts column with the array
                              const { data, error } = await supabase
                                .from(tableName)
                                .update({ transcripts: updatedTranscripts })
                                .eq("id", selectedPatient.id)
                                .select();

                              // Log after update
                              sLog("Update result data:", data);
                              if (error) throw error;

                              sLog(
                                "%c💳 CORTI CREDITS - TRANSCRIPTION RESULTS SAVED",
                                "color: gold; font-weight: bold; font-size: 14px;",
                              );
                              sLog(
                                "📊 Transcription results successfully saved to database",
                              );
                              sLog(
                                "📊 Patient record updated with new transcription",
                              );

                              // Credits are already added when transcription completes
                              // No need to add them again here

                              toast({
                                title: "Transcription Saved!",
                                description:
                                  "Transcription results saved to database!",
                                status: "success",
                                duration: 3000,
                                isClosable: true,
                              });

                              // Refresh the page after successful save
                              setTimeout(() => {
                                window.location.reload();
                              }, 1000);
                            } catch (error) {
                              console.error(
                                "Error saving transcription to Supabase:",
                                error,
                              );
                              sLog(
                                "%c💳 CORTI CREDITS - SAVE TRANSCRIPTION FAILED",
                                "color: gold; font-weight: bold; font-size: 14px;",
                              );
                              sLog(
                                "📊 Failed to save transcription results:",
                                error.message,
                              );

                              toast({
                                title: "Error",
                                description:
                                  "Failed to save transcription results to database.",
                                status: "error",
                                duration: 5000,
                                isClosable: true,
                              });
                            }
                          }}
                        >
                          Save Results
                        </Button>
                      )}
                    </HStack>

                    <HStack justify="space-between" mb={2}>
                      <Heading size="md">Transcript</Heading>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const text = Array.isArray(transcript)
                            ? transcript
                                .map((line) =>
                                  typeof line === "string"
                                    ? line
                                    : line?.transcript || line?.text || "",
                                )
                                .filter(Boolean)
                                .join("\n")
                            : String(transcript || "");
                          const blob = new Blob([text], { type: "text/plain" });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          const patientName = (selectedPatient?.[prompts[0][0]] || "patient").replace(/[^a-zA-Z0-9_-]/g, "_");
                          const dateStr = selectedPatient?.date_ || new Date().toISOString().split("T")[0];
                          a.download = `${patientName}_${dateStr}_transcript.txt`;
                          a.click();
                          URL.revokeObjectURL(url);
                        }}
                      >
                        Download
                      </Button>
                    </HStack>
                    <Box
                      maxH="300px"
                      overflowY="auto"
                      p={4}
                      borderWidth={1}
                      borderRadius="md"
                    >
                      {transcript.map((line, index) => (
                        <Text key={index} mb={2}>
                          {line}
                        </Text>
                      ))}
                    </Box>
                  </Box>
                )}

                {facts && (
                  <Box>
                    <Heading size="md" mb={2}>
                      Extracted Facts
                    </Heading>
                    <Box
                      maxH="300px"
                      overflowY="auto"
                      p={4}
                      borderWidth={1}
                      borderRadius="md"
                    >
                      {Array.isArray(facts) ? (
                        facts.map((fact, index) => (
                          <Box
                            key={index}
                            mb={2}
                            p={2}
                            bg="gray.50"
                            borderRadius="md"
                          >
                            <Text>{fact.text}</Text>
                            <Text fontSize="sm" color="gray.500">
                              Category: {fact.group}
                            </Text>
                          </Box>
                        ))
                      ) : (
                        <Box>
                          <Alert status="error" borderRadius="md" mb={2}>
                            <AlertIcon />
                            <AlertTitle>Transcription Error</AlertTitle>
                            <AlertDescription>
                              {facts.error || "No facts available"}
                            </AlertDescription>
                          </Alert>
                          {facts.error &&
                            facts.error.includes("No speech detected") && (
                              <Box
                                bg="orange.50"
                                p={3}
                                borderRadius="md"
                                borderWidth={1}
                                borderColor="orange.200"
                              >
                                <Text fontSize="sm" color="orange.800">
                                  💡 <strong>Tip:</strong> Make sure your audio
                                  file contains clear speech. Audio files with
                                  only background noise, music, or silence
                                  cannot be transcribed.
                                </Text>
                              </Box>
                            )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                {soapNotes &&
                  (soapNotes.transcript_only || soapNotes.facts_only) && (
                    <Box>
                      {soapNotes.transcript_only && (
                        <Box mb={4}>
                          {sLog(
                            "🎯 Rendering transcript-only SOAP notes with:",
                            soapNotes.transcript_only,
                          )}
                          <Heading size="md" mb={2}>
                            SOAP Notes (Transcript-Based)
                          </Heading>
                          <Box
                            maxH="300px"
                            overflowY="auto"
                            p={4}
                            borderWidth={1}
                            borderRadius="md"
                          >
                            <Text whiteSpace="pre-wrap" fontSize="sm">
                              {soapNotes.transcript_only}
                            </Text>
                          </Box>
                        </Box>
                      )}

                      {soapNotes.facts_only && (
                        <Box>
                          {sLog(
                            "🎯 Rendering facts-only SOAP notes with:",
                            soapNotes.facts_only,
                          )}
                          <Heading size="md" mb={2}>
                            SOAP Notes (Facts-Based)
                          </Heading>
                          <Box
                            maxH="300px"
                            overflowY="auto"
                            p={4}
                            borderWidth={1}
                            borderRadius="md"
                          >
                            <Text whiteSpace="pre-wrap" fontSize="sm">
                              {soapNotes.facts_only}
                            </Text>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}

                {/* Show message when SOAP notes were cleaned (contained invalid data) */}
                {soapNotes &&
                  !soapNotes.transcript_only &&
                  !soapNotes.facts_only && (
                    <Box>
                      <Alert status="info" borderRadius="md">
                        <AlertIcon />
                        <AlertTitle>SOAP Notes Unavailable</AlertTitle>
                        <AlertDescription>
                          SOAP notes could not be generated for this
                          transcription. This may be due to insufficient content
                          or processing issues.
                        </AlertDescription>
                      </Alert>
                    </Box>
                  )}

                {/* Show the last elapsed time after transcription is done */}
                {lastElapsedTime !== null && !isLoading && (
                  <Box mt={2} textAlign="center">
                    <Text color="gray.600" fontSize="sm">
                      Last transcription took: ⏱️{" "}
                      {Math.floor(lastElapsedTime / 60)}:
                      {(lastElapsedTime % 60).toString().padStart(2, "0")}
                    </Text>
                  </Box>
                )}
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="blue"
                mr={3}
                ref={transcribeButtonRef}
                onClick={async () => {
                  // Check if this is an upload operation (new file or URL, not from existing patient audio)
                  const isNewUpload =
                    (selectedAudioSource === "file" && selectedAudioFile) ||
                    (selectedAudioSource === "url" &&
                      selectedAudioUrl &&
                      !selectedPatient?.more_audios?.some(
                        (a) => a.url === selectedAudioUrl,
                      ));

                  // If it's a new upload and we haven't uploaded yet, do upload first
                  if (isNewUpload && !hasUploadedAudio) {
                    // Perform upload only
                    if (
                      selectedAudioSource === "file" &&
                      selectedAudioFile &&
                      selectedPatient
                    ) {
                      try {
                        sLog("📁 Uploading audio to patient record...");
                        const supabase = await getSupabase();

                        const { data: currentData, error: fetchError } =
                          await supabase
                            .from(tableName)
                            .select("more_audios")
                            .eq("id", selectedPatient.id)
                            .single();
                        if (fetchError && fetchError.code !== "PGRST116")
                          throw fetchError;

                        const currentMoreAudios =
                          currentData?.more_audios || [];
                        const audioCount = currentMoreAudios.length + 1;
                        const now = new Date().toISOString();

                        const contentType =
                          selectedAudioFile.type || "audio/webm";
                        const blob = new Blob(
                          [await selectedAudioFile.arrayBuffer()],
                          { type: contentType },
                        );

                        const result = await uploadAudioFile(
                          blob,
                          selectedAudioFile.name,
                          bucketName,
                          supabaseUrl,
                          {
                            patientId: selectedPatient.id,
                            patientName: selectedPatient[prompts[0][0]],
                          },
                          selectedDate || null,
                          "_Uploaded",
                        );

                        const newAudio = {
                          name: `Uploaded Audio ${audioCount}`,
                          displayName: `Uploaded Audio ${audioCount}`,
                          description: selectedAudioFile.name,
                          url: result.url,
                          duration: selectedAudioDuration || 0,
                          uploaded_at: now,
                          recording_date: now,
                          source: "uploaded",
                          type: "audio",
                          patient_id: selectedPatient.id,
                          file_size: selectedAudioFile.size,
                          content_type: contentType,
                        };

                        const nextMoreAudios = [...currentMoreAudios, newAudio];
                        const { error: updateError } = await supabase
                          .from(tableName)
                          .update({ more_audios: nextMoreAudios })
                          .eq("id", selectedPatient.id);
                        if (updateError) throw updateError;

                        // This modal's audio list renders from
                        // selectedPatient.more_audios, so mirror the write into
                        // local state. Without it the new entry — and the
                        // Transcribe button beside it — only appears after a refresh.
                        setSelectedPatient((prev) =>
                          prev && prev.id === selectedPatient.id
                            ? { ...prev, more_audios: nextMoreAudios }
                            : prev,
                        );

                        sLog("✅ Audio uploaded:", newAudio.name);
                        toast({
                          title: "Audio Uploaded",
                          description:
                            "Audio saved to patient record. Select an audio to transcribe.",
                          status: "success",
                          duration: 3000,
                          isClosable: true,
                        });
                      } catch (saveError) {
                        console.error("❌ Error uploading audio:", saveError);
                        toast({
                          title: "Upload Failed",
                          description: "Could not upload audio.",
                          status: "error",
                          duration: 5000,
                          isClosable: true,
                        });
                        return;
                      }
                    } else if (
                      selectedAudioSource === "url" &&
                      selectedAudioUrl &&
                      selectedPatient
                    ) {
                      // URL upload
                      try {
                        sLog("📁 Saving URL audio to patient record...");
                        const supabase = await getSupabase();

                        const { data: currentData, error: fetchError } =
                          await supabase
                            .from(tableName)
                            .select("more_audios")
                            .eq("id", selectedPatient.id)
                            .single();
                        if (fetchError && fetchError.code !== "PGRST116")
                          throw fetchError;

                        const currentMoreAudios =
                          currentData?.more_audios || [];
                        const audioCount = currentMoreAudios.length + 1;
                        const now = new Date().toISOString();

                        const newAudio = {
                          name: `URL Audio ${audioCount}`,
                          displayName: `URL Audio ${audioCount}`,
                          description:
                            selectedAudioUrl.split("/").pop()?.split("?")[0] ||
                            "URL Audio",
                          url: selectedAudioUrl,
                          duration: selectedAudioDuration || 0,
                          uploaded_at: now,
                          recording_date: now,
                          source: "url",
                          type: "audio",
                          patient_id: selectedPatient.id,
                        };

                        const nextMoreAudios = [...currentMoreAudios, newAudio];
                        const { error: updateError } = await supabase
                          .from(tableName)
                          .update({ more_audios: nextMoreAudios })
                          .eq("id", selectedPatient.id);
                        if (updateError) throw updateError;

                        // This modal's audio list renders from
                        // selectedPatient.more_audios, so mirror the write into
                        // local state. Without it the new entry — and the
                        // Transcribe button beside it — only appears after a refresh.
                        setSelectedPatient((prev) =>
                          prev && prev.id === selectedPatient.id
                            ? { ...prev, more_audios: nextMoreAudios }
                            : prev,
                        );

                        sLog("✅ URL audio uploaded:", newAudio.name);
                        toast({
                          title: "Audio URL Saved",
                          description:
                            "Audio URL saved to patient record. Select an audio to transcribe.",
                          status: "success",
                          duration: 3000,
                          isClosable: true,
                        });
                      } catch (saveError) {
                        console.error("❌ Error saving URL audio:", saveError);
                        toast({
                          title: "Save Failed",
                          description: "Could not save audio URL.",
                          status: "error",
                          duration: 5000,
                          isClosable: true,
                        });
                        return;
                      }
                    }

                    // Mark as uploaded and reset selection to show patient audio list
                    setHasUploadedAudio(true);
                    setSelectedAudioFile(null);
                    setSelectedAudioUrl(null);
                    setSelectedAudioSource(null);
                    setSelectedAudioDuration(null);
                    return; // Stop here - don't transcribe yet
                  }

                  // Normal transcription flow (selecting from existing audio or after upload)
                  if (!selectedAudioSource) {
                    toast({
                      title: "No Audio Selected",
                      description:
                        "Please select a patient audio file to transcribe.",
                      status: "warning",
                      duration: 5000,
                      isClosable: true,
                    });
                    return;
                  }

                  if (selectedAudioSource === "url" && !selectedAudioUrl) {
                    toast({
                      title: "No URL Entered",
                      description: "Please enter a valid audio URL.",
                      status: "warning",
                      duration: 5000,
                      isClosable: true,
                    });
                    return;
                  }

                  if (selectedAudioSource === "file" && !selectedAudioFile) {
                    toast({
                      title: "No File Selected",
                      description: "Please select an audio file to transcribe.",
                      status: "warning",
                      duration: 5000,
                      isClosable: true,
                    });
                    return;
                  }

                  setFacts(null);
                  setTranscript(null);
                  setTranscriptionCreditUsage(null);

                  // Show loading state immediately so users see the button
                  // is doing something. The credit check below is an async
                  // network roundtrip (1–3s); previously the UI sat idle
                  // for that whole time before setIsLoading(true) ran.
                  setIsLoading(true);
                  startTimer();
                  setCurrentMilestone("Preparing transcription…");
                  setEstimatedProgress(2);
                  setMilestoneProgress(0);

                  // Check credit limit before starting transcription.
                  // If it blocks, revert the loading state we just set
                  // so the UI doesn't get stuck.
                  const creditLimitExceeded =
                    await checkCreditLimitAndUpdateUI(toast);
                  if (creditLimitExceeded) {
                    sLog(
                      "📊 Regular transcription: Credit limit exceeded, cannot transcribe",
                    );
                    setIsLoading(false);
                    stopTimer();
                    setEstimatedProgress(0);
                    setCurrentMilestone("");
                    setMilestoneProgress(0);
                    return;
                  }

                  try {
                    sLog("Starting transcription using REST API...");
                    sLog(
                      "%c💳 CORTI CREDITS - RECORDED TRANSCRIPTION STARTED",
                      "color: gold; font-weight: bold; font-size: 14px;",
                    );
                    sLog("📊 Audio source:", selectedAudioSource);
                    sLog("📊 Audio duration:", selectedAudioDuration);
                    sLog("📊 Patient:", selectedPatient?.[prompts[0][0]]);

                    // Server-side Vosk was retired (see
                    // backend/Corti_buttons/views.py::transcribe_with_vosk). When
                    // the user picks Vosk in the model selector, transcribe in
                    // the browser against the Vosk service hosted at
                    // VOSK_SERVICE_ORIGIN and feed the result through the same
                    // auto-save path the Corti response takes.
                    if (selectedTranscriptionModel === "vosk") {
                      let browserAudioInput = null;
                      if (selectedAudioSource === "file") {
                        browserAudioInput = selectedAudioFile;
                      } else if (
                        selectedAudioSource === "url" &&
                        selectedAudioUrl
                      ) {
                        // Pass the URL as a string — the service knows how to
                        // download it (Supabase Storage SDK first, plain
                        // fetch with apikey second, bare fetch third).
                        browserAudioInput = selectedAudioUrl;
                      } else {
                        throw new Error(
                          "No audio source available for in-browser Vosk transcription.",
                        );
                      }

                      setCurrentMilestone(
                        "Transcribing in browser via Vosk…",
                      );
                      const PHASE_LABEL = {
                        decoding: "Decoding audio",
                        transcribing: "Transcribing",
                        finalizing: "Finalizing transcription",
                        done: "Completed",
                        error: "Transcription error",
                      };
                      const formatClock = (secs) => {
                        if (!Number.isFinite(secs) || secs < 0) return "0:00";
                        const total = Math.round(secs);
                        const m = Math.floor(total / 60);
                        const s = total % 60;
                        return `${m}:${s.toString().padStart(2, "0")}`;
                      };
                      const inBrowser = await transcribeUploadedAudioInBrowser(
                        browserAudioInput,
                        {
                          onStatus: (msg) => setCurrentMilestone(msg),
                          onProgress: (p) => {
                            const ratio = Math.max(
                              0,
                              Math.min(1, Number(p?.ratio) || 0),
                            );
                            setEstimatedProgress(Math.round(ratio * 100));
                            const phaseLabel =
                              PHASE_LABEL[p?.phase] || "Transcribing";
                            const seconds =
                              typeof p?.secondsProcessed === "number"
                                ? p.secondsProcessed
                                : null;
                            const total =
                              typeof p?.totalSeconds === "number"
                                ? p.totalSeconds
                                : null;
                            const eta =
                              typeof p?.etaSeconds === "number" &&
                              p.etaSeconds !== null
                                ? ` · ~${Math.max(1, Math.round(p.etaSeconds))}s left`
                                : "";
                            if (seconds != null && total) {
                              setCurrentMilestone(
                                `${phaseLabel} ${formatClock(seconds)} / ${formatClock(total)}${eta}`,
                              );
                            } else {
                              setCurrentMilestone(
                                `${phaseLabel}${eta}`,
                              );
                            }
                          },
                        },
                      );
                      const voskText = (inBrowser?.text || "").trim();
                      if (!voskText) {
                        // Surface the recogniser diagnostic counters in the
                        // error so the failure mode is identifiable from
                        // the UI without opening DevTools. The shape is:
                        //   { totalSeconds, totalAcceptSamples,
                        //     acceptWaveformCalls, resultEvents,
                        //     emptyResultEvents, partialEvents,
                        //     retrieveFinalCalls, lastResultText,
                        //     lastPartialText, lastRetrieveFinal }
                        // Three failure modes this distinguishes:
                        //   A. Audio decode produced < 1 s of audio
                        //      (totalSeconds < 1 && resultEvents === 0)
                        //   B. Worker never produced any events
                        //      (resultEvents === 0 && partialEvents === 0)
                        //   C. Worker produced events but they were all
                        //      empty (resultEvents === 0 && partialEvents > 0)
                        const diag = inBrowser?.diag;
                        const diagSummary = diag
                          ? ` ${JSON.stringify({
                              totalSeconds: Math.round(
                                (inBrowser?.durationSeconds || 0) * 100,
                              ) / 100,
                              acceptWaveformCalls: diag.acceptWaveformCalls,
                              resultEvents: diag.resultEvents,
                              emptyResultEvents: diag.emptyResultEvents,
                              partialEvents: diag.partialEvents,
                              retrieveFinalCalls: diag.retrieveFinalCalls,
                              largestAcceptSamples: diag.largestAcceptSamples,
                              lastPartialTextLen: (diag.lastPartialText || "").length,
                            })}`
                          : "";
                        throw new Error(
                          "Vosk returned an empty transcript. Try a clearer recording or a different file." +
                            diagSummary,
                        );
                      }
                      const transcriptLines = voskText
                        .split(/(?<=[.!?])\s+|\n+/)
                        .map((s) => s.trim())
                        .filter(Boolean);
                      const data = {
                        transcript: transcriptLines,
                        facts: [],
                        soap_notes: null,
                        credit_usage: 0,
                        audio_storage_url: null,
                        processing_method: "vosk_browser",
                        vosk_service_origin: VOSK_SERVICE_ORIGIN,
                      };
                      setEstimatedProgress(100);
                      setCurrentMilestone("Completed");
                      const decodedSecs = Math.round(
                        inBrowser?.durationSeconds || 0,
                      );
                      const decodedClock = `${Math.floor(decodedSecs / 60)}:${(
                        decodedSecs % 60
                      )
                        .toString()
                        .padStart(2, "0")}`;
                      const wordCount = voskText.split(/\s+/).filter(Boolean)
                        .length;
                      toast({
                        title: "Transcription Complete",
                        description: `Transcribed ${decodedClock} of audio (${wordCount} words) in browser via Vosk. If that duration is shorter than your file, the audio failed to fully decode — try a WAV/MP3 copy.`,
                        status: "success",
                        duration: 8000,
                        isClosable: true,
                      });
                      setFacts([]);
                      setTranscript(
                        Array.isArray(data.transcript) ? data.transcript : [],
                      );
                      setSoapNotes(cleanSoapNotes(data.soap_notes));
                      // Run the same auto-save the Corti path uses; creditUsage
                      // is 0 so no charge is logged against the user.
                      setTimeout(async () => {
                        const tid = await autoSaveTranscriptionResults(
                          Array.isArray(data.transcript) ? data.transcript : [],
                          [],
                          cleanSoapNotes(data.soap_notes) || {},
                          data.credit_usage,
                          data.audio_storage_url,
                          selectedAudioSource === "file"
                            ? selectedAudioFile
                            : null,
                          selectedAudioDuration,
                          true,
                        );
                        if (tid && selectedPatient?.id)
                          await triggerAiRecapForTranscript(
                            selectedPatient.id,
                            tid,
                            data.transcript,
                          );
                      }, 100);
                      return;
                    }

                    const hostname = window.location.hostname;
                    const baseUrl =
                      hostname === "localhost"
                        ? BASE_URLS.local.backend
                        : BASE_URLS.render.backend;

                    const formData = new FormData();

                    if (selectedAudioSource === "url") {
                      // Send URL as a file name - process_audio handles URLs directly
                      sLog("Sending audio URL to backend:", selectedAudioUrl);
                      const urlBlob = new Blob([selectedAudioUrl], {
                        type: "text/plain",
                      });
                      formData.append("audio_file", urlBlob, "audio_url.txt");
                    } else {
                      // Send actual file
                      sLog(
                        "Sending audio file to backend:",
                        selectedAudioFile.name,
                      );
                      formData.append("audio_file", selectedAudioFile);
                    }

                    // Add transcription model selection
                    formData.append(
                      "transcription_model",
                      selectedTranscriptionModel,
                    );
                    console.log(
                      "📊 Using transcription model:",
                      selectedTranscriptionModel,
                    );

                    const res = await fetch(
                      `${baseUrl}/corti/transcribe-recorded/`,
                      {
                        method: "POST",
                        body: formData,
                        credentials: "include",
                        headers: {
                          "X-CSRFToken":
                            document.cookie
                              .split("csrftoken=")[1]
                              ?.split(";")[0] || "",
                        },
                      },
                    );

                    sLog("Response status:", res.status);
                    const data = await res.json();
                    sLog("Received data:", data);
                    sLog("📝 SOAP notes in response:", data.soap_notes);
                    sLog("💳 Credit usage in response:", data.credit_usage);
                    sLog("📝 SOAP notes type:", typeof data.soap_notes);
                    sLog(
                      "📝 SOAP notes transcript_only:",
                      data.soap_notes?.transcript_only,
                    );
                    sLog(
                      "📝 SOAP notes facts_only:",
                      data.soap_notes?.facts_only,
                    );
                    sLog(
                      "📝 SOAP notes keys:",
                      data.soap_notes ? Object.keys(data.soap_notes) : "null",
                    );

                    if (res.ok) {
                      // Set progress to 100% when complete
                      setEstimatedProgress(100);
                      setCurrentMilestone("Completed");

                      sLog(
                        "%c💳 CORTI CREDITS - RECORDED TRANSCRIPTION COMPLETED",
                        "color: gold; font-weight: bold; font-size: 14px;",
                      );
                      sLog("📊 Transcription successful");
                      sLog(
                        "📊 Transcript lines:",
                        Array.isArray(data.transcript)
                          ? data.transcript.length
                          : 0,
                      );
                      sLog(
                        "📊 Facts extracted:",
                        Array.isArray(data.facts) ? data.facts.length : 0,
                      );
                      sLog("📊 SOAP notes generated:", !!data.soap_notes);
                      sLog(
                        "📊 Credit usage:",
                        data.credit_usage || "Not available",
                      );
                      sLog(
                        "📊 Total processing time:",
                        `${Math.floor(elapsedTime / 60)}:${(elapsedTime % 60).toString().padStart(2, "0")}`,
                      );

                      // Add recorded credits immediately when transcription completes
                      if (data.credit_usage) {
                        sLog(
                          "💳 Adding recorded credits to user total:",
                          data.credit_usage,
                        );
                        addRecordedCredits(data.credit_usage).catch((error) => {
                          console.error(
                            "💳 Error adding recorded credits:",
                            error,
                          );
                        });
                        // Store credit usage for display
                        setTranscriptionCreditUsage(data.credit_usage);

                        // Log transcription to corti_logs table
                        try {
                          await cortiLogger.logTranscription({
                            patientId: selectedPatient?.id,
                            patientName: selectedPatient?.[prompts[0][0]],
                            transcriptionType: "recorded",
                            audioFileName:
                              (selectedAudioSource === "url"
                                ? selectedPatient.more_audios?.find(
                                    (a) => a.url === selectedAudioUrl,
                                  )?.name
                                : selectedAudioFile?.name) || "",
                            audioDurationSeconds: selectedAudioDuration || 0,
                            audioSource: selectedAudioSource || "",
                            audioUrl:
                              selectedAudioSource === "url"
                                ? selectedAudioUrl
                                : null,
                            creditUsage: data.credit_usage,
                            transcriptLength: Array.isArray(data.transcript)
                              ? data.transcript.join(" ").length
                              : 0,
                            factsExtracted: Array.isArray(data.facts)
                              ? data.facts.length
                              : 0,
                            soapNotesGenerated: !!data.soap_notes,
                            processingTimeSeconds: Math.floor(elapsedTime),
                            status: "completed",
                            metadata: {
                              source: "schedules_transcribe_modal_2",
                            },
                          });
                          sLog(
                            "📊 CortiLogger: Successfully logged transcription",
                          );
                        } catch (error) {
                          console.error(
                            "📊 CortiLogger: Error logging transcription:",
                            error,
                          );
                        }
                      }

                      // Show toast with model used for transcription
                      const modelUsed =
                        data.processing_method === "vosk_local" || data.processing_method === "whisper_local"
                          ? "VOSK"
                          : "Corti AI";
                      toast({
                        title: "Transcription Complete",
                        description: `Audio transcribed using ${modelUsed}`,
                        status: "success",
                        duration: 5000,
                        isClosable: true,
                      });

                      setFacts(Array.isArray(data.facts) ? data.facts : []);
                      setTranscript(
                        Array.isArray(data.transcript) ? data.transcript : [],
                      );
                      setSoapNotes(cleanSoapNotes(data.soap_notes));
                      sLog("✅ Set SOAP notes:", data.soap_notes);
                      sLog("✅ SOAP notes state set to:", data.soap_notes);

                      // Auto-save to patient record (same DB shape as manual "Save Results";
                      // credits were already applied above — skip duplicate credit add)
                      setTimeout(async () => {
                        const tid = await autoSaveTranscriptionResults(
                          Array.isArray(data.transcript) ? data.transcript : [],
                          Array.isArray(data.facts) ? data.facts : [],
                          cleanSoapNotes(data.soap_notes) || {},
                          data.credit_usage,
                          data.audio_storage_url,
                          selectedAudioSource === "file"
                            ? selectedAudioFile
                            : null,
                          selectedAudioDuration,
                          true,
                        );
                        if (tid && selectedPatient?.id)
                          await triggerAiRecapForTranscript(
                            selectedPatient.id,
                            tid,
                            data.transcript,
                          );
                      }, 100);
                    } else {
                      sLog(
                        "%c💳 CORTI CREDITS - RECORDED TRANSCRIPTION FAILED",
                        "color: gold; font-weight: bold; font-size: 14px;",
                      );
                      sLog("📊 Transcription failed with error:", data.error);
                      sLog("📊 Error type:", data.error_type);
                      sLog(
                        "📊 Total processing time:",
                        `${Math.floor(elapsedTime / 60)}:${(elapsedTime % 60).toString().padStart(2, "0")}`,
                      );

                      // Check for specific error types
                      if (data.error_type === "no_speech_detected") {
                        setFacts({ error: data.error });
                        setTranscript(null);
                        setSoapNotes(null);
                      } else {
                        setFacts({
                          error:
                            data.error ||
                            "Transcription failed. Please try again.",
                        });
                        setTranscript(null);
                        setSoapNotes(null);
                      }
                    }
                  } catch (err) {
                    console.error("Transcription error:", err);
                    sLog(
                      "%c💳 CORTI CREDITS - RECORDED TRANSCRIPTION ERROR",
                      "color: gold; font-weight: bold; font-size: 14px;",
                    );
                    sLog("📊 Network/API error:", err.message);
                    sLog(
                      "📊 Total processing time:",
                      `${Math.floor(elapsedTime / 60)}:${(elapsedTime % 60).toString().padStart(2, "0")}`,
                    );

                    setFacts({
                      error: "Transcription failed. Please try again.",
                    });
                    setTranscript(null);
                    setSoapNotes(null);
                    // Reset progress on error
                    setEstimatedProgress(0);
                    setCurrentMilestone("");
                    setMilestoneProgress(0);
                  } finally {
                    setIsLoading(false);
                    stopTimer();
                    setLastElapsedTime(elapsedTime);
                  }
                }}
                isDisabled={
                  !selectedAudioSource ||
                  isLoading ||
                  (selectedAudioSource === "url" && !selectedAudioUrl) ||
                  (selectedAudioSource === "file" && !selectedAudioFile)
                }
              >
                {isLoading
                  ? "Transcribing..."
                  : hasUploadedAudio
                    ? "Transcribe"
                    : "Upload"}
              </Button>
              <Button
                onClick={() => {
                  setIsTranscribeModalOpen(false);
                  setSelectedAudioFile(null);
                  setFacts(null);
                  setTranscript(null);
                  setSoapNotes(null);
                  setTranscriptionCreditUsage(null);
                  setSelectedAudioSource(null);
                  setSelectedAudioUrl(null);
                  setHasUploadedAudio(false);
                  setEstimatedProgress(0);
                  setCurrentMilestone("");
                  setMilestoneProgress(0);
                  stopTimer();
                }}
              >
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Transcription Model Selection Modal */}
        <Modal
          isOpen={isModelSelectionOpen}
          onClose={() => {
            setIsModelSelectionOpen(false);
            setPendingTranscribeAction(null);
          }}
          isCentered
        >
          <ModalOverlay />
          <ModalContent maxW="400px">
            <ModalHeader>Select Transcription Model</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <Text color="gray.600">
                  Choose which transcription model to use for this audio:
                </Text>
                <Box
                  p={4}
                  borderWidth={2}
                  borderRadius="md"
                  cursor={isInGroup(userRole, 'freemium') ? "not-allowed" : "pointer"}
                  onClick={() =>
                    !isInGroup(userRole, 'freemium') &&
                    setSelectedTranscriptionModel("corti")
                  }
                  bg={
                    selectedTranscriptionModel === "corti"
                      ? "blue.50"
                      : "gray.50"
                  }
                  borderColor={
                    selectedTranscriptionModel === "corti"
                      ? "blue.500"
                      : "gray.200"
                  }
                  _hover={
                    !isInGroup(userRole, 'freemium') ? { borderColor: "blue.300" } : {}
                  }
                  opacity={isInGroup(userRole, 'freemium') ? 0.45 : 1}
                  filter={
                    isInGroup(userRole, 'freemium') ? "grayscale(70%)" : undefined
                  }
                >
                  <HStack justify="space-between">
                    <VStack align="start" spacing={1}>
                      <Text
                        fontWeight="bold"
                        fontSize="lg"
                        color={
                          isInGroup(userRole, 'freemium') ? "gray.400" : "inherit"
                        }
                      >
                        Corti
                      </Text>
                      <Text
                        fontSize="sm"
                        color={
                          isInGroup(userRole, 'freemium') ? "gray.400" : "gray.600"
                        }
                      >
                        {isInGroup(userRole, 'freemium')
                          ? "Not available for your account — use VOSK"
                          : "AI-powered medical transcription with fact extraction"}
                      </Text>
                    </VStack>
                    {selectedTranscriptionModel === "corti" && (
                      <Text color="blue.500" fontWeight="bold">
                        ✓
                      </Text>
                    )}
                  </HStack>
                </Box>
                <Box
                  p={4}
                  borderWidth={2}
                  borderRadius="md"
                  cursor="pointer"
                  onClick={() => setSelectedTranscriptionModel("vosk")}
                  bg={
                    selectedTranscriptionModel === "vosk"
                      ? "blue.50"
                      : "gray.50"
                  }
                  borderColor={
                    selectedTranscriptionModel === "vosk"
                      ? "blue.500"
                      : "gray.200"
                  }
                  _hover={{ borderColor: "blue.300" }}
                >
                  <HStack justify="space-between">
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="bold" fontSize="lg">
                        VOSK
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        VOSK local model
                      </Text>
                    </VStack>
                    {selectedTranscriptionModel === "vosk" && (
                      <Text color="blue.500" fontWeight="bold">
                        ✓
                      </Text>
                    )}
                  </HStack>
                </Box>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="outline"
                mr={3}
                onClick={() => {
                  setIsModelSelectionOpen(false);
                  setPendingTranscribeAction(null);
                }}
              >
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={() => {
                  // Force VOSK for freemium users regardless of selection
                  const model = isInGroup(userRole, 'freemium')
                    ? "vosk"
                    : selectedTranscriptionModel;
                  const action = pendingTranscribeAction;
                  setSelectedTranscriptionModel(model);
                  setIsModelSelectionOpen(false);

                  // VOSK is now implemented - no need for the info message anymore
                  console.log("Selected transcription model:", model);

                  if (
                    action?.type === "patient_audio" ||
                    action?.type === "modal_transcribe"
                  ) {
                    // Audio source is already set on the transcribe modal.
                    // Trigger the actual transcription flow by clicking the
                    // underlying Transcribe button once the selector closes.
                    setTimeout(() => {
                      transcribeButtonRef.current?.click();
                    }, 50);
                  }
                  setPendingTranscribeAction(null);
                }}
              >
                Continue with{" "}
                {isInGroup(userRole, 'freemium')
                  ? "VOSK"
                  : selectedTranscriptionModel === "corti"
                    ? "Corti"
                    : "VOSK"}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* View Notes Modal */}
        <Modal
          isOpen={isViewNotesOpen}
          onClose={() => setIsViewNotesOpen(false)}
          size="6xl"
        >
          <ModalOverlay />
          <ModalContent maxH="90vh">
            <ModalHeader bg="blue.600" color="white">
              Medical Report Transcription
            </ModalHeader>
            <ModalCloseButton color="white" />
            <ModalBody pb={6} overflowY="auto">
              <VStack spacing={6} align="stretch">
                {/* Patient Information Header */}
                <Box bg="gray.50" p={4} borderRadius="md" borderWidth={1}>
                  <Heading size="md" mb={3} color="blue.700">
                    Patient Information
                  </Heading>
                  <VStack align="stretch" spacing={2}>
                    <HStack>
                      <Text fontWeight="bold" minW="100px">
                        Patient:
                      </Text>
                      <Text>
                        {selectedPatient?.[prompts[0][0]] || "Unknown"}
                      </Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="bold" minW="100px">
                        Date:
                      </Text>
                      <Text>{new Date().toLocaleDateString()}</Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="bold" minW="100px">
                        Duration:
                      </Text>
                      <Text>
                        {selectedAudioDuration
                          ? `${Math.floor(selectedAudioDuration / 60)}:${String(Math.floor(selectedAudioDuration % 60)).padStart(2, "0")}`
                          : "0:00"}
                      </Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="bold" minW="100px">
                        Model:
                      </Text>
                      <Text color="blue.600" fontWeight="semibold">
                        {selectedTranscriptionModel === "corti"
                          ? "Corti AI"
                          : "VOSK"}
                      </Text>
                    </HStack>
                  </VStack>
                </Box>

                {/* Transcript Section */}
                {transcript && (
                  <Box>
                    <Heading
                      size="md"
                      mb={3}
                      color="blue.700"
                      borderBottom="2px solid"
                      borderColor="blue.200"
                      pb={2}
                    >
                      Transcript
                    </Heading>
                    <Box
                      p={4}
                      bg="white"
                      borderWidth={1}
                      borderRadius="md"
                      maxH="400px"
                      overflowY="auto"
                    >
                      {Array.isArray(transcript) ? (
                        transcript.map((line, index) => (
                          <Text key={index} mb={3} lineHeight="1.6">
                            {line}
                          </Text>
                        ))
                      ) : (
                        <Text lineHeight="1.6">{transcript}</Text>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Extracted Facts Section */}
                {facts && (
                  <Box>
                    <Heading
                      size="md"
                      mb={3}
                      color="blue.700"
                      borderBottom="2px solid"
                      borderColor="blue.200"
                      pb={2}
                    >
                      Extracted Facts
                    </Heading>
                    <Box
                      p={4}
                      bg="white"
                      borderWidth={1}
                      borderRadius="md"
                      maxH="300px"
                      overflowY="auto"
                    >
                      {Array.isArray(facts) ? (
                        <VStack align="stretch" spacing={3}>
                          {facts.map((fact, index) => (
                            <Box
                              key={index}
                              p={3}
                              bg="blue.50"
                              borderRadius="md"
                              borderLeftWidth={4}
                              borderLeftColor="blue.400"
                            >
                              <Text fontWeight="medium" mb={1}>
                                {fact.text}
                              </Text>
                              <Text fontSize="sm" color="blue.600">
                                Category: {fact.group || "N/A"}
                              </Text>
                            </Box>
                          ))}
                        </VStack>
                      ) : (
                        <Box p={3} bg="blue.50" borderRadius="md">
                          <Text>{JSON.stringify(facts)}</Text>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                {/* SOAP Notes Section */}
                {soapNotes &&
                  (soapNotes.transcript_only || soapNotes.facts_only) && (
                    <Box>
                      <Heading
                        size="md"
                        mb={3}
                        color="blue.700"
                        borderBottom="2px solid"
                        borderColor="blue.200"
                        pb={2}
                      >
                        SOAP Notes
                      </Heading>

                      {soapNotes.transcript_only && (
                        <Box mb={4}>
                          <Text
                            fontSize="md"
                            fontWeight="semibold"
                            mb={2}
                            color="gray.700"
                          >
                            Based on Transcript
                          </Text>
                          <Box
                            p={4}
                            bg="white"
                            borderWidth={1}
                            borderRadius="md"
                            maxH="300px"
                            overflowY="auto"
                          >
                            <Text whiteSpace="pre-wrap" lineHeight="1.6">
                              {soapNotes.transcript_only}
                            </Text>
                          </Box>
                        </Box>
                      )}

                      {soapNotes.facts_only && (
                        <Box>
                          <Text
                            fontSize="md"
                            fontWeight="semibold"
                            mb={2}
                            color="gray.700"
                          >
                            Based on Facts
                          </Text>
                          <Box
                            p={4}
                            bg="white"
                            borderWidth={1}
                            borderRadius="md"
                            maxH="300px"
                            overflowY="auto"
                          >
                            <Text whiteSpace="pre-wrap" lineHeight="1.6">
                              {soapNotes.facts_only}
                            </Text>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
              </VStack>
            </ModalBody>
            <ModalFooter bg="gray.50">
              <Button
                colorScheme="blue"
                onClick={() => setIsViewNotesOpen(false)}
              >
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <Modal
          isOpen={isLiveTranscribeModalOpen}
          onClose={() => {
            setIsLiveTranscribeModalOpen(false);
            setIsLiveRecording(false);
            setLiveTranscript("");
            setLiveFacts([]);
            if (
              liveMediaRecorderRef.current &&
              liveMediaRecorderRef.current.state !== "inactive"
            ) {
              liveMediaRecorderRef.current.stop();
            }
          }}
        >
          <ModalOverlay />
          <ModalContent maxW="600px">
            <ModalHeader>Live Transcription</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Button
                colorScheme={isLiveRecording ? "red" : "teal"}
                onClick={isLiveRecording ? handleStopLive : handleStartLive}
                mb={4}
              >
                {isLiveRecording
                  ? "Stop Live Transcription"
                  : "Start Live Transcription"}
              </Button>
              <Box mt={4}>
                <Text fontWeight="bold">Transcript:</Text>
                <Box bg="gray.100" p={3} borderRadius={8} minH={60}>
                  {liveTranscript}
                </Box>
                <Text fontWeight="bold" mt={4}>
                  Facts:
                </Text>
                <ul>
                  {liveFacts.map((f) => (
                    <li key={f.id || f.text}>{f.text}</li>
                  ))}
                </ul>
              </Box>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Phone Audio Modal */}
        <Modal
          isOpen={isPhoneAudioModalOpen}
          onClose={() => {
            sLog("📞 Phone Audio Modal closing");
            sLog("📞 Selected patient on close:", selectedPatient?.id);
            sLog(
              "📞 Selected patient name on close:",
              selectedPatient?.[prompts[0][0]],
            );

            // Stop any active recording and clean up
            if (
              phoneMediaRecorderRef.current &&
              phoneMediaRecorderRef.current.state === "recording"
            ) {
              sLog("📞 Stopping active recording before closing modal");
              phoneMediaRecorderRef.current.stop();
            }

            // Stop all stream tracks
            if (phoneStreamRef.current) {
              sLog("📞 Stopping all stream tracks");
              phoneStreamRef.current
                .getTracks()
                .forEach((track) => track.stop());
              phoneStreamRef.current = null;
            }

            // Reset MediaRecorder
            if (phoneMediaRecorderRef.current) {
              phoneMediaRecorderRef.current = null;
            }

            // Set a flag to prevent audio saving when modal is closed
            phoneChunksRef.current = null;
            phoneModalClosedRef.current = true;

            setIsPhoneAudioModalOpen(false);
            setSelectedAudioFile(null);
            setFacts(null);
            setTranscript(null);
            setSoapNotes(null);
            setSelectedAudioSource(null);
            setSelectedAudioUrl(null);
            setPhoneRecordingState("idle");
            setPhoneAudioURL("");
            setPhoneAudioChunks([]);
            phoneChunksRef.current = [];
            stopTimer();
          }}
        >
          <ModalOverlay />
          <ModalContent maxW="800px" maxH="90vh" position="relative">
            <ModalHeader>Phone Audio</ModalHeader>
            <ModalCloseButton />

            {/* Timer Display - positioned in top right corner */}
            {elapsedTime > 0 && (
              <Box
                position="absolute"
                top="10px"
                right="60px"
                bg="orange.500"
                color="white"
                px={4}
                py={2}
                borderRadius="lg"
                boxShadow="xl"
                zIndex={10}
                minWidth="140px"
              >
                <Text fontSize="xl" fontWeight="bold" textAlign="center">
                  ⏱️ {Math.floor(elapsedTime / 60)}:
                  {(elapsedTime % 60).toString().padStart(2, "0")}
                </Text>
                <Text fontSize="sm" fontWeight="bold" textAlign="center">
                  📊 {estimatedProgress}%
                </Text>
                <Text fontSize="xs" textAlign="center" opacity={0.9}>
                  {isLoading
                    ? currentMilestone || "Transcribing..."
                    : currentMilestone === "Completed"
                      ? "Completed"
                      : "Ready"}
                </Text>
              </Box>
            )}

            <ModalBody maxH="70vh" overflowY="auto">
              {sLog(
                "📞 Phone Audio Modal Body - Selected patient:",
                selectedPatient?.id,
              )}
              {sLog(
                "📞 Phone Audio Modal Body - Selected patient name:",
                selectedPatient?.[prompts[0][0]],
              )}
              {sLog(
                "📞 Phone Audio Modal Body - Auto-save setting:",
                autoSaveSetting,
              )}
              <VStack spacing={4} align="stretch">
                {/* File Upload Option */}
                <Box>
                  <Heading size="md" mb={3}>
                    Upload Audio File
                  </Heading>
                  <Input
                    type="file"
                    accept="audio/*,.mpeg,.mpg,.m4a,.m4b,.aac,.mov,.mp4,.3gp,.amr,.ogg,.flac,.caf,.3gpp,.wma,.wav,.mp3,.webm"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      setSelectedAudioSource("file");
                      setSelectedAudioFile(file);
                      setSelectedAudioUrl(null);

                      // Get audio duration from uploaded file
                      if (file) {
                        const audio = new Audio();
                        audio.src = URL.createObjectURL(file);
                        audio.addEventListener("loadedmetadata", () => {
                          setSelectedAudioDuration(audio.duration);
                        });
                      }
                    }}
                  />
                  {selectedAudioFile && (
                    <Text mt={2} color="green.600">
                      Selected: {selectedAudioFile.name}
                    </Text>
                  )}
                  <HStack mt={2} spacing={2}>
                    <Text fontSize="xs" color="gray.500">
                      Want to transcribe right away in the browser?
                    </Text>
                    <AudioUploader
                      modalTitle="Transcribe audio with Vosk"
                      buttonLabel="Quick Transcribe (Vosk)"
                      autoCloseOnSuccess={false}
                      onTranscript={(text, meta) => {
                        if (!text) return;
                        setSelectedAudioSource("file");
                        // best-effort: keep the file selection if there was one,
                        // otherwise populate a pseudo file name from the meta
                        if (!selectedAudioFile && meta?.fileName) {
                          // The AudioUploader already validated the file; we
                          // can't reconstruct the File object here, so just
                          // surface the transcript to the existing display.
                        }
                        const transcriptLines = text
                          .split(/(?<=[.!?])\s+|\n+/)
                          .map((s) => s.trim())
                          .filter(Boolean);
                        setFacts([]);
                        setTranscript(
                          transcriptLines.length ? transcriptLines : [text],
                        );
                        setSoapNotes(null);
                        setEstimatedProgress(100);
                        setCurrentMilestone(
                          `Transcribed in browser via Vosk (${meta?.serviceOrigin || VOSK_SERVICE_ORIGIN})`,
                        );
                        toast({
                          title: "Vosk Transcription Complete",
                          description: `${text.length} characters from ${meta?.fileName || "audio"}`,
                          status: "success",
                          duration: 4000,
                          isClosable: true,
                        });
                        setTimeout(async () => {
                          const tid = await autoSaveTranscriptionResults(
                            transcriptLines.length ? transcriptLines : [text],
                            [],
                            {},
                            0,
                            null,
                            selectedAudioFile || null,
                            meta?.durationSeconds || 0,
                            true,
                          );
                          if (tid && selectedPatient?.id)
                            await triggerAiRecapForTranscript(
                              selectedPatient.id,
                              tid,
                              transcriptLines,
                            );
                        }, 100);
                      }}
                    />
                  </HStack>
                </Box>

                {/* Start Recording Section */}
                <Box>
                  <Heading size="md" mb={3}>
                    Record Audio
                  </Heading>
                  <VStack spacing={3} align="stretch">
                    {/* Auto-save Checkbox */}
                    <Box>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                        }}
                      >
                        <Checkbox
                          isChecked={autoSaveSetting}
                          onChange={(e) =>
                            handleAutoSaveChange(e.target.checked)
                          }
                          mr={2}
                        />
                        <Text fontSize="sm">
                          Auto-save recording transcript afterwards
                        </Text>
                      </label>
                    </Box>

                    {/* Include system/meeting audio */}
                    <Box>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                        }}
                      >
                        <Checkbox
                          isChecked={phoneIncludeSystemAudio}
                          onChange={(e) =>
                            setPhoneIncludeSystemAudio(e.target.checked)
                          }
                          mr={2}
                        />
                        <Text fontSize="sm">
                          Include system/meeting audio (when sharing, check
                          &quot;Also share tab audio&quot; to capture others in
                          calls)
                        </Text>
                      </label>
                    </Box>

                    {/* Recording Controls */}
                    <HStack spacing={3}>
                      {phoneRecordingState === "idle" && (
                        <Button
                          colorScheme="green"
                          onClick={startPhoneRecording}
                          leftIcon={<FontAwesomeIcon icon={faPhone} />}
                        >
                          Start Recording
                        </Button>
                      )}

                      {phoneRecordingState === "recording" && (
                        <>
                          <Button
                            colorScheme="orange"
                            onClick={pausePhoneRecording}
                            leftIcon={<FontAwesomeIcon icon={faPhone} />}
                          >
                            Pause Recording
                          </Button>
                          <Button
                            colorScheme="green"
                            onClick={stopPhoneRecording}
                            leftIcon={<FontAwesomeIcon icon={faPhone} />}
                          >
                            Stop and Save
                          </Button>
                        </>
                      )}

                      {phoneRecordingState === "paused" && (
                        <>
                          <Button
                            colorScheme="green"
                            onClick={resumePhoneRecording}
                            leftIcon={<FontAwesomeIcon icon={faPhone} />}
                          >
                            Resume Recording
                          </Button>
                          <Button
                            colorScheme="green"
                            onClick={stopPhoneRecording}
                            leftIcon={<FontAwesomeIcon icon={faPhone} />}
                          >
                            Stop Recording
                          </Button>
                        </>
                      )}
                    </HStack>

                    {/* Recording Status */}
                    {phoneRecordingState === "recording" && (
                      <Box
                        p={3}
                        bg="green.50"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="red.200"
                      >
                        <Text
                          color="green.600"
                          fontWeight="bold"
                          textAlign="center"
                        >
                          🟢 Recording in progress...
                        </Text>
                      </Box>
                    )}

                    {phoneRecordingState === "paused" && (
                      <Box
                        p={3}
                        bg="orange.50"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="orange.200"
                      >
                        <Text
                          color="orange.600"
                          fontWeight="bold"
                          textAlign="center"
                        >
                          ⏸️ Recording paused
                        </Text>
                      </Box>
                    )}

                    {phoneRecordingState === "stopped" && phoneAudioURL && (
                      <Box
                        p={3}
                        bg="green.50"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="green.200"
                      >
                        <Text
                          color="green.600"
                          fontWeight="bold"
                          textAlign="center"
                          mb={2}
                        >
                          ✅ Recording completed!
                        </Text>
                        <audio
                          controls
                          src={phoneAudioURL}
                          style={{ width: "100%" }}
                        />
                      </Box>
                    )}
                  </VStack>
                </Box>

                {/* Progress bar when loading */}
                {isLoading && (
                  <Box>
                    <Progress
                      size="md"
                      isIndeterminate
                      colorScheme="orange"
                      mb={2}
                    />
                  </Box>
                )}

                {transcript && (
                  <Box>
                    {/* Credit Usage Display */}
                    {transcriptionCreditUsage && (
                      <Box
                        mb={4}
                        p={3}
                        bg="blue.50"
                        borderRadius="md"
                        borderWidth={1}
                        borderColor="blue.200"
                      >
                        <HStack justify="space-between" align="center">
                          <Text fontWeight="bold" color="blue.700">
                            💳 Credits Used:{" "}
                            {transcriptionCreditUsage.toFixed(4)}
                          </Text>
                          <Text fontSize="sm" color="blue.600">
                            Phone Audio Transcription
                          </Text>
                        </HStack>
                      </Box>
                    )}

                    {/* Action buttons at the top */}
                    <HStack spacing={3} mb={4} justify="flex-end">
                      <Button colorScheme="blue" onClick={generatePDF}>
                        Export to PDF
                      </Button>
                      {/* Only show Save Results button if auto-save is disabled */}
                      {!capturedAutoSaveSettingRef.current && (
                        <Button
                          colorScheme="green"
                          onClick={async () => {
                            if (
                              !selectedPatient ||
                              !transcript ||
                              !facts ||
                              !soapNotes
                            ) {
                              toast({
                                title: "No Data to Save",
                                description:
                                  "Please complete a transcription first.",
                                status: "warning",
                                duration: 5000,
                                isClosable: true,
                              });
                              return;
                            }

                            try {
                              sLog(
                                "%c💳 CORTI CREDITS - SAVING TRANSCRIPTION RESULTS",
                                "color: gold; font-weight: bold; font-size: 14px;",
                              );
                              sLog(
                                "📊 Saving transcription for patient:",
                                selectedPatient?.[prompts[0][0]],
                              );
                              sLog("📊 Patient ID:", selectedPatient?.id);

                              const supabase = await getSupabase();

                              // First, get existing transcriptions
                              const { data: existingData, error: fetchError } =
                                await supabase
                                  .from(tableName)
                                  .select("transcripts")
                                  .eq("id", selectedPatient.id)
                                  .single();

                              if (
                                fetchError &&
                                fetchError.code !== "PGRST116"
                              ) {
                                // PGRST116 is "not found"
                                throw fetchError;
                              }

                              // Create new transcription data
                              const newTranscription = {
                                id: Date.now().toString(), // Generate unique ID
                                patient_id:
                                  selectedPatient.id?.toString() || "",
                                name: (
                                  selectedPatient[prompts[0][0]] || ""
                                ).toString(),
                                transcript: Array.isArray(transcript)
                                  ? transcript
                                  : [transcript],
                                facts: Array.isArray(facts) ? facts : [facts],
                                soap_notes: soapNotes || {},
                                audio_duration:
                                  phoneAudioDurationRef.current ||
                                  selectedAudioDuration ||
                                  0,
                                audio_source: selectedAudioSource || "",
                                audio_file_name: selectedAudioFile?.name || "",
                                estimated_credit_usage:
                                  transcriptionCreditUsage || null,
                                created_at: new Date().toISOString(),
                              };

                              // Prepare the updated transcripts array
                              let updatedTranscripts = [];
                              if (existingData && existingData.transcripts) {
                                // If existing data is an array, use it; otherwise, convert single object to array
                                if (Array.isArray(existingData.transcripts)) {
                                  updatedTranscripts = [
                                    ...existingData.transcripts,
                                    newTranscription,
                                  ];
                                } else {
                                  // Convert single transcription object to array and add new one
                                  updatedTranscripts = [
                                    existingData.transcripts,
                                    newTranscription,
                                  ];
                                }
                              } else {
                                // No existing transcriptions, start with new one
                                updatedTranscripts = [newTranscription];
                              }

                              // Log before update
                              sLog(
                                "Updating patient record with id:",
                                selectedPatient.id,
                              );
                              sLog(
                                "Updated transcripts array:",
                                updatedTranscripts,
                              );

                              // Update the transcripts column with the array
                              const { data, error } = await supabase
                                .from(tableName)
                                .update({ transcripts: updatedTranscripts })
                                .eq("id", selectedPatient.id);

                              if (error) {
                                console.error(
                                  "Error saving transcription:",
                                  error,
                                );
                                throw error;
                              }

                              sLog("✅ Transcription saved successfully");
                              // Credits are already added when transcription completes
                              // No need to add them again here
                              toast({
                                title: "Transcription Saved!",
                                description:
                                  "Transcription results have been saved to the patient record.",
                                status: "success",
                                duration: 5000,
                                isClosable: true,
                              });
                            } catch (error) {
                              console.error(
                                "Error saving transcription:",
                                error,
                              );
                              toast({
                                title: "Save Failed",
                                description:
                                  "Failed to save transcription results. Please try again.",
                                status: "error",
                                duration: 5000,
                                isClosable: true,
                              });
                            }
                          }}
                        >
                          Save to Patient Record
                        </Button>
                      )}
                    </HStack>

                    {/* Transcript Display */}
                    <Box mb={4}>
                      <Heading size="md" mb={2}>
                        Transcript
                      </Heading>
                      <Box
                        bg="gray.100"
                        p={3}
                        borderRadius={8}
                        maxH="200px"
                        overflowY="auto"
                      >
                        {Array.isArray(transcript) ? (
                          transcript.map((line, index) => (
                            <Text key={index} mb={1}>
                              {line}
                            </Text>
                          ))
                        ) : (
                          <Text>{transcript}</Text>
                        )}
                      </Box>
                    </Box>

                    {/* Facts Display */}
                    {facts && (
                      <Box mb={4}>
                        <Heading size="md" mb={2}>
                          Facts
                        </Heading>
                        <Box
                          bg="blue.50"
                          p={3}
                          borderRadius={8}
                          maxH="200px"
                          overflowY="auto"
                        >
                          {Array.isArray(facts) ? (
                            facts.map((fact, index) => (
                              <Text key={index} mb={1}>
                                • {fact.text || fact}
                              </Text>
                            ))
                          ) : (
                            <Text>{facts}</Text>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* SOAP Notes Display */}
                    {soapNotes && (
                      <Box>
                        <Heading size="md" mb={2}>
                          SOAP Notes
                        </Heading>
                        <Box
                          bg="green.50"
                          p={3}
                          borderRadius={8}
                          maxH="200px"
                          overflowY="auto"
                        >
                          {soapNotes.transcript_only && (
                            <Box mb={3}>
                              <Text fontWeight="bold" mb={1}>
                                Transcript Only:
                              </Text>
                              <Text>{soapNotes.transcript_only}</Text>
                            </Box>
                          )}
                          {soapNotes.facts_only && (
                            <Box>
                              <Text fontWeight="bold" mb={1}>
                                Facts Only:
                              </Text>
                              <Text>{soapNotes.facts_only}</Text>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Error Display */}
                {facts && facts.error && (
                  <Alert status="error">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>Transcription Error</AlertTitle>
                      <AlertDescription>{facts.error}</AlertDescription>
                    </Box>
                  </Alert>
                )}
              </VStack>
            </ModalBody>
            <ModalFooter>
              {!capturedAutoSaveSettingRef.current && (
                <Button
                  colorScheme="purple"
                  onClick={async () => {
                    if (!selectedAudioSource || isLoading) return;

                    // Check credit limit before starting transcription
                    const creditLimitExceeded =
                      await checkCreditLimitAndUpdateUI(toast);
                    if (creditLimitExceeded) {
                      sLog(
                        "📞 Phone Audio: Credit limit exceeded, cannot transcribe",
                      );
                      return;
                    }

                    setIsLoading(true);
                    startTimer();
                    setEstimatedProgress(0);
                    setCurrentMilestone("Starting transcription...");
                    setMilestoneProgress(0);
                    setTranscriptionCreditUsage(null);

                    try {
                      sLog(
                        "📞 Phone Audio: Starting transcription using REST API...",
                      );
                      sLog(
                        "%c💳 CORTI CREDITS - PHONE AUDIO TRANSCRIPTION STARTED",
                        "color: gold; font-weight: bold; font-size: 14px;",
                      );
                      sLog(
                        "📞 Phone Audio: Audio source:",
                        selectedAudioSource,
                      );
                      sLog(
                        "📞 Phone Audio: Audio duration:",
                        selectedAudioDuration,
                      );
                      sLog(
                        "📞 Phone Audio: Patient:",
                        selectedPatient?.[prompts[0][0]],
                      );

                      const hostname = window.location.hostname;
                      const baseUrl =
                        hostname === "localhost"
                          ? BASE_URLS.local.backend
                          : BASE_URLS.render.backend;
                      const formData = new FormData();

                      // Add transcription model selection
                      formData.append(
                        "transcription_model",
                        selectedTranscriptionModel,
                      );
                      console.log(
                        "📊 Using transcription model:",
                        selectedTranscriptionModel,
                      );

                      // Log audio file details for debugging
                      sLog("📞 Phone Audio: Audio file details:", {
                        name: selectedAudioFile?.name,
                        size: selectedAudioFile?.size,
                        type: selectedAudioFile?.type,
                        lastModified: selectedAudioFile?.lastModified,
                      });

                      if (selectedAudioSource === "url") {
                        sLog(
                          "📞 Phone Audio: Sending audio URL to backend:",
                          selectedAudioUrl,
                        );
                        const urlBlob = new Blob([selectedAudioUrl], {
                          type: "text/plain",
                        });
                        formData.append("audio_file", urlBlob, "audio_url.txt");
                      } else {
                        // Send actual file
                        sLog(
                          "📞 Phone Audio: Sending audio file to backend:",
                          selectedAudioFile.name,
                        );
                        sLog("📞 Phone Audio: File details:", {
                          name: selectedAudioFile.name,
                          size: selectedAudioFile.size,
                          type: selectedAudioFile.type,
                        });
                        formData.append("audio_file", selectedAudioFile);
                      }

                      sLog(
                        "📞 Phone Audio: Sending request to:",
                        `${baseUrl}/corti/transcribe-recorded/`,
                      );

                      const res = await fetch(
                        `${baseUrl}/corti/transcribe-recorded/`,
                        {
                          method: "POST",
                          body: formData,
                          credentials: "include",
                          headers: {
                            "X-CSRFToken":
                              document.cookie
                                .split("csrftoken=")[1]
                                ?.split(";")[0] || "",
                          },
                        },
                      );

                      // Phone Audio Modal - Handle response
                      sLog("📞 Phone Audio: Response status:", res.status);

                      if (!res.ok) {
                        console.error(
                          "📞 Phone Audio: Upload failed with status:",
                          res.status,
                        );
                        let errorMessage = `Failed to upload recording: ${res.status} ${res.statusText}`;

                        try {
                          const errorText = await res.text();
                          console.error(
                            "📞 Phone Audio: Error response:",
                            errorText,
                          );
                          // Try to parse as JSON for more detailed error
                          try {
                            const errorData = JSON.parse(errorText);
                            errorMessage = errorData.error || errorMessage;
                          } catch (parseError) {
                            // If not JSON, use the text as is
                            errorMessage = errorText || errorMessage;
                          }
                        } catch (textError) {
                          console.error(
                            "📞 Phone Audio: Could not read error response:",
                            textError,
                          );
                        }

                        setFacts({ error: errorMessage });
                        setTranscript(null);
                        setSoapNotes(null);
                        return;
                      }

                      const data = await res.json();
                      sLog("📞 Phone Audio: Received data:", data);
                      sLog("📝 SOAP notes in response:", data.soap_notes);
                      sLog("💳 Credit usage in response:", data.credit_usage);
                      sLog("📝 SOAP notes type:", typeof data.soap_notes);
                      sLog(
                        "📝 SOAP notes transcript_only:",
                        data.soap_notes?.transcript_only,
                      );
                      sLog(
                        "📝 SOAP notes facts_only:",
                        data.soap_notes?.facts_only,
                      );
                      sLog(
                        "📝 SOAP notes keys:",
                        data.soap_notes ? Object.keys(data.soap_notes) : "null",
                      );

                      if (res.ok) {
                        // Set progress to 100% when complete
                        setEstimatedProgress(100);
                        setCurrentMilestone("Completed");

                        sLog(
                          "%c💳 CORTI CREDITS - RECORDED TRANSCRIPTION COMPLETED",
                          "color: gold; font-weight: bold; font-size: 14px;",
                        );
                        sLog("📊 Transcription successful");
                        sLog(
                          "📊 Transcript lines:",
                          Array.isArray(data.transcript)
                            ? data.transcript.length
                            : 0,
                        );
                        sLog(
                          "📊 Facts extracted:",
                          Array.isArray(data.facts) ? data.facts.length : 0,
                        );
                        sLog("📊 SOAP notes generated:", !!data.soap_notes);
                        sLog(
                          "📊 Credit usage:",
                          data.credit_usage || "Not available",
                        );
                        sLog(
                          "📊 Total processing time:",
                          `${Math.floor(elapsedTime / 60)}:${(elapsedTime % 60).toString().padStart(2, "0")}`,
                        );

                        // Show toast with model used for transcription
                        const modelUsed =
                          data.processing_method === "vosk_local" || data.processing_method === "whisper_local"
                            ? "VOSK"
                            : "Corti AI";
                        toast({
                          title: "Transcription Complete",
                          description: `Audio transcribed using ${modelUsed}`,
                          status: "success",
                          duration: 5000,
                          isClosable: true,
                        });

                        // Add recorded credits immediately when transcription completes
                        if (data.credit_usage) {
                          sLog(
                            "💳 Adding recorded credits to user total:",
                            data.credit_usage,
                          );
                          addRecordedCredits(data.credit_usage).catch(
                            (error) => {
                              console.error(
                                "💳 Error adding recorded credits:",
                                error,
                              );
                            },
                          );
                          // Store credit usage for display
                          setTranscriptionCreditUsage(data.credit_usage);

                          // Log transcription to corti_logs table (PHONE_AUDIO_SECTION)
                          try {
                            await cortiLogger.logTranscription({
                              patientId: selectedPatient?.id,
                              patientName: selectedPatient?.[prompts[0][0]],
                              transcriptionType: "recorded",
                              audioFileName: selectedAudioFile?.name || "",
                              audioDurationSeconds: selectedAudioDuration || 0,
                              audioSource: "phone_audio",
                              audioUrl: null,
                              creditUsage: data.credit_usage,
                              transcriptLength: Array.isArray(data.transcript)
                                ? data.transcript.join(" ").length
                                : 0,
                              factsExtracted: Array.isArray(data.facts)
                                ? data.facts.length
                                : 0,
                              soapNotesGenerated: !!data.soap_notes,
                              processingTimeSeconds: Math.floor(elapsedTime),
                              status: "completed",
                              metadata: {
                                source: "schedules_phone_audio_modal",
                              },
                            });
                            sLog(
                              "📊 CortiLogger: Successfully logged phone audio transcription",
                            );
                          } catch (error) {
                            console.error(
                              "📊 CortiLogger: Error logging phone audio transcription:",
                              error,
                            );
                          }
                        }

                        setFacts(Array.isArray(data.facts) ? data.facts : []);
                        setTranscript(
                          Array.isArray(data.transcript) ? data.transcript : [],
                        );
                        setSoapNotes(cleanSoapNotes(data.soap_notes));
                        sLog("✅ Set SOAP notes:", data.soap_notes);
                        sLog("✅ SOAP notes state set to:", data.soap_notes);
                      } else {
                        sLog(
                          "%c💳 CORTI CREDITS - RECORDED TRANSCRIPTION FAILED",
                          "color: gold; font-weight: bold; font-size: 14px;",
                        );
                        sLog("📊 Transcription failed with error:", data.error);
                        sLog("📊 Error type:", data.error_type);
                        sLog(
                          "📊 Total processing time:",
                          `${Math.floor(elapsedTime / 60)}:${(elapsedTime % 60).toString().padStart(2, "0")}`,
                        );

                        // Check for specific error types
                        if (data.error_type === "no_speech_detected") {
                          setFacts({ error: data.error });
                          setTranscript(null);
                          setSoapNotes(null);
                        } else {
                          setFacts({
                            error:
                              data.error ||
                              "Transcription failed. Please try again.",
                          });
                          setTranscript(null);
                          setSoapNotes(null);
                        }
                      }
                    } catch (err) {
                      console.error("Transcription error:", err);
                      sLog(
                        "%c💳 CORTI CREDITS - RECORDED TRANSCRIPTION ERROR",
                        "color: gold; font-weight: bold; font-size: 14px;",
                      );
                      sLog("📊 Network/API error:", err.message);
                      sLog(
                        "📊 Total processing time:",
                        `${Math.floor(elapsedTime / 60)}:${(elapsedTime % 60).toString().padStart(2, "0")}`,
                      );

                      setFacts({
                        error: "Transcription failed. Please try again.",
                      });
                      setTranscript(null);
                      setSoapNotes(null);
                      // Reset progress on error
                      setEstimatedProgress(0);
                      setCurrentMilestone("");
                      setMilestoneProgress(0);
                    } finally {
                      setIsLoading(false);
                      stopTimer();
                      setLastElapsedTime(elapsedTime);
                    }
                  }}
                  isDisabled={
                    !selectedAudioSource ||
                    isLoading ||
                    (selectedAudioSource === "url" && !selectedAudioUrl) ||
                    (selectedAudioSource === "file" && !selectedAudioFile)
                  }
                >
                  {isLoading ? "Transcribing..." : "Transcribe"}
                </Button>
              )}
              <Button
                onClick={() => {
                  setIsPhoneAudioModalOpen(false);
                  setSelectedAudioFile(null);
                  setFacts(null);
                  setTranscript(null);
                  setSoapNotes(null);
                  setTranscriptionCreditUsage(null);
                  setSelectedAudioSource(null);
                  setSelectedAudioUrl(null);
                  setPhoneRecordingState("idle");
                  setPhoneAudioURL("");
                  setPhoneAudioChunks([]);
                  phoneChunksRef.current = [];
                  setEstimatedProgress(0);
                  setCurrentMilestone("");
                  setMilestoneProgress(0);
                  stopTimer();
                }}
              >
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Password Modal */}
        <PasswordModal
          isOpen={isPasswordModalOpen}
          onClose={() => {
            setIsPasswordModalOpen(false);
            setPendingAction(null);
          }}
          onSuccess={() => {
            if (pendingAction) {
              pendingAction();
              setPendingAction(null);
            }
          }}
          title="Enable Transcription Features"
        />

        {!isPatientView && selectedPatient?.id && (
          <AudioManagerModal
            isOpen={isAudioManagerModalOpen}
            onClose={() => setIsAudioManagerModalOpen(false)}
            patientData={{
              ...selectedPatient,
              more_audios: selectedVisitAudios,
            }}
            patientId={selectedPatient.id}
            onRestoreFromBackupSuccess={refreshSelectedPatientFromDb}
          />
        )}
        <Box
          as="footer"
          mt={6}
          py={3}
          px={2}
          width="100%"
          borderTopWidth="1px"
          borderColor="gray.200"
        >
          <VStack spacing={0.5} align="center">
            <Text
              fontSize="xs"
              color="gray.600"
              textAlign="center"
              fontFamily="mono"
            >
              Version: {schedulesBuildInfo.version}
            </Text>
            <Tooltip
              label={schedulesBuildInfo.commitFull}
              isDisabled={schedulesBuildInfo.commitFull === "—"}
              hasArrow
              placement="top"
            >
              <Text
                fontSize="xs"
                color="gray.600"
                textAlign="center"
                fontFamily="mono"
                cursor={
                  schedulesBuildInfo.commitFull !== "—"
                    ? "help"
                    : "default"
                }
              >
                Commit: {schedulesBuildInfo.commitPreview}
              </Text>
            </Tooltip>
            <Text
              fontSize="xs"
              color="gray.600"
              textAlign="center"
              fontFamily="mono"
            >
              Tag: {schedulesBuildInfo.tag}
            </Text>
            <Text
              fontSize="xs"
              color="gray.600"
              textAlign="center"
              fontFamily="mono"
            >
              Built: {schedulesBuildInfo.built}
            </Text>
          </VStack>
        </Box>
      </div>
    </div>
  );
};

export default Schedules;
